/**
 * Brand Assets Page
 * 
 * All download functions use:
 * - html2canvas: For converting HTML elements to canvas/images (PNG downloads)
 * - jspdf: For generating multi-page PDF documents
 * 
 * Dependencies: html2canvas, jspdf (installed via npm)
 */

import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Lock, Download, Copy, Check, ChevronDown, ChevronRight,
  Image, FileText, Target, Users, Building2, Home, Megaphone,
  Instagram, Facebook, Linkedin, Mail, Search, Video, Palette,
  Briefcase, Calendar, DollarSign, Star, Shield, Clock, ArrowRight,
  AlertTriangle, Thermometer, Sparkles, Wrench, Droplets
} from 'lucide-react'
import { Footer } from '../components/SharedLayout'

const ease = [0.16, 1, 0.3, 1] as const

// Helper to convert image URL to base64 for CORS-safe html2canvas rendering
const imageToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url, { mode: 'cors' })
    const blob = await response.blob()
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    // If CORS fails, return original URL (html2canvas will try its best)
    return url
  }
}

// Preload all images in a container as base64 for reliable downloads
const preloadImagesForDownload = async (container: HTMLElement): Promise<void> => {
  const images = container.querySelectorAll('img')
  const bgElements = container.querySelectorAll('[style*="background-image"]')
  
  // Convert <img> elements
  const imgPromises = Array.from(images).map(async (img) => {
    if (img.src && !img.src.startsWith('data:')) {
      const base64 = await imageToBase64(img.src)
      img.src = base64
    }
  })
  
  // Convert background-image styles
  const bgPromises = Array.from(bgElements).map(async (el) => {
    const htmlEl = el as HTMLElement
    const style = htmlEl.style.backgroundImage
    const match = style.match(/url\(["']?([^"')]+)["']?\)/)
    if (match && match[1] && !match[1].startsWith('data:')) {
      const base64 = await imageToBase64(match[1])
      htmlEl.style.backgroundImage = `url(${base64})`
    }
  })
  
  await Promise.all([...imgPromises, ...bgPromises])
}

const colors = {
  teal: '#145F6B',
  tealDark: '#083B3A',
  copper: '#9B775D',
  cream: '#F6F6F1',
  nearBlack: '#0F0F0F',
}

// Brand Assets - Logos & Images
const brandAssets = {
  logos: {
    primary: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson-stacked-dark.png',
    primaryWhite: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Primary_Logo_White.png',
    secondary: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_Tape%20Turquoise.png',
    secondaryWhite: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Secondary_Logo_White.png',
  },
  pattern: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/huson-pattern-landscape.png',
  membershipCard: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/membershipcard.png',
  photos: {
    stock1: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock1.png',
    stock2: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock2.png',
    stock3: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock3.png',
    stock4: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock4.png',
    stock5: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock5.png',
    stock6: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock6.png',
    stock8: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock8.png',
    stock10: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock10.png',
    stock12: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock12.png',
    stock14: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock14.png',
    stock16: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock16.png',
    stock18: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock18.png',
    stock20: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock20.png',
    stock22: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock22.png',
    stock24: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock24.png',
    stock25: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/stock25.png',
    familyHugging: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/familyhugging.png',
    curtainCleaning: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/curtaincleaning.png',
    blueprint: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/blueprintmockup.jpg',
    hudson1: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson1.jpg',
    hudson2: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson2.jpg',
    hudson3: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/hudson3.jpg',
  },
  testimonials: {
    taylor: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/taylor-g-testimonial.png',
    bailey: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/bailey-p-testimonial.png',
    kelsey: 'https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/new-website-images/kelsey-s-testimonial.png',
  },
}

const PASSWORD = 'themaker'

// ============================================
// PASSWORD GATE
// ============================================
const PasswordGate: React.FC<{ onSuccess: () => void }> = ({ onSuccess }) => {
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [shake, setShake] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password.toLowerCase() === PASSWORD) {
      onSuccess()
    } else {
      setError(true)
      setShake(true)
      setTimeout(() => setShake(false), 500)
    }
  }

  return (
    <div className="min-h-screen bg-[#0b1a1a] flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, x: shake ? [0, -10, 10, -10, 10, 0] : 0 }}
        transition={{ duration: 0.6, ease }}
        className="max-w-md w-full"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-white/5 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white/40" />
          </div>
          <h1 className="text-3xl font-serif text-white mb-2">Brand Assets</h1>
          <p className="text-white/50">Internal use only. Enter password to continue.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setError(false) }}
            placeholder="Enter password"
            className={`w-full px-6 py-4 bg-white/5 border ${error ? 'border-red-500' : 'border-white/10'} text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors`}
          />
          {error && <p className="text-red-400 text-sm">Incorrect password. Please try again.</p>}
          <button
            type="submit"
            className="w-full py-4 text-white font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: colors.copper }}
          >
            Access Brand Assets
          </button>
        </form>
      </motion.div>
    </div>
  )
}

// ============================================
// COPY BUTTON
// ============================================
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <button
      onClick={handleCopy}
      className="p-2 hover:bg-grey-100 rounded transition-colors"
      title="Copy to clipboard"
    >
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-grey-400" />}
    </button>
  )
}

// ============================================
// DOWNLOAD BUTTON
// ============================================
const DownloadButton: React.FC<{ label: string; onClick?: () => void }> = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2 bg-grey-100 hover:bg-grey-200 text-grey-700 text-sm font-medium rounded transition-colors"
  >
    <Download className="w-4 h-4" />
    {label}
  </button>
)

// ============================================
// EXPANDABLE SECTION
// ============================================
const ExpandableSection: React.FC<{ 
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
}> = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-6 bg-white hover:bg-grey-50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${colors.teal}15` }}>
            {icon}
          </div>
          <span className="text-lg font-medium text-grey-900">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-grey-400" /> : <ChevronRight className="w-5 h-5 text-grey-400" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease }}
            className="overflow-hidden"
          >
            <div className="p-6 pt-0 bg-white">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ============================================
// DESIGNED SOCIAL MEDIA GRAPHIC
// ============================================
const DesignedSocialGraphic: React.FC<{
  design: {
    id: string
    layout: 'split' | 'overlay' | 'minimal' | 'card' | 'testimonial' | 'feature'
    image: string
    logo: string
    headline: string
    subhead?: string
    cta?: string
    bgColor: string
    accentColor: string
    textColor: string
    price?: string
  }
  dimensions: string
}> = ({ design, dimensions }) => {
  const [targetWidth, targetHeight] = dimensions.split('x').map(Number)
  const isSquare = targetWidth === targetHeight

  const [isDownloading, setIsDownloading] = useState(false)

  // Calculate preview scale to fit in container
  const previewScale = Math.min(300 / targetWidth, 300 / targetHeight)

  const downloadGraphic = async () => {
    const container = document.getElementById(`graphic-${design.id}`)
    if (!container) {
      console.error('Graphic container not found')
      return
    }

    setIsDownloading(true)
    try {
      // Clone and render at full size for accurate export
      const clone = container.cloneNode(true) as HTMLElement
      clone.style.width = `${targetWidth}px`
      clone.style.height = `${targetHeight}px`
      clone.style.transform = 'none'
      clone.style.position = 'absolute'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      document.body.appendChild(clone)
      
      await preloadImagesForDownload(clone)
      
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: design.bgColor || null,
        logging: false,
        width: targetWidth,
        height: targetHeight,
      })
      
      document.body.removeChild(clone)
      
      const link = document.createElement('a')
      link.download = `hudson-${design.id}-${dimensions}.png`
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('Error downloading graphic:', error)
      alert('Error generating image. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  // Render the graphic content with fixed pixel dimensions
  const GraphicContent = () => (
    <div
      style={{
        width: `${targetWidth}px`,
        height: `${targetHeight}px`,
        backgroundColor: design.bgColor,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'Instrument Serif, serif',
      }}
    >
      {/* Layout: Split */}
      {design.layout === 'split' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
          <div style={{ width: '50%', backgroundImage: `url(${design.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          <div style={{ width: '50%', display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '48px' }}>
            <img src={design.logo} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain', marginBottom: '24px' }} />
            <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '36px', color: design.textColor, lineHeight: 1.2, margin: 0 }}>{design.headline}</p>
            {design.subhead && <p style={{ fontSize: '18px', color: design.textColor, opacity: 0.8, marginTop: '16px' }}>{design.subhead}</p>}
            {design.price && <p style={{ fontSize: '32px', color: design.accentColor, marginTop: '16px', fontWeight: 300 }}>{design.price}</p>}
            {design.cta && <span style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: design.accentColor, color: 'white', fontSize: '16px', fontWeight: 500, borderRadius: '4px', marginTop: '24px', width: 'fit-content' }}>{design.cta}</span>}
          </div>
        </div>
      )}

      {/* Layout: Overlay */}
      {design.layout === 'overlay' && (
        <div style={{ position: 'absolute', inset: 0 }}>
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `url(${design.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
          <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${design.bgColor}ee 30%, ${design.bgColor}40 100%)` }} />
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '48px' }}>
            <img src={design.logo} alt="Hudson" style={{ height: '40px', width: 'auto', objectFit: 'contain', marginBottom: '20px' }} />
            <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '42px', color: design.textColor, lineHeight: 1.2, margin: 0 }}>{design.headline}</p>
            {design.subhead && <p style={{ fontSize: '18px', color: design.textColor, opacity: 0.9, marginTop: '12px' }}>{design.subhead}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginTop: '24px' }}>
              {design.cta && <span style={{ padding: '12px 24px', backgroundColor: design.accentColor, color: 'white', fontSize: '16px', fontWeight: 500, borderRadius: '4px' }}>{design.cta}</span>}
              {design.price && <span style={{ fontSize: '24px', color: design.accentColor, fontWeight: 300 }}>{design.price}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Layout: Minimal */}
      {design.layout === 'minimal' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: '56px' }}>
          <img src={design.logo} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
          <div>
            <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '48px', color: design.textColor, lineHeight: 1.2, margin: 0 }}>{design.headline}</p>
            {design.subhead && <p style={{ fontSize: '20px', color: design.textColor, opacity: 0.8, marginTop: '16px' }}>{design.subhead}</p>}
            {design.price && <p style={{ fontSize: '32px', color: design.accentColor, marginTop: '16px', fontWeight: 300 }}>{design.price}</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            {design.cta && <span style={{ padding: '14px 28px', backgroundColor: design.accentColor, color: 'white', fontSize: '16px', fontWeight: 500, borderRadius: '4px' }}>{design.cta}</span>}
            <span style={{ fontSize: '14px', color: design.textColor, opacity: 0.6 }}>hudsonmyhome.app</span>
          </div>
        </div>
      )}

      {/* Layout: Card */}
      {design.layout === 'card' && (
        <div style={{ position: 'absolute', inset: 0, padding: '32px' }}>
          <div style={{ height: '100%', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'rgba(255,255,255,0.1)' }}>
            <div style={{ flex: 1, backgroundImage: `url(${design.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
            <div style={{ padding: '32px', backgroundColor: design.bgColor }}>
              <img src={design.logo} alt="Hudson" style={{ height: '32px', width: 'auto', objectFit: 'contain', marginBottom: '16px' }} />
              <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '28px', color: design.textColor, lineHeight: 1.2, margin: 0 }}>{design.headline}</p>
              {design.subhead && <p style={{ fontSize: '16px', color: design.textColor, opacity: 0.8, marginTop: '8px' }}>{design.subhead}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Layout: Feature */}
      {design.layout === 'feature' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '56px' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: `${design.accentColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px' }}>
              <span style={{ color: design.accentColor, fontSize: '28px' }}>✓</span>
            </div>
            <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '42px', color: design.textColor, lineHeight: 1.2, margin: 0 }}>{design.headline}</p>
            {design.subhead && <p style={{ fontSize: '18px', color: design.textColor, opacity: 0.8, marginTop: '16px' }}>{design.subhead}</p>}
            <img src={design.logo} alt="Hudson" style={{ height: '32px', width: 'auto', objectFit: 'contain', marginTop: 'auto', opacity: 0.6 }} />
          </div>
          {isSquare && <div style={{ width: '40%', backgroundImage: `url(${design.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
        </div>
      )}

      {/* Layout: Testimonial */}
      {design.layout === 'testimonial' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', padding: '48px' }}>
          <img src={design.logo} alt="Hudson" style={{ height: '32px', width: 'auto', objectFit: 'contain', marginBottom: '32px' }} />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
                {[...Array(5)].map((_, i) => <span key={i} style={{ color: design.accentColor, fontSize: '28px' }}>★</span>)}
              </div>
              <p style={{ fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', fontSize: '32px', color: design.textColor, lineHeight: 1.4, margin: 0 }}>"{design.headline}"</p>
              {design.subhead && <p style={{ fontSize: '18px', fontWeight: 500, color: design.accentColor, marginTop: '24px' }}>— {design.subhead}</p>}
            </div>
          </div>
          <span style={{ fontSize: '14px', color: design.textColor, opacity: 0.6 }}>hudsonmyhome.app</span>
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-3">
      {/* Preview container with scaling */}
      <div className="overflow-hidden rounded-lg shadow-lg" style={{ width: targetWidth * previewScale, height: targetHeight * previewScale }}>
        <div id={`graphic-${design.id}`} style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left' }}>
          <GraphicContent />
        </div>
      </div>

      {/* Download button */}
      <button
        onClick={downloadGraphic}
        disabled={isDownloading}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-grey-900 hover:bg-grey-800 disabled:bg-grey-600 text-white text-sm font-medium rounded transition-colors"
      >
        {isDownloading ? (
          <>
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Download {dimensions}
          </>
        )}
      </button>
    </div>
  )
}

// ============================================
// DESIGNED DISPLAY AD
// ============================================
const DesignedDisplayAd: React.FC<{
  size: string
  name: string
  design: {
    headline: string
    subhead: string
    cta: string
    bgColor: string
    accentColor: string
    image?: string
  }
}> = ({ size, name, design }) => {
  const [targetWidth, targetHeight] = size.split('x').map(Number)
  const isVertical = targetHeight > targetWidth
  const isWide = targetWidth > targetHeight * 2
  const isMobileBanner = targetHeight < 100
  const [isDownloading, setIsDownloading] = useState(false)

  // Calculate preview scale - max 200px for any dimension
  const previewScale = Math.min(200 / targetWidth, 200 / targetHeight, 1)

  const downloadAd = async () => {
    const container = document.getElementById(`ad-${size}-${name.replace(/\s/g, '')}`)
    if (!container) {
      console.error('Ad container not found')
      return
    }

    setIsDownloading(true)
    try {
      // Clone and render at full size
      const clone = container.cloneNode(true) as HTMLElement
      clone.style.width = `${targetWidth}px`
      clone.style.height = `${targetHeight}px`
      clone.style.transform = 'none'
      clone.style.position = 'absolute'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      document.body.appendChild(clone)
      
      await preloadImagesForDownload(clone)
      
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: design.bgColor || null,
        logging: false,
        width: targetWidth,
        height: targetHeight,
      })
      
      document.body.removeChild(clone)
      
      const link = document.createElement('a')
      link.download = `hudson-ad-${size}.png`
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('Error downloading ad:', error)
      alert('Error generating image. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  // Font sizes scaled for the actual pixel dimensions
  const logoHeight = isMobileBanner ? 16 : isVertical ? 32 : 28
  const headlineFontSize = isMobileBanner ? 12 : isVertical ? 20 : 16
  const subheadFontSize = isMobileBanner ? 10 : isVertical ? 14 : 12
  const ctaPadding = isMobileBanner ? '4px 8px' : '8px 16px'
  const ctaFontSize = isMobileBanner ? 10 : 12
  const contentPadding = isMobileBanner ? 12 : 24

  const AdContent = () => (
    <div
      style={{
        width: `${targetWidth}px`,
        height: `${targetHeight}px`,
        backgroundColor: design.bgColor,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background image if provided */}
      {design.image && !isMobileBanner && (
        <div style={{ position: 'absolute', inset: 0, opacity: 0.2, backgroundImage: `url(${design.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      )}

      {/* Content */}
      <div style={{ 
        position: 'absolute', 
        inset: 0, 
        display: 'flex', 
        flexDirection: isVertical ? 'column' : 'row',
        alignItems: isWide ? 'center' : isVertical ? 'stretch' : 'center',
        justifyContent: isVertical ? 'space-between' : isWide ? 'space-between' : 'center',
        padding: `${contentPadding}px`,
        gap: isWide ? '0' : '8px',
      }}>
        {/* Logo */}
        <img 
          src={brandAssets.logos.secondaryWhite} 
          alt="Hudson" 
          style={{ height: `${logoHeight}px`, width: 'auto', objectFit: 'contain' }}
        />

        {/* Text */}
        <div style={{ flex: isWide ? 1 : 'none', paddingLeft: isWide ? '16px' : 0, paddingRight: isWide ? '16px' : 0 }}>
          <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: `${headlineFontSize}px`, color: 'white', lineHeight: 1.2, margin: 0 }}>
            {design.headline}
          </p>
          {!isMobileBanner && (
            <p style={{ fontSize: `${subheadFontSize}px`, color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
              {design.subhead}
            </p>
          )}
        </div>

        {/* CTA */}
        <span style={{ 
          display: 'inline-block', 
          padding: ctaPadding, 
          backgroundColor: design.accentColor, 
          color: 'white', 
          fontSize: `${ctaFontSize}px`, 
          fontWeight: 500, 
          borderRadius: '4px',
          textAlign: 'center',
        }}>
          {design.cta}
        </span>
      </div>
    </div>
  )

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      {/* Preview with scaling */}
      <div style={{ 
        width: targetWidth * previewScale, 
        height: targetHeight * previewScale, 
        overflow: 'hidden',
        margin: previewScale < 1 ? '12px auto' : '0 auto',
      }}>
        <div 
          id={`ad-${size}-${name.replace(/\s/g, '')}`}
          style={{ 
            transform: `scale(${previewScale})`, 
            transformOrigin: 'top left',
          }}
        >
          <AdContent />
        </div>
      </div>
      
      <div className="p-3 bg-grey-50">
        <p className="font-medium text-grey-900 text-sm">{name}</p>
        <p className="text-xs text-grey-500 mb-2">{size}</p>
        <button
          onClick={downloadAd}
          disabled={isDownloading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-grey-900 hover:bg-grey-800 disabled:bg-grey-600 text-white text-xs font-medium rounded transition-colors"
        >
          {isDownloading ? (
            <>
              <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="w-3 h-3" />
              Download
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================
// SOCIAL MEDIA POST CARD WITH DESIGNED GRAPHIC
// ============================================
const SocialPostCard: React.FC<{
  platform: string
  caption: string
  hashtags: string[]
  design: {
    id: string
    layout: 'split' | 'overlay' | 'minimal' | 'card' | 'testimonial' | 'feature'
    image: string
    logo: string
    headline: string
    subhead?: string
    cta?: string
    bgColor: string
    accentColor: string
    textColor: string
    price?: string
  }
  dimensions: string
}> = ({ platform, caption, hashtags, design, dimensions }) => {
  const platformIcons: Record<string, React.ReactNode> = {
    Instagram: <Instagram className="w-5 h-5" />,
    Facebook: <Facebook className="w-5 h-5" />,
    LinkedIn: <Linkedin className="w-5 h-5" />,
  }

  const fullCaption = `${caption}\n\n${hashtags.join(' ')}`

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden bg-white">
      <div className="p-4 bg-grey-50 border-b border-grey-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center" style={{ color: colors.teal }}>
            {platformIcons[platform]}
          </div>
          <span className="font-medium text-grey-900">{platform}</span>
        </div>
        <span className="text-xs text-grey-500">{dimensions}</span>
      </div>
      <div className="p-4">
        <DesignedSocialGraphic design={design} dimensions={dimensions} />
        <div className="space-y-3 mt-4">
          <p className="text-sm text-grey-700 whitespace-pre-wrap">{caption}</p>
          <p className="text-sm text-blue-600">{hashtags.join(' ')}</p>
        </div>
      </div>
      <div className="p-4 pt-0 flex items-center gap-2">
        <CopyButton text={fullCaption} />
        <span className="text-xs text-grey-400">Copy caption</span>
      </div>
    </div>
  )
}

// ============================================
// AD CARD
// ============================================
const AdCard: React.FC<{
  type: string
  headline: string
  description: string
  cta: string
  dimensions?: string
}> = ({ type, headline, description, cta, dimensions }) => (
  <div className="border border-grey-200 rounded-lg overflow-hidden">
    <div className="p-4 bg-grey-50 border-b border-grey-200 flex items-center justify-between">
      <span className="font-medium text-grey-900">{type}</span>
      {dimensions && <span className="text-xs text-grey-500">{dimensions}</span>}
    </div>
    <div className="p-4 space-y-3">
      <div>
        <span className="text-xs text-grey-500 uppercase tracking-wider">Headline</span>
        <p className="font-medium text-grey-900">{headline}</p>
      </div>
      <div>
        <span className="text-xs text-grey-500 uppercase tracking-wider">Description</span>
        <p className="text-sm text-grey-700">{description}</p>
      </div>
      <div>
        <span className="text-xs text-grey-500 uppercase tracking-wider">CTA</span>
        <p className="text-sm font-medium" style={{ color: colors.copper }}>{cta}</p>
      </div>
    </div>
    <div className="p-4 pt-0 flex gap-2">
      <CopyButton text={`${headline}\n${description}\n${cta}`} />
    </div>
  </div>
)

// ============================================
// COMPREHENSIVE BROCHURE COMPONENT
// ============================================
const DesignedBrochure: React.FC<{
  type: 'membership' | 'refresh' | 'agents' | 'builders'
  title: string
  audience: string
}> = ({ type, title, audience }) => {
  const [isGenerating, setIsGenerating] = useState(false)
  const [showPreview, setShowPreview] = useState(true) // Show by default

  // Comprehensive brochure content for each type
  const brochureData = {
    membership: {
      colors: { bg: colors.teal, accent: 'white', light: colors.cream },
      cover: {
        tagline: 'WHITE-GLOVE HOME MANAGEMENT',
        headline: 'We maintain your home so you don\'t have to.',
        subhead: 'Your dedicated Hudson professional manages everything so you can focus on what matters most.',
        image: brandAssets.photos.stock1,
        stats: [
          { value: '10+', label: 'Hours saved monthly' },
          { value: '85%', label: 'Member retention' },
          { value: '24/7', label: 'Priority support' },
        ],
      },
      about: {
        headline: 'About Hudson My Home',
        intro: 'Subscription-based preventative home maintenance. Proactive care that reduces emergency repairs and extends system life.',
        mission: 'Give busy homeowners their time back while ensuring their home is always in peak condition.',
        values: [
          { title: 'Proactive, Not Reactive', desc: 'Prevent problems before they become expensive.' },
          { title: 'Personal Relationship', desc: 'Your Hudson knows your home intimately.' },
          { title: 'Complete Transparency', desc: 'Every visit documented. No surprises.' },
          { title: 'Fair Pricing', desc: 'Volume relationships mean wholesale pricing.' },
        ],
      },
      problem: {
        headline: 'The Hidden Cost of Homeownership',
        intro: 'Most homeowners don\'t realize how much time, money, and mental energy goes into home maintenance.',
        stats: [
          { value: '10+', label: 'Hours per month' },
          { value: '3-5x', label: 'Higher reactive costs' },
          { value: '$15K', label: 'Preventable failures' },
        ],
        painPoints: [
          { title: 'Never-Ending To-Do List', desc: 'Filters, gutters, HVAC—the list never ends. Something always falls through.' },
          { title: 'Vendor Nightmare', desc: 'Finding reliable contractors. Getting fair quotes. Coordinating schedules.' },
          { title: 'The 2am Emergency', desc: 'When something breaks, who do you call? Are you being overcharged?' },
          { title: 'Mental Load', desc: 'Even when not doing maintenance, you\'re thinking about what needs to be done.' },
        ],
      },
      solution: {
        headline: 'Your Dedicated Hudson Professional',
        intro: 'One person who knows your home better than you do—and handles everything proactively.',
        features: [
          { 
            title: 'Monthly In-Home Visits',
            desc: 'Thorough walkthrough every month. Inspect systems, identify issues, complete routine maintenance.',
            details: ['HVAC filters', 'Plumbing checks', 'Electrical safety', 'Exterior inspection'],
          },
          { 
            title: '24/7 Priority Support',
            desc: 'Emergency at 2am? Your Hudson is a call away. Priority response, always.',
            details: ['Direct line', 'Emergency coordination', 'After-hours support'],
          },
          { 
            title: 'Complete Vendor Management',
            desc: 'We vet contractors, review quotes, oversee work. Quality at fair prices. Never overcharged.',
            details: ['Pre-vetted network', 'Quote negotiation', 'Quality oversight'],
          },
          { 
            title: '5-Year Maintenance Blueprint',
            desc: 'Personalized plan executed flawlessly. From routine care to major renovations.',
            details: ['Custom schedule', 'System tracking', 'Budget forecasting'],
          },
        ],
      },
      experience: {
        headline: 'The Member Experience',
        journey: [
          { step: '01', title: 'Apply', desc: 'Fill out a brief application. We review to ensure we\'re a mutual fit for your home and expectations.' },
          { step: '02', title: 'Match', desc: 'We assign your dedicated Hudson based on location, home type, and expertise. This is your person.' },
          { step: '03', title: 'Assess', desc: 'Your Hudson conducts a comprehensive home assessment, documenting every system and creating your baseline.' },
          { step: '04', title: 'Plan', desc: 'We create your personalized 5-year maintenance blueprint with clear priorities and timelines.' },
          { step: '05', title: 'Execute', desc: 'Monthly visits begin. We handle everything. You relax and enjoy your home.' },
        ],
        testimonials: [
          { quote: 'I manage three properties and two companies. My Hudson handles all the home stuff so I can focus on what matters.', name: 'Taylor G.', title: 'Business Owner' },
          { quote: 'She caught a drainage issue that would have cost $15K. The membership paid for itself in one visit.', name: 'Bailey P.', title: 'Homeowner' },
          { quote: 'My Saturdays are mine again. That\'s worth far more than $399 a month.', name: 'Kelsey S.', title: 'Homeowner' },
        ],
      },
      whatWeHandle: {
        headline: 'From Furnace Filters to Full Remodels',
        categories: [
          { 
            title: 'Routine Maintenance',
            items: ['HVAC filter changes', 'Water heater flushes', 'Gutter cleaning', 'Dryer vent cleaning', 'Smoke detector batteries', 'Seasonal HVAC tune-ups'],
          },
          { 
            title: 'Seasonal Care',
            items: ['Winterization', 'Spring HVAC prep', 'Fall furnace inspection', 'Irrigation blow-outs', 'Exterior inspections', 'Storm preparations'],
          },
          { 
            title: 'Major Systems',
            items: ['Roof inspections', 'Foundation checks', 'Electrical panel reviews', 'Plumbing assessments', 'Insulation evaluations', 'HVAC replacements'],
          },
          { 
            title: 'Big Projects',
            items: ['Kitchen remodels', 'Bathroom renovations', 'Basement finishes', 'Additions', 'Full home updates', 'Landscaping overhauls'],
          },
        ],
      },
      pricing: {
        headline: 'Membership Investment',
        price: '$399',
        period: '/month',
        description: 'One flat fee. Everything included. No surprises.',
        includes: [
          'Dedicated Hudson professional assigned to your home',
          'Monthly in-home inspections and maintenance',
          'Complete 5-year maintenance plan management',
          'All vendor vetting, coordination, and oversight',
          '24/7 priority support and emergency response',
          'Complete documentation and home history',
          'Full access to the Hudson My Home app',
          'Price protection on all vendor services',
        ],
        guarantee: {
          title: '30-Day Money-Back Guarantee',
          desc: 'Try membership for 30 days. If we haven\'t saved you time, stress, and money, we\'ll refund every penny. No questions asked.',
        },
      },
      cta: {
        headline: 'Your Home Should Feel Like a Five-Star Hotel',
        subhead: 'Apply for membership today and experience what it means to truly live in your home.',
        url: 'hudsonmyhome.app/apply',
        phone: '(515) 216-4433',
        email: 'info@hudsonmyhome.com',
      },
    },
    refresh: {
      colors: { bg: colors.tealDark, accent: 'white', light: colors.cream },
      cover: {
        tagline: 'ONE-TIME PREVENTIVE HOME CARE',
        headline: 'The MyHome Refresh™',
        subhead: 'A thoughtful reset for homeowners who want clarity, confidence, and proactive care.',
        image: brandAssets.photos.stock2,
        stats: [
          { value: '$299', label: 'Starting price' },
          { value: '2-3.5hrs', label: 'Visit duration' },
          { value: '15+', label: 'Systems checked' },
        ],
      },
      about: {
        headline: 'What Is the MyHome Refresh?',
        intro: 'One-time preventive home care. Clear understanding of your home\'s condition and essentials handled in a single visit.',
        mission: 'Professional home health review plus hands-on maintenance. Stay ahead of issues that are easy to miss.',
        values: [
          { title: 'No Membership', desc: 'Standalone service. No commitment.' },
          { title: 'Immediate Value', desc: 'Completed maintenance and clear priorities the same day.' },
          { title: 'Professional Insight', desc: 'Expert review of major systems with honest recommendations.' },
          { title: 'Foundation', desc: 'Strong baseline if you choose ongoing care.' },
        ],
      },
      problem: {
        headline: 'Why Homes Need a Reset',
        intro: 'Small issues build quietly over time until they become expensive disruptions.',
        stats: [
          { value: '67%', label: 'Never had a home check' },
          { value: '80%', label: 'Preventable HVAC failures' },
          { value: '$4K', label: 'Annual deferred costs' },
        ],
        painPoints: [
          { title: 'Filters Go Unchanged', desc: 'Dirty filters reduce efficiency 15% and strain systems.' },
          { title: 'Safety Devices Untested', desc: 'When did you last test smoke detectors or GFCI outlets?' },
          { title: 'Minor Issues Unnoticed', desc: 'Slow drains, small leaks, inefficient dryers build up quietly.' },
          { title: 'No Clear Priorities', desc: 'You know things need attention, but which first? Overwhelm leads to inaction.' },
        ],
      },
      solution: {
        headline: 'One Visit. Complete Clarity.',
        intro: 'Immediate value and long-term clarity in a single, focused visit.',
        features: [
          { 
            title: 'Professional Home Health Check',
            desc: 'Guided review of primary systems. Early indicators of wear, inefficiency, or risk.',
            details: ['HVAC assessment', 'Plumbing inspection', 'Electrical review', 'Safety testing'],
          },
          { 
            title: 'Hands-On Maintenance',
            desc: 'We don\'t just look—we do. Essential tasks completed during your visit.',
            details: ['Filter replacement', 'Detector testing', 'GFCI testing', 'Vent inspection'],
          },
          { 
            title: 'Clear Prioritized Report',
            desc: 'Complete picture of your home\'s condition and what to address first.',
            details: ['MyHome Score™', 'Priority recommendations', 'Cost estimates'],
          },
          { 
            title: 'Future Service Credit',
            desc: 'Credit toward future Hudson services. No expiration.',
            details: ['Essential: $50', 'Deluxe: $100'],
          },
        ],
      },
      whatWeCheck: {
        headline: 'What We Inspect & Maintain',
        categories: [
          { 
            title: 'HVAC Systems',
            items: ['Filter condition', 'Thermostat function', 'Airflow assessment', 'Ductwork inspection', 'Outdoor unit condition'],
          },
          { 
            title: 'Plumbing',
            items: ['Under-sink inspection', 'Water heater check', 'Toilet function', 'Drain flow', 'Supply line condition'],
          },
          { 
            title: 'Electrical',
            items: ['GFCI outlet testing', 'Panel inspection', 'Outlet function', 'Light fixtures', 'Smoke/CO detectors'],
          },
          { 
            title: 'Exterior & Safety',
            items: ['Roof visual check', 'Gutter condition', 'Foundation inspection', 'Window/door seals', 'Garage door safety'],
          },
        ],
      },
      pricing: {
        headline: 'Choose Your Refresh',
        tiers: [
          {
            name: 'MyHome Refresh',
            price: '$299',
            description: 'Peace of mind through a one-time proactive tune-up. Choose 2 key systems for inspection.',
            includes: [
              'Home Health Check (all major systems)',
              'Choose 3 seasonal maintenance services',
              'Mini MyHome Score™ summary',
              'Prioritized recommendations',
              '$50 credit toward future services',
            ],
          },
          {
            name: 'MyHome Refresh Deluxe',
            price: '$399',
            description: 'In-depth assessment, full preventive tune-up, and detailed recommendations for all major systems.',
            includes: [
              'Everything in Essential, plus:',
              'Expanded review (appliances, roofing, exterior)',
              'ALL seasonal maintenance completed',
              'Full MyHome Snapshot™ report',
              'Complete Home Health Score',
              '$100 credit toward future services',
            ],
            popular: true,
          },
        ],
        guarantee: {
          title: 'Satisfaction Guaranteed',
          desc: 'If you\'re not completely satisfied with your MyHome Refresh experience, we\'ll make it right.',
        },
      },
      cta: {
        headline: 'A Thoughtful First Step',
        subhead: 'Ensure your home is cared for today and create a confident foundation for whatever comes next.',
        url: 'hudsonmyhome.app/refresh',
        phone: '(515) 216-4433',
        email: 'info@hudsonmyhome.com',
      },
    },
    agents: {
      colors: { bg: colors.cream, accent: colors.teal, light: 'white' },
      cover: {
        tagline: 'REAL ESTATE PARTNER PROGRAM',
        headline: 'The Closing Gift They Actually Use',
        subhead: 'Partner with Hudson My Home™ and stand out from every other agent in your market.',
        image: brandAssets.photos.stock5,
        stats: [
          { value: '$599', label: 'Annual partnership' },
          { value: '12', label: 'Monthly touchpoints' },
          { value: '3x', label: 'Referral increase' },
        ],
      },
      about: {
        headline: 'About the Partner Program',
        intro: 'Exclusively for top-producing agents. Differentiate yourself, generate referrals, provide exceptional value.',
        mission: 'Become unforgettable to clients with a gift that keeps giving—and keeps you top of mind monthly.',
        values: [
          { title: 'Stand Out', desc: 'While others give wine, you give ongoing home care.' },
          { title: 'Stay Top of Mind', desc: 'Every Hudson visit reminds clients who took care of them.' },
          { title: 'Generate Referrals', desc: 'Happy clients with well-maintained homes refer friends.' },
          { title: 'Eliminate Calls', desc: 'We become their go-to for home questions, not you.' },
        ],
      },
      benefits: {
        headline: 'Partner Benefits',
        items: [
          { 
            title: 'Pre-Listing Inspections',
            desc: 'Discounted professional inspections for your listings. Help sellers identify and address issues before buyers find them.',
            discount: '10% off standard rates',
          },
          { 
            title: 'Closing Gift Memberships',
            desc: 'Gift your buyers a Hudson membership. Every monthly visit keeps you in their minds and generates referrals.',
            discount: '5-15% off based on volume',
          },
          { 
            title: 'In-App Marketing',
            desc: 'Your branding appears in the Hudson app for all clients you\'ve gifted memberships to. Ongoing visibility.',
            discount: 'Included with partnership',
          },
          { 
            title: 'Priority Scheduling',
            desc: 'Your clients get priority scheduling for all Hudson services. Fast turnaround when it matters.',
            discount: 'Included with partnership',
          },
        ],
      },
      howItWorks: {
        headline: 'How the Program Works',
        steps: [
          { step: '01', title: 'Join', desc: 'Apply for partnership and pay your annual membership fee. We\'ll set up your partner account.' },
          { step: '02', title: 'Gift', desc: 'Purchase closing gift memberships at your discounted rate. We handle all the coordination.' },
          { step: '03', title: 'Impress', desc: 'Your clients receive white-glove home care. You receive their gratitude and referrals.' },
          { step: '04', title: 'Grow', desc: 'As your clients love their Hudson experience, they tell their friends. Your business grows.' },
        ],
      },
      pricing: {
        headline: 'Partnership Investment',
        price: '$599',
        period: '/year',
        description: 'Annual partnership with exclusive benefits and discounted pricing.',
        includes: [
          '10% off all pre-listing inspections',
          '5% off closing gift memberships (1-9 gifts)',
          '10% off closing gift memberships (10-24 gifts)',
          '15% off closing gift memberships (25+ gifts)',
          'In-app branding for all your clients',
          'Priority scheduling for your clients',
          'Partner badge for marketing materials',
          'Quarterly market reports',
        ],
        closingGiftPricing: {
          headline: 'Closing Gift Options',
          options: [
            { duration: '3-Month Membership', price: '$1,197', savings: 'Perfect for first-time buyers' },
            { duration: '6-Month Membership', price: '$2,394', savings: 'Great for move-up buyers' },
            { duration: 'Annual Membership', price: '$4,788', savings: 'Best for luxury clients' },
          ],
        },
        guarantee: {
          title: 'Cancel Anytime',
          desc: 'If the program isn\'t working for you, cancel your partnership at any time. No long-term commitment required.',
        },
      },
      cta: {
        headline: 'Become the Agent They Never Forget',
        subhead: 'Join our partner program and give your clients something truly valuable.',
        url: 'hudsonmyhome.app/partners',
        phone: '(515) 216-4433',
        email: 'mitch@hudsonmyhome.com',
      },
    },
    builders: {
      colors: { bg: colors.nearBlack, accent: 'white', light: colors.cream },
      cover: {
        tagline: 'BUILDER PARTNERSHIP PROGRAM',
        headline: 'Build Exceptional Homes. We Handle Everything After.',
        subhead: 'First-year warranty coordination and buyer experience management that protects your reputation.',
        image: brandAssets.photos.stock4,
        stats: [
          { value: '0', label: 'Post-closing calls' },
          { value: '100%', label: 'Warranty coordination' },
          { value: '4.9★', label: 'Buyer satisfaction' },
        ],
      },
      about: {
        headline: 'Why Partner With Hudson?',
        intro: 'You build exceptional homes. Your relationship with buyers doesn\'t end at closing. Hudson ensures the same care continues.',
        mission: 'We become the homeowner\'s single point of contact after closing. Warranty coordination, monthly visits, all communication.',
        values: [
          { title: 'Protect Reputation', desc: 'Happy homeowners become your best marketing.' },
          { title: 'Eliminate Callbacks', desc: 'We handle post-move-in issues. You focus on building.' },
          { title: 'Differentiate', desc: 'Offer ongoing home care competitors don\'t.' },
          { title: 'Generate Referrals', desc: 'Buyers who love their experience refer friends.' },
        ],
      },
      problem: {
        headline: 'The Post-Closing Problem',
        intro: 'No matter how exceptional your homes are, the first call after move-in almost always goes to the builder.',
        painPoints: [
          { title: 'Blue Tape Nightmares', desc: 'Endless punch list items that drag on for months, consuming your team\'s time and testing buyer patience.' },
          { title: 'The 6-Month Call', desc: 'Something goes wrong. The buyer doesn\'t know who to call. They call you. Even when it\'s not your issue.' },
          { title: 'Warranty Coordination', desc: 'Managing subcontractor callbacks, scheduling, and follow-ups falls on your already-busy team.' },
          { title: 'Reputation Risk', desc: 'One bad post-move-in experience can undo all the goodwill you built during the construction process.' },
        ],
      },
      solution: {
        headline: 'The Hudson Solution',
        intro: 'We step in after closing to provide proactive, concierge-level home care and full warranty coordination.',
        features: [
          { 
            title: 'Full Warranty Coordination',
            desc: 'We manage all warranty-related requests during the covered year, from homeowner intake to resolution.',
            details: ['Homeowner request intake', 'Issue assessment', 'Subcontractor scheduling', 'Follow-up and resolution', 'Complete documentation'],
          },
          { 
            title: 'Monthly Homeowner Visits',
            desc: 'Hudson visits the home monthly, proactively addressing maintenance and educating homeowners on their new home.',
            details: ['Proactive maintenance', 'System education', 'Seasonal preparations', 'Issue identification', 'Maintenance reminders'],
          },
          { 
            title: 'Single Point of Contact',
            desc: 'From day one, buyers know Hudson is their contact for home care, maintenance, and warranty needs.',
            details: ['24/7 availability', 'Immediate response', 'Professional handling', 'Consistent experience'],
          },
          { 
            title: 'Complete Documentation',
            desc: 'Every visit, every issue, every resolution documented. Full transparency for you and the homeowner.',
            details: ['Visit reports', 'Issue tracking', 'Resolution records', 'Homeowner access', 'Builder dashboard'],
          },
        ],
      },
      howItWorks: {
        headline: 'How Builder Partnerships Work',
        steps: [
          { step: '01', title: 'Integrate', desc: 'Include one full year of Hudson My Home™ as part of your new construction or remodel package.' },
          { step: '02', title: 'Introduce', desc: 'At closing, we introduce ourselves to buyers as their point of contact for home care and warranty needs.' },
          { step: '03', title: 'Execute', desc: 'Hudson visits monthly, handles all warranty coordination, and keeps homeowners happy.' },
          { step: '04', title: 'Report', desc: 'You receive regular reports on homeowner satisfaction and any builder-relevant issues.' },
        ],
      },
      pricing: {
        headline: 'Partnership Options',
        description: 'Flexible partnership structures to fit your business model.',
        options: [
          {
            name: 'Per-Home Partnership',
            description: 'Include Hudson as part of your buyer package on a per-home basis.',
            includes: ['Full first-year coverage', 'Monthly homeowner visits', 'Complete warranty coordination', 'Builder reporting dashboard'],
          },
          {
            name: 'Annual Partnership',
            description: 'Volume pricing for builders with consistent production.',
            includes: ['Discounted per-home rates', 'Priority onboarding', 'Dedicated account manager', 'Co-marketing opportunities'],
          },
          {
            name: 'Pilot Program',
            description: 'Try the program with 3-5 homes before committing fully.',
            includes: ['No upfront commitment', 'Full service experience', 'Results evaluation', 'Easy scaling path'],
          },
        ],
        guarantee: {
          title: 'Risk-Free Pilot Available',
          desc: 'Try the program with a few homes and see the results before committing to a full partnership.',
        },
      },
      cta: {
        headline: 'Elevate Your Buyer Experience',
        subhead: 'Add Hudson My Home™ to your buyer package and protect your reputation.',
        url: 'hudsonmyhome.app/builders',
        phone: '(515) 216-4433',
        email: 'info@hudsonmyhome.com',
      },
    },
  }

  const data = brochureData[type]
  // Simplified colors - less gold/copper, more teal
  const bgColor = type === 'membership' ? colors.teal 
    : type === 'refresh' ? colors.tealDark 
    : type === 'agents' ? colors.cream 
    : colors.nearBlack
  const accentColor = type === 'agents' ? colors.teal : 'white'
  const textOnBg = type === 'agents' ? colors.nearBlack : 'white'
  const textOnLight = colors.nearBlack

  const downloadBrochure = async () => {
    setIsGenerating(true)
    
    // Ensure preview is shown
    if (!showPreview) {
      setShowPreview(true)
      // Wait for preview to render
      await new Promise(resolve => setTimeout(resolve, 1000))
    }

    try {
      const container = document.getElementById(`brochure-full-${type}`)
      if (!container) {
        alert('Error: Could not find brochure content. Please refresh and try again.')
        setIsGenerating(false)
        return
      }

      const html2canvas = (await import('html2canvas')).default
      const jsPDF = (await import('jspdf')).default

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'letter'
      })

      const pages = container.querySelectorAll('.brochure-page')
      
      if (pages.length === 0) {
        alert('No pages found. Please wait for the preview to load.')
        setIsGenerating(false)
        return
      }

      // Letter size in mm
      const pageWidth = 215.9
      const pageHeight = 279.4

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement
        
        // Scroll page into view
        page.scrollIntoView({ behavior: 'instant', block: 'center' })
        
        // Wait for images to load
        await new Promise(resolve => setTimeout(resolve, 800))
        
        // Capture the page
        const canvas = await html2canvas(page, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          logging: false,
          backgroundColor: page.style.backgroundColor || '#ffffff',
          width: page.offsetWidth,
          height: page.offsetHeight,
          windowWidth: page.scrollWidth,
          windowHeight: page.scrollHeight,
        })
        
        // Convert to image
        const imgData = canvas.toDataURL('image/png', 1.0)
        
        // Calculate dimensions to fit letter size
        const imgWidth = pageWidth
        const imgHeight = (canvas.height * pageWidth) / canvas.width
        
        // Add new page if not first
        if (i > 0) {
          pdf.addPage()
        }
        
        // Add image to PDF, scaling if needed
        if (imgHeight > pageHeight) {
          // Scale down to fit
          const scale = pageHeight / imgHeight
          pdf.addImage(imgData, 'PNG', 0, 0, imgWidth * scale, pageHeight, undefined, 'FAST')
        } else {
          // Center vertically if smaller than page
          const yOffset = (pageHeight - imgHeight) / 2
          pdf.addImage(imgData, 'PNG', 0, yOffset, imgWidth, imgHeight, undefined, 'FAST')
        }
      }
      
      // Save the PDF
      pdf.save(`hudson-${type}-brochure.pdf`)
      
    } catch (error) {
      console.error('Error generating PDF:', error)
      alert(`Error generating PDF: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`)
    }
    
    setIsGenerating(false)
  }

  const pageCount = type === 'membership' ? 6 : type === 'refresh' ? 5 : type === 'agents' ? 5 : 5

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      <div className="p-4 bg-grey-50 border-b border-grey-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h4 className="font-medium text-grey-900">{title}</h4>
          <p className="text-sm text-grey-500">Target: {audience} • {pageCount} pages</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 border border-grey-300 text-grey-700 text-sm font-medium rounded hover:bg-grey-50 transition-colors"
          >
            {showPreview ? 'Hide Preview' : 'Preview All Pages'}
          </button>
          <button
            onClick={downloadBrochure}
            disabled={isGenerating}
            className="flex items-center gap-2 px-4 py-2 bg-grey-900 hover:bg-grey-800 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            {isGenerating ? 'Generating PDF...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Full Brochure Preview - Always visible */}
      <div className={showPreview ? 'block' : 'block'}>
        <div id={`brochure-full-${type}`} className="p-4 space-y-4 bg-grey-100 overflow-x-auto">
          
          {/* PAGE 1: Cover */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden" style={{ width: '680px', aspectRatio: '8.5/11', backgroundColor: bgColor }}>
            <div className="h-full p-6 flex flex-col">
              <img src={type === 'agents' ? brandAssets.logos.primary : brandAssets.logos.primaryWhite} alt="Hudson" className="h-10 w-auto mb-6 object-contain max-w-full" style={{ objectFit: 'contain' }} />
              <p className="text-xs font-medium tracking-widest uppercase mb-3" style={{ color: textOnBg, opacity: 0.7 }}>{data.cover.tagline}</p>
              <h1 className="font-serif text-4xl leading-tight mb-4" style={{ color: textOnBg }}>{data.cover.headline}</h1>
              <p className="text-base mb-6" style={{ color: textOnBg, opacity: 0.9 }}>{data.cover.subhead}</p>
              <div className="h-40 rounded-lg bg-cover bg-center mb-6" style={{ backgroundImage: `url(${data.cover.image})` }} />
              <div className="flex justify-between mt-auto mb-4">
                {data.cover.stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <p className="text-2xl font-light" style={{ color: type === 'agents' ? colors.teal : 'white' }}>{stat.value}</p>
                    <p className="text-xs" style={{ color: textOnBg, opacity: 0.7 }}>{stat.label}</p>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t flex justify-between text-xs" style={{ borderColor: `${textOnBg}20`, color: textOnBg, opacity: 0.6 }}>
                <span>hudsonmyhome.app</span>
                <span>(515) 216-4433</span>
              </div>
            </div>
          </div>

          {/* PAGE 2: About */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden bg-white" style={{ width: '680px', aspectRatio: '8.5/11' }}>
            <div className="h-full p-6 flex flex-col">
              <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: colors.teal }}>About</p>
              <h2 className="font-serif text-2xl text-grey-900 mb-4">{data.about.headline}</h2>
              <p className="text-sm text-grey-700 leading-relaxed mb-3">{data.about.intro}</p>
              <div className="flex-1 grid grid-cols-2 gap-4">
                {data.about.values.map((value, i) => (
                  <div key={i} className="p-4 rounded-lg" style={{ backgroundColor: `${colors.teal}08` }}>
                    <h4 className="font-medium text-sm text-grey-900 mb-2">{value.title}</h4>
                    <p className="text-xs text-grey-600">{value.desc}</p>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-4 border-t border-grey-100">
                <img src={brandAssets.logos.primary} alt="Hudson" className="h-6 w-auto object-contain max-w-full" style={{ objectFit: 'contain' }} />
              </div>
            </div>
          </div>

          {/* PAGE 3: Problem */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden bg-white" style={{ width: '680px', aspectRatio: '8.5/11' }}>
            <div className="h-full p-6 flex flex-col">
              <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: colors.teal }}>The Challenge</p>
              <h2 className="font-serif text-2xl text-grey-900 mb-3">{data.problem.headline}</h2>
              <p className="text-sm text-grey-700 mb-4">{data.problem.intro}</p>
              
              {'stats' in data.problem && (
                <div className="flex justify-between mb-5 p-4 rounded-lg" style={{ backgroundColor: `${colors.teal}10` }}>
                  {data.problem.stats.map((stat, i) => (
                    <div key={i} className="text-center">
                      <p className="text-xl font-light" style={{ color: colors.teal }}>{stat.value}</p>
                      <p className="text-xs text-grey-600">{stat.label}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex-1 space-y-4">
                {data.problem.painPoints.map((point, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-medium" style={{ backgroundColor: colors.teal }}>{i + 1}</div>
                    <div>
                      <h4 className="font-medium text-sm text-grey-900 mb-1">{point.title}</h4>
                      <p className="text-xs text-grey-600 leading-relaxed">{point.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-4 border-t border-grey-100">
                <img src={brandAssets.logos.primary} alt="Hudson" className="h-6 w-auto object-contain max-w-full" style={{ objectFit: 'contain' }} />
              </div>
            </div>
          </div>

          {/* PAGE 4: Solution */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden bg-white" style={{ width: '680px', aspectRatio: '8.5/11' }}>
            <div className="h-full p-6 flex flex-col">
              <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: colors.teal }}>The Solution</p>
              <h2 className="font-serif text-2xl text-grey-900 mb-3">{data.solution.headline}</h2>
              <p className="text-sm text-grey-700 mb-4">{data.solution.intro}</p>
              
              <div className="flex-1 space-y-3">
                {data.solution.features.slice(0, 2).map((feature, i) => (
                  <div key={i} className="p-3 rounded-lg border border-grey-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.teal }}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="font-medium text-sm text-grey-900">{feature.title}</h4>
                    </div>
                    <p className="text-xs text-grey-600 mb-3 leading-relaxed">{feature.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {feature.details.slice(0, 3).map((detail, j) => (
                        <span key={j} className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: `${colors.teal}15`, color: colors.teal }}>{detail}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-auto pt-4 border-t border-grey-100">
                <img src={brandAssets.logos.primary} alt="Hudson" className="h-6 w-auto object-contain max-w-full" style={{ objectFit: 'contain' }} />
              </div>
            </div>
          </div>

          {/* PAGE 5: Solution Continued */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden bg-white" style={{ width: '680px', aspectRatio: '8.5/11' }}>
            <div className="h-full p-6 flex flex-col">
              <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: colors.teal }}>The Solution (continued)</p>
              
              <div className="flex-1 space-y-4">
                {data.solution.features.slice(2).map((feature, i) => (
                  <div key={i} className="p-4 rounded-lg border border-grey-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.teal }}>
                        <Check className="w-4 h-4 text-white" />
                      </div>
                      <h4 className="font-medium text-sm text-grey-900">{feature.title}</h4>
                    </div>
                    <p className="text-xs text-grey-600 mb-3 leading-relaxed">{feature.desc}</p>
                    <div className="flex flex-wrap gap-2">
                      {feature.details.map((detail, j) => (
                        <span key={j} className="text-xs px-3 py-1 rounded-full" style={{ backgroundColor: `${colors.teal}15`, color: colors.teal }}>{detail}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {type === 'membership' && 'experience' in data && (
                <div className="mt-5 p-4 rounded-lg" style={{ backgroundColor: bgColor }}>
                  <p className="text-xs font-medium mb-2" style={{ color: textOnBg, opacity: 0.9 }}>What Members Say:</p>
                  <p className="text-sm italic leading-relaxed" style={{ color: textOnBg }}>"{data.experience.testimonials[0].quote}"</p>
                  <p className="text-xs mt-2" style={{ color: type === 'agents' ? colors.teal : 'white' }}>— {data.experience.testimonials[0].name}, {data.experience.testimonials[0].title}</p>
                </div>
              )}
              
              <div className="mt-auto pt-4 border-t border-grey-100">
                <img src={brandAssets.logos.primary} alt="Hudson" className="h-6 w-auto object-contain max-w-full" style={{ objectFit: 'contain' }} />
              </div>
            </div>
          </div>

          {/* PAGE 6: Pricing */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden" style={{ width: '680px', aspectRatio: '8.5/11', backgroundColor: bgColor }}>
            <div className="h-full p-6 flex flex-col">
              <p className="text-xs font-medium tracking-widest uppercase mb-2" style={{ color: type === 'agents' ? colors.teal : `${textOnBg}80` }}>Investment</p>
              <h2 className="font-serif text-2xl mb-4" style={{ color: textOnBg }}>{data.pricing.headline}</h2>
              
              {'price' in data.pricing && (
                <div className="mb-5">
                  <span className="text-5xl font-light" style={{ color: type === 'agents' ? colors.teal : textOnBg }}>{data.pricing.price}</span>
                  <span className="text-2xl" style={{ color: textOnBg, opacity: 0.8 }}>{data.pricing.period}</span>
                  {'description' in data.pricing && (
                    <p className="text-sm mt-2" style={{ color: textOnBg, opacity: 0.8 }}>{data.pricing.description}</p>
                  )}
                </div>
              )}

              {'includes' in data.pricing && (
                <div className="flex-1">
                  <p className="text-sm font-medium mb-3" style={{ color: textOnBg, opacity: 0.9 }}>What's Included:</p>
                  <div className="space-y-2">
                    {data.pricing.includes.map((item, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: type === 'agents' ? colors.teal : 'white' }} />
                        <span className="text-sm" style={{ color: textOnBg }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {'tiers' in data.pricing && (
                <div className="flex-1 space-y-4">
                  {data.pricing.tiers.map((tier, i) => (
                    <div key={i} className="p-4 rounded-lg border" style={{ backgroundColor: tier.popular ? colors.teal : 'rgba(255,255,255,0.1)', borderColor: tier.popular ? 'transparent' : 'rgba(255,255,255,0.2)' }}>
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-medium text-sm" style={{ color: tier.popular ? 'white' : textOnBg }}>{tier.name}</h4>
                        <span className="text-xl font-light" style={{ color: tier.popular ? 'white' : textOnBg }}>{tier.price}</span>
                      </div>
                      <p className="text-xs mb-2" style={{ color: tier.popular ? 'rgba(255,255,255,0.9)' : `${textOnBg}80` }}>{tier.description}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-5 p-4 rounded-lg text-center" style={{ backgroundColor: type === 'agents' ? `${colors.teal}15` : 'rgba(255,255,255,0.15)' }}>
                <p className="text-sm font-medium" style={{ color: textOnBg }}>{data.pricing.guarantee.title}</p>
                <p className="text-xs mt-1" style={{ color: textOnBg, opacity: 0.8 }}>{data.pricing.guarantee.desc}</p>
              </div>
              
              <div className="mt-auto pt-4 border-t flex justify-between items-center" style={{ borderColor: `${textOnBg}20` }}>
                <img src={type === 'agents' ? brandAssets.logos.primary : brandAssets.logos.primaryWhite} alt="Hudson" className="h-6 w-auto object-contain max-w-full" style={{ objectFit: 'contain' }} />
                <p className="text-xs italic" style={{ color: textOnBg, opacity: 0.6 }}>We maintain your home so you don't have to.</p>
              </div>
            </div>
          </div>

          {/* PAGE 7: CTA */}
          <div className="brochure-page rounded-lg shadow-lg overflow-hidden" style={{ width: '680px', aspectRatio: '8.5/11', backgroundColor: bgColor }}>
            <div className="h-full p-8 flex flex-col justify-center items-center text-center">
              <img src={brandAssets.logos.primaryWhite} alt="Hudson" className="h-12 w-auto mb-10 object-contain max-w-full" style={{ objectFit: 'contain' }} />
              <h2 className="font-serif text-3xl text-white mb-4">{data.cta.headline}</h2>
              <p className="text-base text-white/90 mb-10 max-w-sm leading-relaxed">{data.cta.subhead}</p>
              
              <div className="w-full max-w-sm space-y-4">
                <div className="p-5 rounded-lg bg-white/10">
                  <p className="text-xs text-white/70 uppercase tracking-wide mb-2">Website</p>
                  <p className="text-white font-medium text-lg">{data.cta.url}</p>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1 p-4 rounded-lg bg-white/10">
                    <p className="text-xs text-white/70 uppercase tracking-wide mb-2">Phone</p>
                    <p className="text-white text-base">{data.cta.phone}</p>
                  </div>
                  <div className="flex-1 p-4 rounded-lg bg-white/10">
                    <p className="text-xs text-white/70 uppercase tracking-wide mb-2">Email</p>
                    <p className="text-white text-base">{data.cta.email}</p>
                  </div>
                </div>
              </div>
              
              <p className="mt-auto text-sm text-white/50 italic">We maintain your home so you don't have to.</p>
            </div>
          </div>

        </div>
      </div>

      {/* Thumbnail Preview - Removed, always showing full preview */}
      {false && (
        <div className="p-4 flex gap-2 overflow-x-auto">
          {['Cover', 'About', 'Problem', 'Solution', 'Features', 'Pricing', 'CTA'].slice(0, pageCount).map((page, i) => (
            <div 
              key={i} 
              className="w-12 h-16 rounded shadow-sm flex items-center justify-center text-[7px] font-medium flex-shrink-0"
              style={{ 
                backgroundColor: i === 0 || i === pageCount - 1 || i === pageCount - 2 ? bgColor : 'white',
                color: i === 0 || i === pageCount - 1 || i === pageCount - 2 ? (type === 'agents' && i !== pageCount - 1 ? colors.nearBlack : 'white') : colors.nearBlack,
                border: i === 0 || i === pageCount - 1 || i === pageCount - 2 ? 'none' : '1px solid #e5e5e5',
              }}
            >
              {page}
            </div>
          ))}
          <span className="text-xs text-grey-400 self-center ml-2">{pageCount} pages</span>
        </div>
      )}
    </div>
  )
}

// ============================================
// MAIN CONTENT
// ============================================
const BrandAssetsContent: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview')

  const navGroups = [
    {
      label: 'Overview',
      items: [
        { id: 'overview', label: 'Dashboard', icon: <Briefcase className="w-4 h-4" /> },
        { id: 'launch', label: 'Launch Plan', icon: <Target className="w-4 h-4" /> },
        { id: 'founder', label: 'Founder Graphics', icon: <Star className="w-4 h-4" /> },
      ]
    },
    {
      label: 'Service GTM',
      items: [
        { id: 'refresh', label: 'MyHome Refresh', icon: <Star className="w-4 h-4" /> },
        { id: 'smartstart', label: 'SmartStart', icon: <Shield className="w-4 h-4" /> },
        { id: 'membership', label: 'Membership', icon: <Users className="w-4 h-4" /> },
      ]
    },
    {
      label: 'Partner GTM',
      items: [
        { id: 'agents', label: 'Real Estate Agents', icon: <Home className="w-4 h-4" /> },
        { id: 'builders', label: 'Builders', icon: <Building2 className="w-4 h-4" /> },
      ]
    },
    {
      label: 'Marketing Assets',
      items: [
        { id: 'social', label: 'Social Media', icon: <Instagram className="w-4 h-4" /> },
        { id: 'ads', label: 'Google Ads', icon: <Search className="w-4 h-4" /> },
        { id: 'signatures', label: 'Email Signatures', icon: <Mail className="w-4 h-4" /> },
        { id: 'brochures', label: 'Brochures & Flyers', icon: <FileText className="w-4 h-4" /> },
      ]
    },
    {
      label: 'Brand',
      items: [
        { id: 'brand', label: 'Brand Guidelines', icon: <Palette className="w-4 h-4" /> },
      ]
    },
  ]

  return (
    <div className="min-h-screen bg-grey-50">
      {/* Header */}
      <header className="border-b border-white/10 sticky top-0 z-40" style={{ backgroundColor: '#0b1a1a' }}>
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Primary_Logo_White.png" 
                alt="Hudson My Home" 
                className="h-10 w-auto"
              />
              <div>
                <h1 className="text-xl font-serif text-white">Brand & Marketing Hub</h1>
                <p className="text-xs text-white/60">GTM Campaigns, Assets & Guidelines</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="text-sm text-white/60 hover:text-white transition-colors">← Back to Site</a>
              <span className="text-xs px-3 py-1 bg-green-500/20 text-green-300 rounded-full">Internal Use Only</span>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-64 bg-white border-r border-grey-200 min-h-[calc(100vh-73px)] sticky top-[73px] overflow-y-auto hidden lg:block">
          <nav className="p-4 space-y-6">
            {navGroups.map((group, groupIdx) => (
              <div key={groupIdx}>
                <p className="text-xs font-semibold text-grey-400 uppercase tracking-wider mb-2 px-3">
                  {group.label}
                </p>
                <div className="space-y-1">
                  {group.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                        activeTab === item.id
                          ? 'bg-teal-50 text-teal-700 font-medium'
                          : 'text-grey-600 hover:bg-grey-50 hover:text-grey-900'
                      }`}
                    >
                      <span className={activeTab === item.id ? 'text-teal-600' : 'text-grey-400'}>
                        {item.icon}
                      </span>
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile Tab Navigation */}
        <div className="lg:hidden bg-white border-b border-grey-200 sticky top-[73px] z-30 overflow-x-auto w-full">
          <div className="px-4 py-2">
            <select
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              className="w-full px-4 py-2 border border-grey-200 rounded-lg text-sm"
            >
              {navGroups.flatMap(group => group.items).map(item => (
                <option key={item.id} value={item.id}>{item.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 p-6 lg:p-8 max-w-6xl">
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'launch' && <LaunchPlanTab />}
          {activeTab === 'founder' && <FounderGraphicsTab />}
          {activeTab === 'refresh' && <MyHomeRefreshGTMTab />}
          {activeTab === 'membership' && <MembershipGTMTab />}
          {activeTab === 'agents' && <AgentsGTMTab />}
          {activeTab === 'builders' && <BuildersGTMTab />}
          {activeTab === 'smartstart' && <SmartStartGTMTab />}
          {activeTab === 'social' && <SocialMediaTab />}
          {activeTab === 'ads' && <GoogleAdsTab />}
          {activeTab === 'signatures' && <EmailSignaturesTab />}
          {activeTab === 'brochures' && <BrochuresTab />}
          {activeTab === 'brand' && <BrandGuidelinesTab />}
        </main>
      </div>
    </div>
  )
}

// ============================================
// LAUNCH PLAN TAB
// ============================================
const LaunchPlanTab: React.FC = () => (
  <div className="space-y-8">
    {/* Launch Overview */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}15` }}>
          <Target className="w-6 h-6" style={{ color: colors.teal }} />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">30-Day Launch Plan</h2>
          <p className="text-grey-500">Comprehensive go-to-market execution plan for Hudson My Home</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="p-5 rounded-xl" style={{ backgroundColor: colors.cream }}>
          <p className="text-sm font-medium text-grey-500 mb-1">Primary Goal</p>
          <p className="text-lg font-medium" style={{ color: colors.teal }}>25 Founding Members</p>
        </div>
        <div className="p-5 rounded-xl" style={{ backgroundColor: colors.cream }}>
          <p className="text-sm font-medium text-grey-500 mb-1">Secondary Goal</p>
          <p className="text-lg font-medium" style={{ color: colors.teal }}>50 MyHome Refresh Bookings</p>
        </div>
        <div className="p-5 rounded-xl" style={{ backgroundColor: colors.cream }}>
          <p className="text-sm font-medium text-grey-500 mb-1">Launch Date</p>
          <p className="text-lg font-medium" style={{ color: colors.teal }}>February 2026</p>
        </div>
      </div>
    </div>

    {/* Team Roles & Responsibilities */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-serif text-grey-900 mb-6">Team Roles & Responsibilities</h3>
      
      <div className="space-y-6">
        {/* Joe */}
        <div className="border border-grey-200 rounded-xl p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: colors.teal }}>JS</div>
            <div>
              <h4 className="font-medium text-grey-900">Joe Schafbuch — Founder & CEO</h4>
              <p className="text-sm text-grey-500">Strategic leadership, sales, partnerships</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Launch Responsibilities:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• Lead founding member outreach & sales calls</li>
                <li>• Builder partnership development</li>
                <li>• Real estate agent relationship building</li>
                <li>• Strategic messaging approval</li>
                <li>• VIP launch event hosting</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Key Metrics:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• 15+ discovery calls per week</li>
                <li>• 5 builder meetings scheduled</li>
                <li>• 10 agent partnerships initiated</li>
                <li>• 25 founding members closed</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Cassi */}
        <div className="border border-grey-200 rounded-xl p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: colors.copper }}>CL</div>
            <div>
              <h4 className="font-medium text-grey-900">Cassi Luree — Director of Operations</h4>
              <p className="text-sm text-grey-500">Operations, service delivery, vendor network</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Launch Responsibilities:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• Service provider network finalization</li>
                <li>• MyHome Refresh service checklist creation</li>
                <li>• Scheduling system setup & testing</li>
                <li>• Quality assurance protocols</li>
                <li>• Member onboarding process documentation</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Key Metrics:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• 10+ vetted service providers per category</li>
                <li>• 48hr service scheduling turnaround</li>
                <li>• 100% onboarding documentation complete</li>
                <li>• Zero service delivery failures</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bonnie */}
        <div className="border border-grey-200 rounded-xl p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: colors.nearBlack }}>BL</div>
            <div>
              <h4 className="font-medium text-grey-900">Bonnie Le Page — Fractional CMO</h4>
              <p className="text-sm text-grey-500">Marketing strategy, brand, campaigns</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Launch Responsibilities:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• 30-day social content calendar execution</li>
                <li>• Email nurture sequence deployment</li>
                <li>• Google Ads campaign management</li>
                <li>• PR & media outreach</li>
                <li>• Brand asset creation & approvals</li>
              </ul>
            </div>
            <div>
              <p className="text-sm font-medium text-grey-700 mb-2">Key Metrics:</p>
              <ul className="text-sm text-grey-600 space-y-1">
                <li>• 500+ website visitors/week</li>
                <li>• 100+ email subscribers</li>
                <li>• 5% email open rate improvement</li>
                <li>• 50+ qualified leads generated</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* 30-Day Timeline */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-serif text-grey-900 mb-6">30-Day Launch Timeline</h3>
      
      <div className="space-y-6">
        {/* Week 1 */}
        <div className="border-l-4 pl-6 py-2" style={{ borderColor: colors.teal }}>
          <h4 className="font-medium text-grey-900 mb-3">Week 1: Pre-Launch Preparation</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-grey-700 mb-2">Marketing</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Finalize all launch assets</li>
                <li>• Schedule 30-day social content</li>
                <li>• Set up email sequences</li>
                <li>• Configure ad campaigns</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Sales</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Warm outreach to network</li>
                <li>• VIP preview invitations</li>
                <li>• Sales script finalization</li>
                <li>• CRM setup & lead tracking</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Operations</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Service checklist approval</li>
                <li>• Vendor contracts signed</li>
                <li>• Scheduling system live</li>
                <li>• Team training complete</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Week 2 */}
        <div className="border-l-4 pl-6 py-2" style={{ borderColor: colors.copper }}>
          <h4 className="font-medium text-grey-900 mb-3">Week 2: Soft Launch</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-grey-700 mb-2">Marketing</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Launch day social blast</li>
                <li>• Email announcement to list</li>
                <li>• Activate Google Ads</li>
                <li>• Press release distribution</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Sales</p>
              <ul className="text-grey-600 space-y-1">
                <li>• First founding member calls</li>
                <li>• Agent partnership pitches</li>
                <li>• Builder introductions</li>
                <li>• Referral program launch</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Operations</p>
              <ul className="text-grey-600 space-y-1">
                <li>• First Refresh bookings</li>
                <li>• Service quality monitoring</li>
                <li>• Customer feedback collection</li>
                <li>• Process refinement</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Week 3 */}
        <div className="border-l-4 pl-6 py-2" style={{ borderColor: colors.tealDark }}>
          <h4 className="font-medium text-grey-900 mb-3">Week 3: Momentum Building</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-grey-700 mb-2">Marketing</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Testimonial collection</li>
                <li>• Social proof campaign</li>
                <li>• Retargeting ads live</li>
                <li>• Content amplification</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Sales</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Follow-up sequences</li>
                <li>• Objection handling refinement</li>
                <li>• Partner program sign-ups</li>
                <li>• Upsell opportunities</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Operations</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Scale service capacity</li>
                <li>• Member onboarding optimization</li>
                <li>• Vendor performance review</li>
                <li>• Documentation updates</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Week 4 */}
        <div className="border-l-4 pl-6 py-2" style={{ borderColor: colors.nearBlack }}>
          <h4 className="font-medium text-grey-900 mb-3">Week 4: Conversion Push</h4>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-grey-700 mb-2">Marketing</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Urgency campaign launch</li>
                <li>• Founding member deadline</li>
                <li>• Case study publication</li>
                <li>• Performance analysis</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Sales</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Final founding member push</li>
                <li>• Hot lead follow-ups</li>
                <li>• Partnership agreements</li>
                <li>• Next phase planning</li>
              </ul>
            </div>
            <div>
              <p className="font-medium text-grey-700 mb-2">Operations</p>
              <ul className="text-grey-600 space-y-1">
                <li>• Capacity planning Q2</li>
                <li>• Process documentation</li>
                <li>• Team expansion needs</li>
                <li>• Technology improvements</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Channel Strategy */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-serif text-grey-900 mb-6">Channel Strategy</h3>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">Digital Channels</h4>
          <div className="space-y-3">
            {[
              { channel: 'Instagram', focus: 'Brand awareness, lifestyle content', frequency: 'Daily' },
              { channel: 'Facebook', focus: 'Community building, testimonials', frequency: '3-4x/week' },
              { channel: 'LinkedIn', focus: 'Thought leadership, B2B partners', frequency: '2-3x/week' },
              { channel: 'Google Ads', focus: 'Lead generation, local targeting', frequency: 'Always on' },
              { channel: 'Email Marketing', focus: 'Nurture sequences, announcements', frequency: '2x/week' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
                <div>
                  <p className="font-medium text-grey-900">{item.channel}</p>
                  <p className="text-sm text-grey-500">{item.focus}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-white rounded text-grey-600">{item.frequency}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">Offline & Direct</h4>
          <div className="space-y-3">
            {[
              { channel: 'Direct Sales', focus: 'Warm outreach, referrals', frequency: 'Daily' },
              { channel: 'Networking Events', focus: 'HBA, real estate associations', frequency: 'Weekly' },
              { channel: 'Strategic Partnerships', focus: 'Builders, agents, vendors', frequency: 'Ongoing' },
              { channel: 'Local PR', focus: 'Des Moines media, business journals', frequency: 'Monthly' },
              { channel: 'Referral Program', focus: 'Member-to-member referrals', frequency: 'Always on' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
                <div>
                  <p className="font-medium text-grey-900">{item.channel}</p>
                  <p className="text-sm text-grey-500">{item.focus}</p>
                </div>
                <span className="text-xs px-2 py-1 bg-white rounded text-grey-600">{item.frequency}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    {/* Pre-Launch Checklist */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-serif text-grey-900 mb-6">Pre-Launch Checklist</h3>
      
      <div className="grid md:grid-cols-3 gap-6">
        <div>
          <h4 className="font-medium text-grey-900 mb-4 flex items-center gap-2">
            <Megaphone className="w-5 h-5" style={{ color: colors.teal }} />
            Marketing
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              'Website live and tested (hudsonmyhome.app)',
              'All social profiles created & branded',
              '30-day content calendar scheduled',
              'Email sequences built in platform',
              'Google Ads campaigns configured',
              'Tracking pixels installed',
              'Brand assets finalized',
              'Press kit prepared',
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-grey-600">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.teal }} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-medium text-grey-900 mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5" style={{ color: colors.copper }} />
            Sales
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              'CRM configured (lead tracking)',
              'Sales scripts documented',
              'Pricing finalized & tested',
              'Payment processing live',
              'Contract templates approved',
              'Discovery call scheduling set up',
              'Proposal templates created',
              'Objection handling guide complete',
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-grey-600">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.copper }} />
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-medium text-grey-900 mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" style={{ color: colors.nearBlack }} />
            Operations
          </h4>
          <ul className="space-y-2 text-sm">
            {[
              'Service checklists finalized',
              'Vendor network contracted',
              'Scheduling system operational',
              'Quality standards documented',
              'Onboarding process mapped',
              'Insurance & liability confirmed',
              'Team training completed',
              'Emergency protocols in place',
            ].map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-grey-600">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: colors.nearBlack }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>

    {/* KPIs & Metrics */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-serif text-grey-900 mb-6">Launch KPIs & Success Metrics</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-grey-200">
              <th className="text-left p-3 font-medium text-grey-900">Metric</th>
              <th className="text-left p-3 font-medium text-grey-900">Week 1</th>
              <th className="text-left p-3 font-medium text-grey-900">Week 2</th>
              <th className="text-left p-3 font-medium text-grey-900">Week 3</th>
              <th className="text-left p-3 font-medium text-grey-900">Week 4</th>
              <th className="text-left p-3 font-medium text-grey-900">30-Day Total</th>
            </tr>
          </thead>
          <tbody>
            {[
              { metric: 'Website Visitors', w1: '200', w2: '400', w3: '500', w4: '600', total: '1,700+' },
              { metric: 'Email Subscribers', w1: '25', w2: '35', w3: '25', w4: '15', total: '100+' },
              { metric: 'Qualified Leads', w1: '10', w2: '15', w3: '15', w4: '10', total: '50+' },
              { metric: 'Discovery Calls', w1: '5', w2: '10', w3: '10', w4: '10', total: '35+' },
              { metric: 'Founding Members', w1: '3', w2: '7', w3: '8', w4: '7', total: '25' },
              { metric: 'Refresh Bookings', w1: '5', w2: '15', w3: '15', w4: '15', total: '50' },
              { metric: 'Partner Sign-ups', w1: '1', w2: '2', w3: '2', w4: '3', total: '8+' },
            ].map((row, idx) => (
              <tr key={idx} className="border-b border-grey-100">
                <td className="p-3 font-medium text-grey-900">{row.metric}</td>
                <td className="p-3 text-grey-600">{row.w1}</td>
                <td className="p-3 text-grey-600">{row.w2}</td>
                <td className="p-3 text-grey-600">{row.w3}</td>
                <td className="p-3 text-grey-600">{row.w4}</td>
                <td className="p-3 font-medium" style={{ color: colors.teal }}>{row.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)

// ============================================
// FOUNDER GRAPHICS TAB - High-End Shareable Graphics
// ============================================
const FounderGraphicsTab: React.FC = () => {
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  // Download with fixed dimensions to prevent morphing
  const downloadGraphic = async (id: string, filename: string, width: number, height: number) => {
    const container = document.getElementById(id)
    if (!container) return

    setDownloadingId(id)
    try {
      // Clone the element to render at full size
      const clone = container.cloneNode(true) as HTMLElement
      clone.style.width = `${width}px`
      clone.style.height = `${height}px`
      clone.style.position = 'absolute'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      document.body.appendChild(clone)

      await preloadImagesForDownload(clone)
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        width: width,
        height: height,
      })
      
      document.body.removeChild(clone)
      
      const link = document.createElement('a')
      link.download = filename
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('Error downloading graphic:', error)
    } finally {
      setDownloadingId(null)
    }
  }

  // Reusable graphic card wrapper with fixed pixel sizing
  const GraphicCard: React.FC<{
    id: string
    title: string
    dimensions: string
    width: number
    height: number
    filename: string
    children: React.ReactNode
    previewScale?: number
  }> = ({ id, title, dimensions, width, height, filename, children, previewScale = 0.25 }) => (
    <div className="border border-grey-200 rounded-xl overflow-hidden">
      <div className="p-3 bg-grey-50 border-b border-grey-200 flex items-center justify-between">
        <span className="text-sm font-medium text-grey-700">{title}</span>
        <span className="text-xs text-grey-400">{dimensions}</span>
      </div>
      <div className="p-4 flex flex-col items-center">
        <div 
          className="overflow-hidden rounded-lg"
          style={{ 
            width: width * previewScale,
            height: height * previewScale,
          }}
        >
          <div 
            id={id}
            style={{ 
              width: `${width}px`,
              height: `${height}px`,
              transform: `scale(${previewScale})`,
              transformOrigin: 'top left',
            }}
          >
            {children}
          </div>
        </div>
        <button
          onClick={() => downloadGraphic(id, filename, width, height)}
          disabled={downloadingId === id}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: colors.teal, color: 'white', opacity: downloadingId === id ? 0.7 : 1 }}
        >
          {downloadingId === id ? 'Generating...' : <><Download className="w-4 h-4" /> Download PNG</>}
        </button>
      </div>
    </div>
  )

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl p-8 border border-grey-200">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}20` }}>
            <Star className="w-6 h-6" style={{ color: colors.teal }} />
          </div>
          <div>
            <h2 className="text-2xl font-serif text-grey-900">Founder Graphics</h2>
            <p className="text-grey-500">High-end shareable graphics for social media and presentations</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Brand Hero - 1200x628 */}
          <GraphicCard id="founder-hero-1" title="Brand Hero" dimensions="1200×628" width={1200} height={628} filename="hudson-brand-hero.png">
            <div 
              style={{ 
                width: '1200px',
                height: '628px',
                background: `linear-gradient(135deg, ${colors.nearBlack} 0%, ${colors.tealDark} 100%)`,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ 
                position: 'absolute', 
                inset: 0, 
                opacity: 0.3,
                backgroundImage: `url(${brandAssets.photos.stock1})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.8), rgba(0,0,0,0.6), transparent)' }} />
              <div style={{ position: 'relative', height: '100%', padding: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <img src={brandAssets.logos.primaryWhite} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <p style={{ fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: colors.copper, marginBottom: '12px' }}>White-Glove Home Management</p>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', color: 'white', lineHeight: 1.1, margin: 0 }}>We maintain your home</p>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', color: colors.copper, lineHeight: 1.1, margin: 0 }}>so you don&apos;t have to.</p>
                  <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginTop: '16px' }}>hudsonmyhome.app</p>
                </div>
              </div>
            </div>
          </GraphicCard>

          {/* Mission Square - 1080x1080 */}
          <GraphicCard id="founder-square-1" title="Mission Statement" dimensions="1080×1080" width={1080} height={1080} filename="hudson-mission-square.png" previewScale={0.22}>
            <div style={{ width: '1080px', height: '1080px', backgroundColor: colors.cream, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: '50%', height: '100%', opacity: 0.1, background: `linear-gradient(135deg, ${colors.teal}40, transparent)` }} />
              <div style={{ position: 'relative', height: '100%', padding: '64px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <img src={brandAssets.logos.primary} alt="Hudson" style={{ height: '56px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <div style={{ width: '80px', height: '6px', backgroundColor: colors.copper, marginBottom: '32px' }} />
                  <p style={{ fontSize: '42px', fontFamily: 'Instrument Serif, serif', color: colors.nearBlack, lineHeight: 1.3, margin: 0 }}>"Your home should feel like a sanctuary—not a second job."</p>
                  <p style={{ fontSize: '24px', color: colors.teal, marginTop: '32px', lineHeight: 1.4 }}>Monthly visits. Proactive care.<br />Complete peace of mind.</p>
                </div>
                <p style={{ fontSize: '18px', color: colors.copper, margin: 0 }}>hudsonmyhome.app</p>
              </div>
            </div>
          </GraphicCard>

          {/* Value Prop - 1200x628 */}
          <GraphicCard id="founder-hero-2" title="Value Proposition" dimensions="1200×628" width={1200} height={628} filename="hudson-value-prop.png">
            <div style={{ width: '1200px', height: '628px', backgroundColor: colors.teal, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, opacity: 0.1, backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div style={{ position: 'relative', height: '100%', padding: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <img src={brandAssets.logos.primaryWhite} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', color: 'white', lineHeight: 1.1, margin: 0 }}>From furnace filters</p>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', color: colors.cream, lineHeight: 1.1, margin: 0 }}>to full remodels.</p>
                  <div style={{ display: 'flex', gap: '48px', marginTop: '32px' }}>
                    <div><p style={{ fontSize: '36px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>$399</p><p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>/month</p></div>
                    <div><p style={{ fontSize: '36px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>Monthly</p><p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>visits</p></div>
                    <div><p style={{ fontSize: '36px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>5-Year</p><p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)', margin: 0 }}>home plan</p></div>
                  </div>
                </div>
                <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>Apply at hudsonmyhome.app</p>
              </div>
            </div>
          </GraphicCard>

          {/* Services Square - 1080x1080 */}
          <GraphicCard id="founder-square-2" title="Services Overview" dimensions="1080×1080" width={1080} height={1080} filename="hudson-services-square.png" previewScale={0.22}>
            <div style={{ width: '1080px', height: '1080px', backgroundColor: 'white', position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '12px', backgroundColor: colors.teal }} />
              <div style={{ padding: '64px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <div>
                  <img src={brandAssets.logos.primary} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain', marginBottom: '24px' }} />
                  <p style={{ fontSize: '42px', fontFamily: 'Instrument Serif, serif', color: colors.nearBlack, margin: 0 }}>What We Do</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {['Monthly in-home maintenance', 'Dedicated Hudson professional', 'MyHome Score™ quarterly', 'Vendor coordination', '5-year home plan'].map((text, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                      <span style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: colors.teal, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>✓</span>
                      <span style={{ fontSize: '24px', color: colors.nearBlack }}>{text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '22px', fontWeight: 500, color: colors.teal }}>Apply for membership →</span>
                  <span style={{ fontSize: '18px', color: colors.copper }}>hudsonmyhome.app</span>
                </div>
              </div>
            </div>
          </GraphicCard>

          {/* Story - 1080x1920 */}
          <GraphicCard id="founder-story-1" title="Story Card" dimensions="1080×1920" width={1080} height={1920} filename="hudson-story.png" previewScale={0.13}>
            <div style={{ width: '1080px', height: '1920px', background: `linear-gradient(180deg, ${colors.tealDark} 0%, ${colors.nearBlack} 100%)`, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', inset: 0, opacity: 0.4, backgroundImage: `url(${brandAssets.photos.stock4})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.6), transparent, rgba(0,0,0,0.8))' }} />
              <div style={{ position: 'relative', height: '100%', padding: '80px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <div style={{ textAlign: 'center' }}>
                  <img src={brandAssets.logos.primaryWhite} alt="Hudson" style={{ height: '72px', width: 'auto', margin: '0 auto 32px', display: 'block', objectFit: 'contain' }} />
                  <p style={{ fontSize: '20px', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Private Home Management</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '72px', fontFamily: 'Instrument Serif, serif', color: 'white', lineHeight: 1.2, margin: 0 }}>Your home<br />deserves a</p>
                  <p style={{ fontSize: '72px', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', color: colors.copper, lineHeight: 1.2, margin: 0 }}>Hudson.</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.7)', marginBottom: '16px' }}>Apply for membership</p>
                  <p style={{ fontSize: '28px', fontWeight: 500, color: 'white' }}>hudsonmyhome.app</p>
                </div>
              </div>
            </div>
          </GraphicCard>

          {/* Launch Announcement - 1200x628 */}
          <GraphicCard id="founder-announcement" title="Launch Announcement" dimensions="1200×628" width={1200} height={628} filename="hudson-launch.png">
            <div style={{ width: '1200px', height: '628px', backgroundColor: colors.nearBlack, position: 'relative' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: '8px', height: '100%', backgroundColor: '#C9A962' }} />
              <div style={{ height: '100%', padding: '48px', paddingLeft: '56px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <img src={brandAssets.logos.primaryWhite} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <p style={{ fontSize: '14px', letterSpacing: '0.3em', textTransform: 'uppercase', color: '#C9A962', marginBottom: '12px' }}>Now Accepting Founding Members</p>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', color: 'white', lineHeight: 1.1, margin: 0 }}>Hudson My Home</p>
                  <p style={{ fontSize: '48px', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', color: 'rgba(255,255,255,0.7)', lineHeight: 1.1, margin: 0 }}>is officially live.</p>
                  <p style={{ fontSize: '20px', color: 'rgba(255,255,255,0.6)', marginTop: '20px' }}>White-glove home management for Des Moines area homeowners.</p>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '20px', color: 'white' }}>Learn more →</span>
                  <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)' }}>hudsonmyhome.app</span>
                </div>
              </div>
            </div>
          </GraphicCard>
        </div>
      </div>

      {/* Additional Founder Graphics */}
      <div className="bg-white rounded-xl p-8 border border-grey-200">
        <h3 className="text-xl font-serif text-grey-900 mb-6">Testimonial & Social Proof Graphics</h3>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Testimonial - 1080x1080 */}
          <GraphicCard id="founder-testimonial" title="Testimonial Card" dimensions="1080×1080" width={1080} height={1080} filename="hudson-testimonial.png" previewScale={0.25}>
            <div style={{ width: '1080px', height: '1080px', backgroundColor: colors.cream, position: 'relative' }}>
              <div style={{ padding: '64px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <img src={brandAssets.logos.primary} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
                <div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
                    {[...Array(5)].map((_, i) => <span key={i} style={{ color: '#C9A962', fontSize: '32px' }}>★</span>)}
                  </div>
                  <p style={{ fontSize: '36px', fontFamily: 'Instrument Serif, serif', fontStyle: 'italic', color: colors.nearBlack, lineHeight: 1.4, margin: 0 }}>"I used to spend every Saturday dealing with house stuff. Now I spend it with my kids. Hudson My Home changed everything."</p>
                  <p style={{ fontSize: '22px', fontWeight: 500, color: colors.teal, marginTop: '32px' }}>— Sarah M., West Des Moines</p>
                </div>
                <p style={{ fontSize: '18px', color: colors.copper, margin: 0 }}>hudsonmyhome.app</p>
              </div>
            </div>
          </GraphicCard>

          {/* Stats - 1200x628 */}
          <GraphicCard id="founder-stats" title="Stats Card" dimensions="1200×628" width={1200} height={628} filename="hudson-stats.png">
            <div style={{ width: '1200px', height: '628px', backgroundColor: colors.tealDark, position: 'relative' }}>
              <div style={{ height: '100%', padding: '48px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxSizing: 'border-box' }}>
                <img src={brandAssets.logos.primaryWhite} alt="Hudson" style={{ height: '48px', width: 'auto', objectFit: 'contain' }} />
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '72px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>10+</p>
                    <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>Hours saved<br />per month</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '72px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>100%</p>
                    <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>Satisfaction<br />guaranteed</p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ fontSize: '72px', fontFamily: 'Instrument Serif, serif', color: 'white', margin: 0 }}>5yr</p>
                    <p style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)', marginTop: '8px' }}>Home plan<br />included</p>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '20px', color: 'rgba(255,255,255,0.8)' }}>Join the founding members →</span>
                  <span style={{ fontSize: '16px', color: 'rgba(255,255,255,0.5)' }}>hudsonmyhome.app</span>
                </div>
              </div>
            </div>
          </GraphicCard>
        </div>
      </div>

      {/* Quick Tips */}
      <div className="bg-gradient-to-r from-grey-50 to-white rounded-xl p-6 border border-grey-200">
        <h4 className="font-medium text-grey-900 mb-3">Sharing Tips for Founder</h4>
        <div className="grid md:grid-cols-3 gap-4 text-sm text-grey-600">
          <div className="flex items-start gap-2">
            <span style={{ color: colors.teal }}>•</span>
            <span><strong>LinkedIn:</strong> Use 1200×628 hero graphics for posts, share personal story with mission statement</span>
          </div>
          <div className="flex items-start gap-2">
            <span style={{ color: colors.teal }}>•</span>
            <span><strong>Instagram:</strong> Use 1080×1080 squares for feed, story cards for stories</span>
          </div>
          <div className="flex items-start gap-2">
            <span style={{ color: colors.teal }}>•</span>
            <span><strong>Facebook:</strong> Launch announcement works great for personal shares</span>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// OVERVIEW TAB
// ============================================
const OverviewTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-4">Go-To-Market Strategy Overview</h2>
      <p className="text-grey-600 mb-6">
        Hudson My Home serves three primary segments: Direct Homeowners (Membership), Real Estate Agents (Partners), 
        and Luxury Home Builders. Each segment requires tailored messaging, channels, and offers.
      </p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="p-6 bg-gradient-to-br rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.tealDark}, ${colors.teal})` }}>
          <Star className="w-10 h-10 mb-4 opacity-80" />
          <h3 className="text-xl font-medium mb-2">MyHome Refresh™</h3>
          <p className="text-white/70 text-sm mb-4">One-time preventive care experience. No commitment.</p>
          <div className="text-2xl font-light">$299-$399</div>
        </div>

        <div className="p-6 bg-gradient-to-br rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.teal}, ${colors.tealDark})` }}>
          <Users className="w-10 h-10 mb-4 opacity-80" />
          <h3 className="text-xl font-medium mb-2">Membership</h3>
          <p className="text-white/70 text-sm mb-4">White-glove subscription for high-net-worth homeowners</p>
          <div className="text-2xl font-light">$399/mo</div>
        </div>

        <div className="p-6 bg-gradient-to-br rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.copper}, #7a5d47)` }}>
          <Home className="w-10 h-10 mb-4 opacity-80" />
          <h3 className="text-xl font-medium mb-2">Real Estate Agents</h3>
          <p className="text-white/70 text-sm mb-4">Partner program with inspections & closing gifts</p>
          <div className="text-2xl font-light">$599/yr</div>
        </div>

        <div className="p-6 bg-gradient-to-br rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.nearBlack}, #2a2a2a)` }}>
          <Building2 className="w-10 h-10 mb-4 opacity-80" />
          <h3 className="text-xl font-medium mb-2">Luxury Builders</h3>
          <p className="text-white/70 text-sm mb-4">First-year warranty coordination & buyer experience</p>
          <div className="text-2xl font-light">Custom</div>
        </div>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Primary Channels</h3>
        <ul className="space-y-3">
          {[
            { channel: 'Google Ads', focus: 'High-intent search terms' },
            { channel: 'Meta Ads', focus: 'Lookalike audiences, retargeting' },
            { channel: 'LinkedIn', focus: 'Professional & executive targeting' },
            { channel: 'Email Marketing', focus: 'Nurture sequences, referrals' },
            { channel: 'Strategic Partnerships', focus: 'Wealth managers, CPAs' },
          ].map((item, idx) => (
            <li key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
              <span className="font-medium text-grey-900">{item.channel}</span>
              <span className="text-sm text-grey-500">{item.focus}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Key Metrics</h3>
        <ul className="space-y-3">
          {[
            { metric: 'Target CAC', value: '<$150', desc: 'Membership acquisition' },
            { metric: 'LTV Target', value: '$4,788', desc: '12-month retention' },
            { metric: 'Conversion Goal', value: '2.5%', desc: 'Landing page to trial' },
            { metric: 'Retention Target', value: '85%', desc: 'Annual membership renewal' },
            { metric: 'NPS Goal', value: '70+', desc: 'Member satisfaction' },
          ].map((item, idx) => (
            <li key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
              <div>
                <span className="font-medium text-grey-900">{item.metric}</span>
                <span className="text-xs text-grey-500 block">{item.desc}</span>
              </div>
              <span className="text-lg font-medium" style={{ color: colors.teal }}>{item.value}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>

    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h3 className="text-lg font-medium text-grey-900 mb-4">Campaign Timeline - Q1 2026</h3>
      <div className="space-y-4">
        {[
          { week: 'Week 1-2', activities: ['Launch Google Search campaigns', 'Deploy Meta retargeting pixels', 'Email list warm-up'] },
          { week: 'Week 3-4', activities: ['Instagram content calendar live', 'LinkedIn thought leadership posts', 'Agent outreach begins'] },
          { week: 'Week 5-6', activities: ['Builder partnership presentations', 'First member testimonial campaign', 'Referral program launch'] },
          { week: 'Week 7-8', activities: ['Campaign optimization', 'A/B test winners scaled', 'Q1 review and Q2 planning'] },
        ].map((phase, idx) => (
          <div key={idx} className="flex gap-6">
            <div className="w-24 flex-shrink-0 font-medium text-grey-900">{phase.week}</div>
            <div className="flex-1 flex flex-wrap gap-2">
              {phase.activities.map((activity, i) => (
                <span key={i} className="px-3 py-1 bg-grey-100 text-grey-700 text-sm rounded-full">{activity}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)

// ============================================
// MYHOME REFRESH GTM TAB
// ============================================
const MyHomeRefreshGTMTab: React.FC = () => (
  <div className="space-y-8">
    {/* Header */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}20` }}>
          <Star className="w-6 h-6" style={{ color: colors.teal }} />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">The MyHome Refresh™</h2>
          <p className="text-grey-500">A thoughtful reset for homeowners who want clarity, confidence, and proactive care.</p>
        </div>
      </div>

      <div className="p-6 rounded-xl mb-6" style={{ background: `linear-gradient(135deg, ${colors.cream}, #ffffff)` }}>
        <h3 className="text-lg font-medium text-grey-900 mb-3">What Is The MyHome Refresh™?</h3>
        <p className="text-grey-600 leading-relaxed">
          A one-time, preventive home care experience designed to give homeowners a clear understanding of their home&apos;s 
          condition and take care of the essentials in a single visit. It blends a professional home health review with 
          hands-on preventive maintenance, helping you stay ahead of the small issues that are easy to miss and costly to ignore.
        </p>
        <p className="text-grey-600 leading-relaxed mt-3">
          <strong>No membership required. No ongoing commitment.</strong> The MyHome Refresh™ stands confidently on its own, 
          while creating a strong foundation for future planning if you choose to continue.
        </p>
      </div>

        <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 border-2 border-grey-200 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h4 className="text-lg font-medium text-grey-900">MyHome Refresh</h4>
              <span className="text-xs text-grey-500">2 hours</span>
            </div>
            <span className="text-2xl font-light" style={{ color: colors.teal }}>$299</span>
          </div>
          <p className="text-sm text-grey-600 mb-4">Peace of mind through a one-time proactive tune-up.</p>
          <ul className="space-y-2 text-sm text-grey-700">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Mini MyHome Health Check (2 key systems of your choice)</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />On-site walkthrough with recommendations</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Mini MyHome Score™ with top 3 recommendations</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Seasonal maintenance (tailored to your chosen systems)</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />$50 credit toward future Hudson services</li>
          </ul>
        </div>

        <div className="p-6 border-2 rounded-xl" style={{ borderColor: colors.teal, backgroundColor: `${colors.teal}08` }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <span className="text-xs font-medium px-2 py-1 rounded-full text-white mb-2 inline-block" style={{ backgroundColor: colors.teal }}>Recommended</span>
              <h4 className="text-lg font-medium text-grey-900">MyHome Refresh Deluxe</h4>
              <span className="text-xs text-grey-500">3.5 hours</span>
            </div>
            <span className="text-2xl font-light" style={{ color: colors.teal }}>$399</span>
          </div>
          <p className="text-sm text-grey-600 mb-4">In-depth assessment, full preventive tune-up, detailed recommendations.</p>
          <ul className="space-y-2 text-sm text-grey-700">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Expanded MyHome Health Check (all major systems)</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />On-site walkthrough with recommendations</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Full MyHome Score™ showing home condition</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />Full seasonal maintenance (HVAC, plumbing, electrical, exterior)</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />$100 credit toward future Hudson services</li>
          </ul>
        </div>
      </div>
    </div>

    {/* Key Messaging */}
    <ExpandableSection title="Key Messaging & Value Props" icon={<Megaphone className="w-5 h-5" style={{ color: colors.teal }} />} defaultOpen>
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-grey-900 mb-4">Primary Headlines</h4>
            {[
              'A Smarter Way to Care for Your Home',
              'Clarity. Confidence. Proactive Care.',
              'Finally Know Where Your Home Stands',
              'The Reset Your Home Deserves',
              'Small Things Add Up. We Interrupt That Cycle.',
            ].map((headline, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg mb-2">
                <span className="text-grey-800">{headline}</span>
                <CopyButton text={headline} />
              </div>
            ))}
          </div>
          <div>
            <h4 className="font-medium text-grey-900 mb-4">Supporting Messages</h4>
            {[
              'No membership required. No ongoing commitment.',
              'Replace uncertainty with insight.',
              'Scattered to-do lists become clear priorities.',
              'Immediate value. Long-term clarity.',
              'A thoughtful first step toward confident homeownership.',
            ].map((message, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg mb-2">
                <span className="text-grey-700 text-sm">{message}</span>
                <CopyButton text={message} />
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-3">The Problem We Solve</h4>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { problem: 'Silent Deterioration', desc: 'Filters go unchanged. Safety devices go untested. Minor issues build quietly.' },
              { problem: 'Lack of Clarity', desc: 'Homeowners don\'t know where their home truly stands until something breaks.' },
              { problem: 'Overwhelm', desc: 'Scattered to-do lists with no clear prioritization of what matters most.' },
            ].map((item, idx) => (
              <div key={idx} className="p-3 bg-white rounded border border-grey-200">
                <h5 className="font-medium text-grey-900 text-sm">{item.problem}</h5>
                <p className="text-xs text-grey-600 mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Social Media Content */}
    <ExpandableSection title="Social Media Content" icon={<Instagram className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <SocialPostCard
          platform="Instagram"
          caption="Your home quietly keeps track of everything you forget. The filter you meant to change. The smoke detector you assumed was fine. The slow drain you've been ignoring. The MyHome Refresh™ is a single visit that catches up on all of it—and gives you a clear picture of where your home stands."
          hashtags={['#MyHomeRefresh', '#HomeHealth', '#ProactiveCare', '#HudsonMyHome']}
          design={{
            id: 'refresh-ig-1',
            layout: 'overlay',
            image: brandAssets.photos.stock2,
            logo: brandAssets.logos.primaryWhite,
            headline: 'The MyHome Refresh™',
            subhead: 'One visit. Complete clarity.',
            cta: 'Book Now',
            bgColor: colors.tealDark,
            accentColor: colors.teal,
            textColor: 'white',
            price: 'Starting at $299',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Instagram"
          caption="Small things add up. A clogged dryer vent. An untested GFCI outlet. A refrigerator coil covered in dust. They're easy to miss, costly to ignore. The MyHome Refresh™ takes care of it all—no membership required."
          hashtags={['#HomeMaintenance', '#PreventiveCare', '#MyHomeRefresh', '#HudsonMyHome']}
          design={{
            id: 'refresh-ig-2',
            layout: 'minimal',
            image: brandAssets.photos.stock6,
            logo: brandAssets.logos.primary,
            headline: 'Small things add up.',
            subhead: 'The MyHome Refresh™ catches what you miss.',
            cta: 'Learn More',
            bgColor: colors.cream,
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="When was the last time you felt truly confident about your home's condition? Not just hoping everything's fine—but actually knowing. The MyHome Refresh™ is a one-time visit that reviews your home's key systems, handles seasonal maintenance, and delivers a clear report on what matters most. Starting at $299. No membership required."
          hashtags={['#HomeConfidence', '#MyHomeRefresh', '#HudsonMyHome']}
          design={{
            id: 'refresh-fb-1',
            layout: 'split',
            image: brandAssets.photos.stock10,
            logo: brandAssets.logos.primary,
            headline: 'Finally know where your home stands.',
            subhead: 'Professional review. Preventive maintenance. Clear priorities.',
            cta: 'Book Your Refresh',
            bgColor: 'white',
            accentColor: colors.teal,
            textColor: colors.nearBlack,
            price: '$299',
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="LinkedIn"
          caption="As business owners, we invest in preventive maintenance for our companies. Annual reviews. System audits. Proactive planning. Why not for our homes? The MyHome Refresh™ brings the same strategic thinking to home care: a professional assessment, preventive maintenance, and a clear roadmap. One visit. Complete clarity."
          hashtags={['#SmartHomeowner', '#PreventiveMaintenance', '#MyHomeRefresh']}
          design={{
            id: 'refresh-li-1',
            layout: 'feature',
            image: brandAssets.photos.stock14,
            logo: brandAssets.logos.primary,
            headline: 'Strategic home care for business owners.',
            subhead: 'The same proactive approach you use in business—applied to your home.',
            cta: 'Schedule Now',
            bgColor: colors.cream,
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="Instagram"
          caption="The biggest benefit isn't just what gets done. It's the confidence that comes from finally knowing where your home truly stands. Book your MyHome Refresh™ today."
          hashtags={['#HomeClarity', '#MyHomeRefresh', '#PeaceOfMind', '#HudsonMyHome']}
          design={{
            id: 'refresh-ig-3',
            layout: 'testimonial',
            image: brandAssets.photos.stock18,
            logo: brandAssets.logos.primaryWhite,
            headline: 'The peace of mind is worth every penny.',
            subhead: 'Recent MyHome Refresh Customer',
            bgColor: colors.teal,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="MyHome Refresh: $299 - Home health check (2 systems), seasonal maintenance, Mini MyHome Score, $50 future credit. Refresh Deluxe: $399 - All systems review, full seasonal maintenance, Full MyHome Score, $100 future credit. No membership. Just clarity."
          hashtags={['#MyHomeRefresh', '#HomeCare', '#HudsonMyHome']}
          design={{
            id: 'refresh-fb-2',
            layout: 'card',
            image: brandAssets.photos.stock22,
            logo: brandAssets.logos.primaryWhite,
            headline: 'Choose Your Refresh',
            subhead: 'Refresh $299 • Deluxe $399',
            cta: 'Compare Options',
            bgColor: colors.tealDark,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1200x628"
        />
      </div>
    </ExpandableSection>

    {/* Google Ads */}
    <ExpandableSection title="Google Ads" icon={<Search className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          <AdCard
            type="Responsive Search Ad"
            headline="Home Health Check - $299"
            description="One visit. Complete clarity. Professional review of HVAC, plumbing, electrical & safety. Plus seasonal maintenance. Book your MyHome Refresh today."
            cta="Schedule Your Refresh"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="When Did You Last Check Your Home?"
            description="Small issues build quietly. Don't wait for a breakdown. The MyHome Refresh reviews key systems and handles preventive maintenance. Starting at $299."
            cta="Book Now"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="Home Maintenance Made Simple"
            description="Stop guessing about your home's condition. Get a professional assessment, seasonal maintenance, and clear priorities. No membership required."
            cta="Get Started"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="The MyHome Refresh - One Visit"
            description="Professional home health review + hands-on maintenance. Clear report with prioritized recommendations. $299 Refresh or $399 Deluxe."
            cta="Learn More"
          />
        </div>

        <div>
          <h4 className="font-medium text-grey-900 mb-4">Target Keywords</h4>
          <div className="flex flex-wrap gap-2">
            {[
              'home maintenance service',
              'home health check',
              'preventive home maintenance',
              'home inspection service',
              'seasonal home maintenance',
              'home tune up',
              'home care service near me',
              'HVAC maintenance service',
              'home safety inspection',
              'home maintenance checklist service',
            ].map((keyword, idx) => (
              <span key={idx} className="px-3 py-1 bg-grey-100 text-grey-700 text-sm rounded-full">{keyword}</span>
            ))}
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Email Sequences */}
    <ExpandableSection title="Email Sequences" icon={<Mail className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="space-y-6">
        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-3">Lead Nurture Sequence (5 Emails)</h4>
          <div className="space-y-3">
            {[
              { day: 'Day 0', subject: 'Your home is keeping score', content: 'Introduction to the MyHome Refresh concept. Paint the picture of silent deterioration. Soft CTA to learn more.' },
              { day: 'Day 2', subject: 'Small things add up (here\'s what we mean)', content: 'Specific examples: clogged dryer vents, untested smoke detectors, refrigerator coils. Link to infographic.' },
              { day: 'Day 4', subject: 'What 87% of homeowners don\'t know', content: 'Statistics on deferred maintenance costs. Position the Refresh as the smart alternative to reactive repairs.' },
              { day: 'Day 7', subject: 'Essential vs. Deluxe: Which is right for you?', content: 'Clear comparison of both tiers. Help them self-select based on their needs and home age.' },
              { day: 'Day 10', subject: 'This week only: $25 off your MyHome Refresh', content: 'Time-sensitive offer. Clear CTA to book. Testimonial from recent Refresh customer.' },
            ].map((email, idx) => (
              <div key={idx} className="flex items-start gap-4 p-3 bg-white rounded border border-grey-200">
                <span className="text-xs font-medium px-2 py-1 bg-grey-100 rounded text-grey-600">{email.day}</span>
                <div className="flex-1">
                  <p className="font-medium text-grey-900">{email.subject}</p>
                  <p className="text-sm text-grey-500">{email.content}</p>
                </div>
                <CopyButton text={`Subject: ${email.subject}\n\n${email.content}`} />
              </div>
            ))}
          </div>
        </div>

        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-3">Post-Refresh Follow-Up (Membership Upsell)</h4>
          <div className="space-y-3">
            {[
              { day: 'Day 1', subject: 'Your MyHome Snapshot is ready', content: 'Deliver the report. Highlight key findings. Thank them for trusting Hudson.' },
              { day: 'Day 7', subject: 'Did you tackle that [specific finding]?', content: 'Personalized follow-up based on their report. Offer to help coordinate any repairs.' },
              { day: 'Day 14', subject: 'What if you never had to think about this again?', content: 'Introduce the membership concept. Position as the natural next step for those who loved the Refresh.' },
              { day: 'Day 21', subject: 'Your $100 credit expires soon', content: 'Reminder about their included credit. CTA to either book another service or explore membership.' },
            ].map((email, idx) => (
              <div key={idx} className="flex items-start gap-4 p-3 bg-white rounded border border-grey-200">
                <span className="text-xs font-medium px-2 py-1 bg-grey-100 rounded text-grey-600">{email.day}</span>
                <div className="flex-1">
                  <p className="font-medium text-grey-900">{email.subject}</p>
                  <p className="text-sm text-grey-500">{email.content}</p>
                </div>
                <CopyButton text={`Subject: ${email.subject}\n\n${email.content}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Landing Page Strategy */}
    <ExpandableSection title="Landing Page Strategy" icon={<Target className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-grey-900 mb-4">Page Structure</h4>
            <div className="space-y-2">
              {[
                { section: 'Hero', content: 'Headline + value prop + dual CTA (Essential/Deluxe)' },
                { section: 'Problem', content: 'Why homes need a reset - small things add up' },
                { section: 'Solution', content: 'What the Refresh delivers - immediate value, long-term clarity' },
                { section: 'Comparison', content: 'Essential vs Deluxe side-by-side' },
                { section: 'What\'s Included', content: 'Detailed breakdown of services' },
                { section: 'Testimonials', content: 'Quotes from Refresh customers' },
                { section: 'FAQ', content: 'Common questions and objections' },
                { section: 'CTA', content: 'Book now with trust badges and guarantee' },
              ].map((item, idx) => (
                <div key={idx} className="p-3 bg-grey-50 rounded-lg flex justify-between">
                  <span className="font-medium text-grey-900 text-sm">{item.section}</span>
                  <span className="text-grey-600 text-sm">{item.content}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-grey-900 mb-4">Trust Elements</h4>
            <ul className="space-y-3">
              {[
                'Satisfaction guarantee: "Love it or it\'s free"',
                'No membership required messaging',
                'Included credit toward future services',
                'Licensed and insured badge',
                'Local Des Moines business',
                'Before/after photo gallery',
                'Video testimonials from Refresh customers',
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-grey-700">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Brochure Content */}
    <ExpandableSection title="Sales Brochure" icon={<FileText className="w-5 h-5" style={{ color: colors.teal }} />}>
      <DesignedBrochure
        type="refresh"
        title="MyHome Refresh™ Sales Brochure"
        audience="Homeowners seeking one-time preventive care, no membership commitment"
      />
    </ExpandableSection>

    {/* Seasonal Campaigns */}
    <ExpandableSection title="Seasonal Campaign Ideas" icon={<Calendar className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="grid md:grid-cols-2 gap-6">
        {[
          {
            season: 'Spring',
            theme: 'Spring Reset',
            headline: 'Give Your Home a Fresh Start',
            focus: ['HVAC tune-up for cooling season', 'Exterior inspection after winter', 'Gutter and drainage check', 'Window seal inspection'],
            offer: '$25 off Deluxe Refresh',
          },
          {
            season: 'Summer',
            theme: 'Beat the Heat',
            headline: 'Is Your Home Ready for Summer?',
            focus: ['A/C performance check', 'Refrigerator coil cleaning', 'Dryer vent safety', 'Ceiling fan direction'],
            offer: 'Free A/C filter with any Refresh',
          },
          {
            season: 'Fall',
            theme: 'Winter Prep',
            headline: 'Prepare Your Home Before the Cold',
            focus: ['Furnace inspection', 'Weather stripping check', 'Smoke/CO detector testing', 'Pipe insulation review'],
            offer: '$50 off when booked before Oct 31',
          },
          {
            season: 'Winter',
            theme: 'New Year Refresh',
            headline: 'Start the Year with Confidence',
            focus: ['Complete home health check', 'Safety device testing', 'Energy efficiency review', 'Priority planning for spring'],
            offer: 'Free Home Health Score upgrade',
          },
        ].map((campaign, idx) => (
          <div key={idx} className="p-6 border border-grey-200 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ backgroundColor: `${colors.teal}20`, color: colors.teal }}>{campaign.season}</span>
              <span className="text-sm text-grey-500">{campaign.theme}</span>
            </div>
            <h4 className="text-lg font-medium text-grey-900 mb-3">{campaign.headline}</h4>
            <ul className="space-y-1 mb-4">
              {campaign.focus.map((item, i) => (
                <li key={i} className="text-sm text-grey-600 flex items-center gap-2">
                  <Check className="w-3 h-3 text-green-600" />{item}
                </li>
              ))}
            </ul>
            <div className="p-3 bg-grey-50 rounded-lg">
              <span className="text-xs text-grey-500 uppercase">Offer</span>
              <p className="font-medium text-grey-900">{campaign.offer}</p>
            </div>
          </div>
        ))}
      </div>
    </ExpandableSection>
  </div>
)

// ============================================
// MEMBERSHIP GTM TAB
// ============================================
const MembershipGTMTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}15` }}>
          <Users className="w-6 h-6" style={{ color: colors.teal }} />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">Membership GTM Campaign</h2>
          <p className="text-grey-500">Direct-to-Consumer Strategy for High-Net-Worth Homeowners</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-medium text-grey-900 mb-4">Target Audience</h3>
          <div className="space-y-3">
            <div className="p-4 bg-grey-50 rounded-lg">
              <h4 className="font-medium text-grey-900">Demographics</h4>
              <ul className="mt-2 text-sm text-grey-600 space-y-1">
                <li>• Age: 35-65</li>
                <li>• HHI: $250K+</li>
                <li>• Home value: $500K+</li>
                <li>• Multiple property owners</li>
              </ul>
            </div>
            <div className="p-4 bg-grey-50 rounded-lg">
              <h4 className="font-medium text-grey-900">Psychographics</h4>
              <ul className="mt-2 text-sm text-grey-600 space-y-1">
                <li>• Values time over money</li>
                <li>• Strategic Coach members</li>
                <li>• Busy professionals & executives</li>
                <li>• Desire for white-glove service</li>
              </ul>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-grey-900 mb-4">Value Proposition</h3>
          <div className="p-6 rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.teal}, ${colors.tealDark})` }}>
            <p className="text-xl font-serif italic mb-4">"We maintain your home so you don't have to."</p>
            <p className="text-white/80 text-sm">
              Your dedicated Hudson professional manages your home&apos;s entire 5-year plan. 
              Monthly visits. Proactive care. Complete peace of mind.
            </p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            {['Dedicated professional', 'Monthly in-home visits', '24/7 priority support', 'Vendor oversight'].map((benefit, idx) => (
              <div key={idx} className="flex items-center gap-2 p-3 bg-grey-50 rounded-lg">
                <Check className="w-4 h-4 text-green-600" />
                <span className="text-sm text-grey-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>

    <ExpandableSection title="Email Sequences" icon={<Mail className="w-5 h-5" style={{ color: colors.teal }} />} defaultOpen>
      <div className="space-y-6">
        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-3">Welcome Sequence (5 Emails)</h4>
          <div className="space-y-3">
            {[
              { day: 'Day 0', subject: 'Welcome to Hudson My Home', content: 'Thank you for your interest. Here\'s what sets us apart...' },
              { day: 'Day 2', subject: 'What would you do with 10 extra hours?', content: 'Most homeowners spend 10+ hours/month on home maintenance. What if you could reclaim that time?' },
              { day: 'Day 4', subject: 'Meet your dedicated Hudson professional', content: 'Introduce the concept of a dedicated home professional who knows your home intimately.' },
              { day: 'Day 7', subject: 'How one homeowner saved $15K', content: 'Case study: Early issue detection that prevented costly repairs.' },
              { day: 'Day 10', subject: 'Your exclusive invitation', content: 'Limited spots available. Apply for membership today.' },
            ].map((email, idx) => (
              <div key={idx} className="flex items-start gap-4 p-3 bg-white rounded border border-grey-200">
                <span className="text-xs font-medium px-2 py-1 bg-grey-100 rounded text-grey-600">{email.day}</span>
                <div className="flex-1">
                  <p className="font-medium text-grey-900">{email.subject}</p>
                  <p className="text-sm text-grey-500">{email.content}</p>
                </div>
                <CopyButton text={`Subject: ${email.subject}\n\n${email.content}`} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </ExpandableSection>

    <ExpandableSection title="Landing Page Strategy" icon={<Target className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">Primary Headlines</h4>
          {[
            'Your Home Deserves a Director, Not a To-Do List',
            'White-Glove Home Care for Those Who Value Their Time',
            'Stop Managing Your Home. Start Enjoying It.',
          ].map((headline, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
              <span className="text-grey-800">{headline}</span>
              <CopyButton text={headline} />
            </div>
          ))}
        </div>
        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">CTAs</h4>
          {[
            { cta: 'Discover Home Membership', type: 'Primary' },
            { cta: 'Take the Assessment', type: 'Secondary' },
            { cta: 'Schedule a Consultation', type: 'Tertiary' },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-grey-50 rounded-lg">
              <div>
                <span className="font-medium text-grey-900">{item.cta}</span>
                <span className="text-xs text-grey-500 ml-2">{item.type}</span>
              </div>
              <CopyButton text={item.cta} />
            </div>
          ))}
        </div>
      </div>
    </ExpandableSection>

    <ExpandableSection title="Referral Program" icon={<Users className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-6 bg-grey-50 rounded-xl">
          <h4 className="font-medium text-grey-900 mb-4">Member Rewards</h4>
          <ul className="space-y-3">
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium">1</div>
              <span>1 referral: 1 month free ($399 value)</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium">3</div>
              <span>3 referrals: Annual membership discount (20% off)</span>
            </li>
            <li className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium">5</div>
              <span>5 referrals: VIP concierge upgrade</span>
            </li>
          </ul>
        </div>
        <div className="p-6 bg-grey-50 rounded-xl">
          <h4 className="font-medium text-grey-900 mb-4">New Member Offer</h4>
          <p className="text-grey-600 mb-4">Referred members receive:</p>
          <ul className="space-y-2 text-grey-700">
            <li>• First month at 50% off</li>
            <li>• Complimentary home assessment</li>
            <li>• Priority scheduling</li>
          </ul>
        </div>
      </div>
    </ExpandableSection>
  </div>
)

// ============================================
// AGENTS GTM TAB
// ============================================
const AgentsGTMTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${colors.teal}15` }}>
          <Home className="w-6 h-6" style={{ color: colors.teal }} />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">Real Estate Agent GTM Campaign</h2>
          <p className="text-grey-500">Partner Program for Top-Producing Agents</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="p-6 border border-grey-200 rounded-xl">
          <DollarSign className="w-8 h-8 mb-4" style={{ color: colors.teal }} />
          <h3 className="text-lg font-medium text-grey-900 mb-2">$599/Year</h3>
          <p className="text-sm text-grey-600">Annual partner membership with exclusive benefits</p>
        </div>
        <div className="p-6 border border-grey-200 rounded-xl">
          <Star className="w-8 h-8 mb-4" style={{ color: colors.teal }} />
          <h3 className="text-lg font-medium text-grey-900 mb-2">Closing Gifts</h3>
          <p className="text-sm text-grey-600">Discounted Hudson memberships as closing gifts</p>
        </div>
        <div className="p-6 border border-grey-200 rounded-xl">
          <Shield className="w-8 h-8 mb-4" style={{ color: colors.teal }} />
          <h3 className="text-lg font-medium text-grey-900 mb-2">Pre-Listing</h3>
          <p className="text-sm text-grey-600">Discounted inspection services for listings</p>
        </div>
      </div>
    </div>

    <ExpandableSection title="Outreach Templates" icon={<Mail className="w-5 h-5" style={{ color: colors.teal }} />} defaultOpen>
      <div className="space-y-4">
        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-2">Cold Email - Initial Outreach</h4>
          <div className="p-4 bg-white rounded border border-grey-200 text-sm text-grey-700 whitespace-pre-wrap">
{`Subject: A closing gift they'll actually use

Hi [Agent Name],

I noticed you're one of the top producers in [Market]. Your clients clearly trust you with one of the biggest decisions of their lives.

What if you could extend that trust beyond closing day?

Hudson My Home is a white-glove home management service that we're offering exclusively to select agents as closing gifts.

Unlike a bottle of wine or a cutting board, this gift keeps giving:
• Monthly professional home visits
• Proactive maintenance coordination  
• 24/7 priority support

Your buyers remember you every month when their Hudson visits.

Would you be open to a 15-minute call to see if this fits your business?

Best,
[Your Name]`}
          </div>
          <div className="mt-2">
            <CopyButton text="Subject: A closing gift they'll actually use\n\nHi [Agent Name]..." />
          </div>
        </div>

        <div className="p-4 bg-grey-50 rounded-lg">
          <h4 className="font-medium text-grey-900 mb-2">Follow-Up Email</h4>
          <div className="p-4 bg-white rounded border border-grey-200 text-sm text-grey-700 whitespace-pre-wrap">
{`Subject: Quick question about your listings

Hi [Agent Name],

Following up on my note about Hudson My Home.

Quick question: How much time do your buyers spend calling you about home maintenance questions after closing?

Our agents tell us it's one of the biggest time drains. Hudson eliminates those calls because we become their go-to resource.

5 minutes to show you how it works?

[Your Name]`}
          </div>
        </div>
      </div>
    </ExpandableSection>

    <ExpandableSection title="Partnership Tiers" icon={<Star className="w-5 h-5" style={{ color: colors.teal }} />}>
      <div className="grid md:grid-cols-3 gap-6">
        {[
          {
            tier: 'Partner',
            price: '$599/year',
            benefits: ['10% off pre-listing inspections', '5% off closing gift memberships', 'In-app client marketing', 'Partner badge for marketing'],
          },
          {
            tier: 'Premier Partner',
            price: '$999/year',
            benefits: ['15% off pre-listing inspections', '10% off closing gift memberships', 'Priority scheduling', 'Co-branded materials', 'Quarterly market reports'],
          },
          {
            tier: 'Elite Partner',
            price: '$1,999/year',
            benefits: ['20% off all services', '15% off closing gift memberships', 'Dedicated account manager', 'Exclusive events', 'First access to new services'],
          },
        ].map((tier, idx) => (
          <div key={idx} className={`p-6 rounded-xl border-2 ${idx === 1 ? 'border-teal bg-teal/5' : 'border-grey-200'}`}>
            {idx === 1 && <span className="text-xs font-medium px-2 py-1 text-white rounded-full mb-4 inline-block" style={{ backgroundColor: colors.teal }}>Most Popular</span>}
            <h4 className="text-lg font-medium text-grey-900">{tier.tier}</h4>
            <p className="text-2xl font-light mt-2 mb-4" style={{ color: colors.teal }}>{tier.price}</p>
            <ul className="space-y-2">
              {tier.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-grey-600">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </ExpandableSection>
  </div>
)

// ============================================
// BUILDERS GTM TAB
// ============================================
const BuildersGTMTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-grey-900">
          <Building2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">Luxury Builders GTM Campaign</h2>
          <p className="text-grey-500">First-Year Warranty Coordination & Buyer Experience</p>
        </div>
      </div>

      <div className="p-6 bg-grey-50 rounded-xl mb-6">
        <h3 className="text-lg font-medium text-grey-900 mb-4">The Builder Problem We Solve</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { problem: 'Blue Tape Nightmares', desc: 'Endless punch list items that drag on for months' },
            { problem: 'Post-Closing Calls', desc: 'Homeowners calling you 6 months later with questions' },
            { problem: 'Warranty Coordination', desc: 'Managing subcontractor callbacks and scheduling' },
          ].map((item, idx) => (
            <div key={idx} className="p-4 bg-white rounded-lg border border-grey-200">
              <h4 className="font-medium text-grey-900 mb-1">{item.problem}</h4>
              <p className="text-sm text-grey-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 rounded-xl text-white" style={{ background: `linear-gradient(135deg, ${colors.nearBlack}, #2a2a2a)` }}>
        <h3 className="text-lg font-medium mb-4">The Hudson Solution</h3>
        <p className="text-white/80 mb-6">
          We become the homeowner&apos;s single point of contact after closing. We handle all warranty-related 
          coordination, monthly maintenance visits, and homeowner communication for the first year.
        </p>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { stat: '100%', label: 'Fewer builder calls' },
            { stat: '90%', label: 'Warranty issues resolved' },
            { stat: '4.9★', label: 'Buyer satisfaction' },
            { stat: '85%', label: 'Referral rate' },
          ].map((item, idx) => (
            <div key={idx} className="text-center">
              <div className="text-3xl font-light mb-1" style={{ color: colors.teal }}>{item.stat}</div>
              <div className="text-sm text-white/60">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <ExpandableSection title="Builder Presentation Deck" icon={<FileText className="w-5 h-5 text-grey-600" />} defaultOpen>
      <div className="space-y-4">
        <p className="text-grey-600 mb-4">Key slides for builder presentations:</p>
        {[
          { slide: 1, title: 'The Post-Closing Problem', content: 'Statistics on builder callbacks, warranty costs, and reputation impact' },
          { slide: 2, title: 'The Hudson Solution', content: 'How we become the single point of contact for homeowners' },
          { slide: 3, title: 'How It Works', content: 'Step-by-step process from closing to year-end' },
          { slide: 4, title: 'What We Handle', content: 'Monthly visits, warranty coordination, maintenance requests' },
          { slide: 5, title: 'ROI Calculator', content: 'Time saved, callback reduction, referral increase' },
          { slide: 6, title: 'Partnership Options', content: 'Pricing tiers and implementation' },
          { slide: 7, title: 'Case Study', content: 'Real results from current builder partners' },
          { slide: 8, title: 'Next Steps', content: 'Pilot program offer and timeline' },
        ].map((item) => (
          <div key={item.slide} className="flex items-start gap-4 p-4 bg-grey-50 rounded-lg">
            <span className="w-8 h-8 flex items-center justify-center bg-grey-200 rounded text-grey-600 font-medium text-sm">{item.slide}</span>
            <div>
              <h4 className="font-medium text-grey-900">{item.title}</h4>
              <p className="text-sm text-grey-600">{item.content}</p>
            </div>
          </div>
        ))}
        <DownloadButton label="Download Full Presentation" />
      </div>
    </ExpandableSection>

    <ExpandableSection title="Builder Outreach Strategy" icon={<Megaphone className="w-5 h-5 text-grey-600" />}>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">Target Builder Profiles</h4>
          <div className="space-y-3">
            {[
              { type: 'Custom Home Builders', criteria: '10-50 homes/year, $750K+ price point' },
              { type: 'Luxury Spec Builders', criteria: '5-20 homes/year, $1M+ price point' },
              { type: 'High-End Remodelers', criteria: '$200K+ projects, repeat clients' },
            ].map((profile, idx) => (
              <div key={idx} className="p-4 bg-grey-50 rounded-lg">
                <h5 className="font-medium text-grey-900">{profile.type}</h5>
                <p className="text-sm text-grey-600">{profile.criteria}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-4">
          <h4 className="font-medium text-grey-900">Outreach Channels</h4>
          <div className="space-y-3">
            {[
              { channel: 'Home Builders Association', action: 'Sponsor local HBA events' },
              { channel: 'Parade of Homes', action: 'Partner with featured builders' },
              { channel: 'Direct Outreach', action: 'LinkedIn + email sequence' },
              { channel: 'Architect Referrals', action: 'Partner with luxury architects' },
            ].map((item, idx) => (
              <div key={idx} className="p-4 bg-grey-50 rounded-lg flex items-center justify-between">
                <span className="font-medium text-grey-900">{item.channel}</span>
                <span className="text-sm text-grey-600">{item.action}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </ExpandableSection>
  </div>
)

// ============================================
// SMARTSTART GTM TAB
// ============================================
const SmartStartGTMTab: React.FC = () => (
  <div className="space-y-8">
    {/* Overview */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: colors.teal }}>
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-2xl font-serif text-grey-900">MyHome SmartStart</h2>
          <p className="text-grey-500">New Home Reset Service for Real Estate Agents</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="font-semibold text-grey-900 mb-3">Service Overview</h3>
          <p className="text-grey-600 mb-4">
            SmartStart ensures a new home is safe, secure, efficient, and protected from preventable issues from day one—before life gets busy and small problems become expensive ones.
          </p>
          <p className="text-grey-600 mb-4 italic">
            "Because a new home should feel like a true reset—not a question mark."
          </p>
          <div className="p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
            <p className="text-sm font-medium text-grey-900 mb-2">The Perfect Agent Closing Gift</p>
            <ul className="text-sm text-grey-600 space-y-1">
              <li>• Thoughtful, practical, & memorable</li>
              <li>• Protects the client after closing</li>
              <li>• Reinforces trust and long-term value</li>
              <li>• Positions the agent as the advocate—not just a salesperson</li>
            </ul>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-grey-900 mb-3">Target Market</h3>
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-green-50 border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-1">Perfect For:</p>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Real Estate Agents as closing gifts</li>
                <li>• New Homeowners</li>
                <li>• First-Time Homebuyers</li>
                <li>• Pending closing transactions</li>
              </ul>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-sm font-medium text-amber-800 mb-1">Not Designed For:</p>
              <ul className="text-sm text-amber-700">
                <li>• New Construction Builds (come with builder warranties)</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Pricing Tiers */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-semibold text-grey-900 mb-6">Pricing Tiers</h3>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Essential */}
        <div className="border-2 border-grey-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-xl font-bold text-grey-900">SmartStart Essential</h4>
            <span className="px-3 py-1 rounded-full text-sm bg-grey-100 text-grey-600">2 hours</span>
          </div>
          <div className="mb-4">
            <span className="text-3xl font-bold" style={{ color: colors.teal }}>$299</span>
            <span className="text-sm text-grey-500 ml-2">Valued at $450-$750</span>
          </div>
          <p className="text-grey-600 mb-4 italic">"Everything you need to start fresh with confidence"</p>
          <p className="text-sm text-grey-500 mb-4">
            This tier covers the most important safety, security, and move-in essentials—giving homeowners peace of mind from day one.
          </p>
          <div className="p-3 rounded-lg bg-grey-50 text-sm text-grey-600">
            <strong>Agent at closing:</strong> "This takes care of the things every homeowner hopes were done—but never really knows."
          </div>
        </div>

        {/* Complete */}
        <div className="border-2 rounded-xl p-6 relative" style={{ borderColor: colors.teal }}>
          <div className="absolute -top-3 left-4 px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: colors.teal }}>
            RECOMMENDED
          </div>
          <div className="flex items-center justify-between mb-4 mt-2">
            <h4 className="text-xl font-bold text-grey-900">SmartStart Complete</h4>
            <span className="px-3 py-1 rounded-full text-sm bg-grey-100 text-grey-600">3 hours</span>
          </div>
          <div className="mb-4">
            <span className="text-3xl font-bold" style={{ color: colors.teal }}>$399</span>
            <span className="text-sm text-grey-500 ml-2">Valued at $850-$1,400+</span>
          </div>
          <p className="text-grey-600 mb-4 italic">"A full home reset—nothing left to question"</p>
          <p className="text-sm text-grey-500 mb-4">
            This tier provides reassurance that everything has been checked, cleaned, and optimized. Includes MyHome Health Snapshot Report.
          </p>
          <div className="p-3 rounded-lg text-sm text-grey-600" style={{ backgroundColor: `${colors.teal}10` }}>
            <strong>Agent at closing:</strong> "This gives you a clean slate—so you never have to wonder when something was last handled."
          </div>
        </div>
      </div>
    </div>

    {/* Services Breakdown */}
    <ExpandableSection title="Complete Services Breakdown" defaultExpanded={true}>
      <div className="space-y-6">
        {/* Safety & Security Reset */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-grey-50 border-b border-grey-200">
            <h4 className="font-semibold text-grey-900 flex items-center gap-2">
              <Lock className="w-5 h-5" style={{ color: colors.teal }} />
              Safety & Security Reset
            </h4>
            <p className="text-sm text-grey-500 mt-1">Homeowners don't have to wonder who still has access or whether safety items were overlooked before they moved in.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Replace all exterior door locks', essential: true, complete: true, provides: 'Door Locks', why: 'A fresh start begins with knowing you\'re the only one with access.' },
                  { service: 'Rekey garage keypad', essential: true, complete: true, provides: null, why: 'Resetting codes ensures every entry point reflects a true new beginning.' },
                  { service: 'Replace smoke & CO detectors or batteries', essential: true, complete: true, provides: 'Batteries', why: 'Safety shouldn\'t come with guesswork.' },
                  { service: 'Fire extinguisher in kitchen', essential: true, complete: true, provides: 'Fire Extinguisher', why: 'Knowing protection is in place allows homeowners to relax.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3">{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Home Systems Orientation */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-grey-50 border-b border-grey-200">
            <h4 className="font-semibold text-grey-900 flex items-center gap-2">
              <Home className="w-5 h-5" style={{ color: colors.teal }} />
              Home Systems Orientation
            </h4>
            <p className="text-sm text-grey-500 mt-1">Knowing how the home works removes anxiety and builds confidence immediately.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Locate emergency shutoffs (water, gas, etc.)', essential: true, complete: true, provides: null, why: 'Peace of mind comes from knowing what to do in a stressful moment.' },
                  { service: 'Ensure all breakers are labeled on electrical panel', essential: true, complete: true, provides: null, why: 'Clear labeling eliminates confusion when something needs attention.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3">{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Hidden Risk Removal */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-grey-50 border-b border-grey-200">
            <h4 className="font-semibold text-grey-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" style={{ color: colors.teal }} />
              Hidden Risk Removal
            </h4>
            <p className="text-sm text-grey-500 mt-1">These are commonly neglected items that impact safety, air quality, and appliance performance.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Clean dryer vent & lint trap housing', essential: true, complete: true, provides: null, why: 'Removes one of the most common hidden risks in a home.' },
                  { service: 'Replace HVAC filters', essential: true, complete: true, provides: 'HVAC Filter', why: 'Starting fresh with clean filters ensures healthier air.' },
                  { service: 'Replace fridge water filter', essential: true, complete: true, provides: 'Fridge Filter', why: 'Clean water and ice should be a given, not a question.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3">{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Full Interior Refresh - Complete Only */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-grey-200" style={{ backgroundColor: `${colors.teal}10` }}>
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-grey-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5" style={{ color: colors.teal }} />
                Full Interior Refresh
              </h4>
              <span className="text-xs font-semibold px-2 py-1 rounded-full text-white" style={{ backgroundColor: colors.teal }}>COMPLETE ONLY</span>
            </div>
            <p className="text-sm text-grey-500 mt-1">Removes uncertainty around moisture, ventilation, and hidden plumbing issues.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Clean & inspect all exhaust fans (bathroom, kitchen range)', essential: false, complete: true, provides: null, why: 'Proper ventilation keeps the home feeling clean and prevents moisture issues.' },
                  { service: 'Inspect caulking around tubs, sinks, toilets; replace if needed', essential: false, complete: true, provides: 'Caulking', why: 'Fresh caulking removes the worry of hidden moisture damage.' },
                  { service: 'Inspect & clean all under-sink plumbing, add water sensors optional', essential: false, complete: true, provides: null, why: 'Early alerts prevent surprises later.' },
                  { service: 'Swap toilet seats', essential: false, complete: true, provides: 'Toilet Seats', why: 'A small but meaningful way to make the home feel truly theirs.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3 text-grey-400">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3" style={{ color: colors.teal }}>{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mechanical & Water Protection - Complete Only */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-grey-200" style={{ backgroundColor: `${colors.teal}10` }}>
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-grey-900 flex items-center gap-2">
                <Thermometer className="w-5 h-5" style={{ color: colors.teal }} />
                Mechanical & Water Protection
              </h4>
              <span className="text-xs font-semibold px-2 py-1 rounded-full text-white" style={{ backgroundColor: colors.teal }}>COMPLETE ONLY</span>
            </div>
            <p className="text-sm text-grey-500 mt-1">Confirms critical systems are functioning safely and reliably.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Flush water heater & check temperature (set to 120°F)', essential: false, complete: true, provides: null, why: 'Resets the system to operate safely and efficiently.' },
                  { service: 'Check windows & door seals, replace weather stripping if needed', essential: false, complete: true, provides: null, why: 'Ensures comfort, energy efficiency, and confidence.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3 text-grey-400">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3" style={{ color: colors.teal }}>{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Appliance Deep Reset - Complete Only */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-grey-200" style={{ backgroundColor: `${colors.teal}10` }}>
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-grey-900 flex items-center gap-2">
                <Wrench className="w-5 h-5" style={{ color: colors.teal }} />
                Appliance Deep Reset
              </h4>
              <span className="text-xs font-semibold px-2 py-1 rounded-full text-white" style={{ backgroundColor: colors.teal }}>COMPLETE ONLY</span>
            </div>
            <p className="text-sm text-grey-500 mt-1">Establishes a known maintenance baseline so appliances perform better and last longer.</p>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Service</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                  <th className="text-left py-2 font-medium text-grey-700">Client Provides</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { service: 'Deep clean appliances (fridge coils, dishwasher filter, washing machine filter)', essential: false, complete: true, provides: null, why: 'Resets major appliances to a known starting point for better performance and longevity.' },
                ].map((item, idx) => (
                  <tr key={idx} className="border-b border-grey-50 hover:bg-grey-50">
                    <td className="py-3">
                      <div className="font-medium text-grey-900">{item.service}</div>
                      <div className="text-xs text-grey-500 mt-1">{item.why}</div>
                    </td>
                    <td className="text-center py-3 text-grey-400">{item.essential ? '✓' : '—'}</td>
                    <td className="text-center py-3" style={{ color: colors.teal }}>{item.complete ? '✓' : '—'}</td>
                    <td className="py-3 text-grey-600">{item.provides || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Deliverables */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-grey-50 border-b border-grey-200">
            <h4 className="font-semibold text-grey-900 flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: colors.teal }} />
              Deliverables
            </h4>
          </div>
          <div className="p-4">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-grey-100">
                  <th className="text-left py-2 font-medium text-grey-700">Deliverable</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Essential</th>
                  <th className="text-center py-2 font-medium text-grey-700 w-20">Complete</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-grey-50">
                  <td className="py-3 font-medium text-grey-900">Digital confirmation of all completed items & notes on any follow-up items</td>
                  <td className="text-center py-3">✓</td>
                  <td className="text-center py-3">✓</td>
                </tr>
                <tr className="border-b border-grey-50">
                  <td className="py-3">
                    <div className="font-medium text-grey-900">MyHome Health Snapshot Report</div>
                    <div className="text-xs text-grey-500 mt-1">Documented condition of key systems, next-step maintenance priorities, & optional path to Hudson membership</div>
                  </td>
                  <td className="text-center py-3 text-grey-400">—</td>
                  <td className="text-center py-3" style={{ color: colors.teal }}>✓</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Optional Add-ons */}
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div className="p-4 bg-amber-50 border-b border-amber-200">
            <h4 className="font-semibold text-grey-900">Optional Add-ons (Client Paid)</h4>
          </div>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <Droplets className="w-5 h-5 mt-0.5" style={{ color: colors.teal }} />
              <div>
                <div className="font-medium text-grey-900">Clean Gutters (Spring/Fall)</div>
                <div className="text-sm text-grey-500">Clear drainage ensures water flows where it should, so homeowners don't have to wonder what's happening every time it rains.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Social Media Content */}
    <ExpandableSection title="SmartStart Social Media Content" defaultExpanded={false}>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <SocialPostCard
          platform="Instagram"
          caption="The perfect closing gift doesn't exist... until now. SmartStart gives your buyers the fresh start they deserve—with every safety detail handled before they unpack a single box."
          hashtags={['#RealEstateAgents', '#ClosingGift', '#SmartStart', '#HudsonMyHome']}
          design={{
            id: 'smartstart-ig-1',
            layout: 'overlay',
            image: brandAssets.photos.stock1,
            logo: brandAssets.logos.primaryWhite,
            headline: 'The Closing Gift They\'ll Never Forget',
            subhead: 'SmartStart: A fresh start for new homeowners',
            cta: 'Partner With Us',
            bgColor: colors.tealDark,
            accentColor: colors.teal,
            textColor: 'white',
            price: 'From $299',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Instagram"
          caption="New keys. New locks. New beginning. SmartStart ensures your buyers are the only ones with access to their new home. The perfect closing gift that shows you care beyond the transaction."
          hashtags={['#NewHome', '#SafetyFirst', '#SmartStart', '#HudsonMyHome', '#RealEstate']}
          design={{
            id: 'smartstart-ig-2',
            layout: 'split',
            image: brandAssets.photos.stock4,
            logo: brandAssets.logos.primary,
            headline: 'New Home. Fresh Start.',
            subhead: 'Safety, security, and peace of mind from day one.',
            cta: 'Learn More',
            bgColor: 'white',
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="Real estate agents: Want to be remembered long after closing day? SmartStart is the closing gift that keeps giving—safety checks, security resets, and a home that's truly move-in ready. Your clients will thank you (and refer you) for years."
          hashtags={['#RealEstateAgent', '#ClientCare', '#SmartStart', '#HudsonMyHome']}
          design={{
            id: 'smartstart-fb-1',
            layout: 'minimal',
            image: brandAssets.photos.stock8,
            logo: brandAssets.logos.primaryWhite,
            headline: 'Be the Agent They Remember',
            subhead: 'SmartStart: The closing gift that shows you truly care',
            cta: 'Become a Partner',
            bgColor: colors.teal,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="LinkedIn"
          caption="Attention real estate professionals: Stop giving forgettable closing gifts. SmartStart ensures every new home is safe, secure, and ready—while positioning you as an advocate, not just a salesperson. The ROI? Referrals for years to come."
          hashtags={['#RealEstate', '#ClosingGifts', '#SmartStart', '#HudsonMyHome', '#AgentLife']}
          design={{
            id: 'smartstart-li-1',
            layout: 'feature',
            image: brandAssets.photos.stock12,
            logo: brandAssets.logos.primary,
            headline: 'Elevate Your Closing Experience',
            subhead: 'SmartStart: Professional home reset service',
            cta: 'Partner With Hudson',
            bgColor: colors.cream,
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="Instagram"
          caption={`"This takes care of the things every homeowner hopes were done—but never really knows." That's what you get to tell your clients when you gift them SmartStart. A fresh beginning, handled.`}
          hashtags={['#NewHomeowner', '#PeaceOfMind', '#SmartStart', '#HudsonMyHome']}
          design={{
            id: 'smartstart-ig-3',
            layout: 'card',
            image: brandAssets.photos.blueprint,
            logo: brandAssets.logos.primaryWhite,
            headline: 'Peace of Mind, Gifted',
            subhead: 'Every detail handled. Every system checked.',
            cta: 'See the Services',
            bgColor: colors.nearBlack,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="First-time homebuyers have enough to worry about. Give them one less thing with SmartStart—our professional home reset service that handles safety, security, and hidden risks before they even move in. Starting at just $299."
          hashtags={['#FirstTimeHomeBuyer', '#NewHome', '#SmartStart', '#HudsonMyHome']}
          design={{
            id: 'smartstart-fb-2',
            layout: 'split',
            image: brandAssets.photos.stock16,
            logo: brandAssets.logos.primary,
            headline: 'The Fresh Start They Deserve',
            subhead: 'SmartStart: Professional home reset service',
            cta: 'Gift SmartStart',
            bgColor: 'white',
            accentColor: colors.teal,
            textColor: colors.nearBlack,
            price: 'From $299',
          }}
          dimensions="1200x628"
        />
      </div>
    </ExpandableSection>

    {/* Email Templates */}
    <ExpandableSection title="SmartStart Email Templates" defaultExpanded={false}>
      <div className="space-y-6">
        <div className="border border-grey-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-grey-900">Agent Introduction Email</h4>
            <CopyButton text={`Subject: Give Your Clients the Fresh Start They Deserve

Hi [Agent Name],

I wanted to introduce you to a new service that's changing how agents approach closing gifts.

MyHome SmartStart is a professional home reset service designed specifically for real estate agents to gift their clients. In just 2-3 hours, we ensure every new home is:

✓ Secure - All locks replaced, garage codes reset
✓ Safe - Smoke detectors, fire extinguishers verified
✓ Optimized - HVAC filters, dryer vents, and more handled

Two packages available:
• Essential ($299): Core safety & security reset
• Complete ($399): Full home reset with detailed health report

Why agents love it:
• Thoughtful, practical, and memorable
• Protects clients after closing
• Positions you as an advocate, not just a salesperson
• Generates referrals for years

Interested in learning more? I'd love to show you how SmartStart can elevate your client experience.

Best,
[Your Name]
Hudson My Home
info@hudsonmyhome.com`} />
          </div>
          <div className="bg-grey-50 rounded-lg p-4 text-sm text-grey-700 font-mono whitespace-pre-wrap">
{`Subject: Give Your Clients the Fresh Start They Deserve

Hi [Agent Name],

I wanted to introduce you to a new service that's changing how agents approach closing gifts.

MyHome SmartStart is a professional home reset service designed specifically for real estate agents to gift their clients. In just 2-3 hours, we ensure every new home is:

✓ Secure - All locks replaced, garage codes reset
✓ Safe - Smoke detectors, fire extinguishers verified
✓ Optimized - HVAC filters, dryer vents, and more handled

Two packages available:
• Essential ($299): Core safety & security reset
• Complete ($399): Full home reset with detailed health report

Why agents love it:
• Thoughtful, practical, and memorable
• Protects clients after closing
• Positions you as an advocate, not just a salesperson
• Generates referrals for years

Interested in learning more? I'd love to show you how SmartStart can elevate your client experience.

Best,
[Your Name]
Hudson My Home
info@hudsonmyhome.com`}
          </div>
        </div>

        <div className="border border-grey-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-grey-900">Client Gift Announcement (For Agent to Send)</h4>
            <CopyButton text={`Subject: A Special Gift for Your New Home 🏠

Congratulations on your new home!

As a thank you for trusting me with this important milestone, I've arranged something special for you.

You'll be receiving a MyHome SmartStart service—a professional home reset that ensures your new home is safe, secure, and ready for your fresh beginning.

What's included:
• All exterior locks replaced (you provide new locks)
• Garage keypad recoded
• Smoke & CO detectors checked/replaced
• Emergency shutoffs located and labeled
• Dryer vent cleaned (a top fire hazard!)
• HVAC and fridge filters replaced
• And more...

A SmartStart technician will reach out within the next few days to schedule your visit at a time that works for you.

Because you deserve to settle in knowing every detail has been handled.

Welcome home!

[Agent Name]`} />
          </div>
          <div className="bg-grey-50 rounded-lg p-4 text-sm text-grey-700 font-mono whitespace-pre-wrap">
{`Subject: A Special Gift for Your New Home 🏠

Congratulations on your new home!

As a thank you for trusting me with this important milestone, I've arranged something special for you.

You'll be receiving a MyHome SmartStart service—a professional home reset that ensures your new home is safe, secure, and ready for your fresh beginning.

What's included:
• All exterior locks replaced (you provide new locks)
• Garage keypad recoded
• Smoke & CO detectors checked/replaced
• Emergency shutoffs located and labeled
• Dryer vent cleaned (a top fire hazard!)
• HVAC and fridge filters replaced
• And more...

A SmartStart technician will reach out within the next few days to schedule your visit at a time that works for you.

Because you deserve to settle in knowing every detail has been handled.

Welcome home!

[Agent Name]`}
          </div>
        </div>
      </div>
    </ExpandableSection>

    {/* Quick Stats */}
    <div className="bg-white rounded-xl p-8 border border-grey-200">
      <h3 className="text-xl font-semibold text-grey-900 mb-6">Quick Reference Stats</h3>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
          <div className="text-3xl font-bold" style={{ color: colors.teal }}>$299</div>
          <div className="text-sm text-grey-600">Essential Package</div>
        </div>
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
          <div className="text-3xl font-bold" style={{ color: colors.teal }}>$399</div>
          <div className="text-sm text-grey-600">Complete Package</div>
        </div>
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
          <div className="text-3xl font-bold" style={{ color: colors.teal }}>2-3</div>
          <div className="text-sm text-grey-600">Hours On-Site</div>
        </div>
        <div className="text-center p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
          <div className="text-3xl font-bold" style={{ color: colors.teal }}>$1,400+</div>
          <div className="text-sm text-grey-600">Complete Value</div>
        </div>
      </div>
    </div>
  </div>
)

// ============================================
// SOCIAL MEDIA TAB
// ============================================
const SocialMediaTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-2">Social Media Content Library</h2>
      <p className="text-grey-500 mb-6">Ready-to-post content for all platforms. Download images and copy captions.</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        <SocialPostCard
          platform="Instagram"
          caption="Your home should feel like a five-star hotel. Not a never-ending to-do list. That's why we created Hudson My Home—white-glove home management for those who value their time above all else."
          hashtags={['#HudsonMyHome', '#LuxuryLiving', '#HomeManagement', '#WhiteGloveService', '#TimeIsMoney']}
          design={{
            id: 'membership-ig-1',
            layout: 'overlay',
            image: brandAssets.photos.stock1,
            logo: brandAssets.logos.primaryWhite,
            headline: 'We maintain your home so you don\'t have to.',
            subhead: 'White-glove home management',
            cta: 'Discover Home Membership',
            bgColor: colors.tealDark,
            accentColor: colors.teal,
            textColor: 'white',
            price: '$399/mo',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Instagram"
          caption="Monthly visits. Proactive care. Complete peace of mind. Your dedicated Hudson knows your home better than you do—and handles everything before it becomes a problem."
          hashtags={['#ProactiveHomeCare', '#HomeMaintenance', '#LuxuryHomeowner', '#HudsonMyHome']}
          design={{
            id: 'membership-ig-2',
            layout: 'split',
            image: brandAssets.photos.stock4,
            logo: brandAssets.logos.primary,
            headline: 'Your dedicated Hudson professional.',
            subhead: 'Monthly visits. Proactive care. Complete peace of mind.',
            cta: 'Learn More',
            bgColor: 'white',
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="What would you do with 10 extra hours every month? Most homeowners spend that much time on maintenance, repairs, and coordinating vendors. Hudson My Home gives you that time back. Our dedicated professionals handle your home's 5-year plan so you can focus on what matters."
          hashtags={['#TimeBack', '#HomeManagement', '#HudsonMyHome']}
          design={{
            id: 'membership-fb-1',
            layout: 'minimal',
            image: brandAssets.photos.stock8,
            logo: brandAssets.logos.primaryWhite,
            headline: 'What would you do with 10 extra hours?',
            subhead: 'Most homeowners spend that much on home maintenance each month.',
            cta: 'Reclaim Your Time',
            bgColor: colors.teal,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="LinkedIn"
          caption="As professionals, we optimize everything in our work. Why not our homes? Hudson My Home applies the same systematic approach to home management that successful people use in business: dedicated resources, proactive maintenance, complete documentation, and measurable outcomes. For those who value their time."
          hashtags={['#TimeManagement', '#HudsonMyHome', '#HomeManagement', '#WhiteGloveService']}
          design={{
            id: 'membership-li-1',
            layout: 'feature',
            image: brandAssets.photos.stock12,
            logo: brandAssets.logos.primary,
            headline: 'For those who value their time.',
            subhead: 'Systematic home management for busy homeowners.',
            cta: 'Apply Now',
            bgColor: colors.cream,
            accentColor: colors.teal,
            textColor: colors.nearBlack,
          }}
          dimensions="1200x628"
        />
        <SocialPostCard
          platform="Instagram"
          caption="From furnace filters to full remodels—we manage it all. Your Hudson professional creates a personalized 5-year maintenance plan for your home, then executes it with precision. Nothing falls through the cracks."
          hashtags={['#HomePlan', '#MaintenanceSchedule', '#HudsonMyHome', '#NoWorries']}
          design={{
            id: 'membership-ig-3',
            layout: 'card',
            image: brandAssets.photos.blueprint,
            logo: brandAssets.logos.primaryWhite,
            headline: 'Your 5-Year Home Plan',
            subhead: 'From furnace filters to full remodels.',
            cta: 'See How It Works',
            bgColor: colors.nearBlack,
            accentColor: colors.teal,
            textColor: 'white',
          }}
          dimensions="1080x1080"
        />
        <SocialPostCard
          platform="Facebook"
          caption="Real estate agents: Stop getting calls about leaky faucets 6 months after closing. Hudson My Home becomes your buyers' go-to resource for everything home-related. The closing gift they'll actually use—and remember you for."
          hashtags={['#RealEstateAgents', '#ClosingGifts', '#HudsonMyHome', '#ClientRetention']}
          design={{
            id: 'agents-fb-1',
            layout: 'split',
            image: brandAssets.photos.stock16,
            logo: brandAssets.logos.primary,
            headline: 'The closing gift they actually use.',
            subhead: 'Stop the post-closing calls. Start the referrals.',
            cta: 'Partner With Us',
            bgColor: 'white',
            accentColor: colors.teal,
            textColor: colors.nearBlack,
            price: '$599/year',
          }}
          dimensions="1200x628"
        />
      </div>
    </div>

    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h3 className="text-lg font-medium text-grey-900 mb-4">Content Calendar Template</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-grey-200">
              <th className="text-left p-3 font-medium text-grey-900">Day</th>
              <th className="text-left p-3 font-medium text-grey-900">Platform</th>
              <th className="text-left p-3 font-medium text-grey-900">Content Type</th>
              <th className="text-left p-3 font-medium text-grey-900">Theme</th>
            </tr>
          </thead>
          <tbody>
            {[
              { day: 'Monday', platform: 'Instagram', type: 'Carousel', theme: 'Educational - Home Tips' },
              { day: 'Tuesday', platform: 'LinkedIn', type: 'Text Post', theme: 'Thought Leadership' },
              { day: 'Wednesday', platform: 'Instagram', type: 'Reel', theme: 'Behind the Scenes' },
              { day: 'Thursday', platform: 'Facebook', type: 'Image Post', theme: 'Testimonial' },
              { day: 'Friday', platform: 'All', type: 'Story', theme: 'Weekend Home Tips' },
            ].map((row, idx) => (
              <tr key={idx} className="border-b border-grey-100">
                <td className="p-3 font-medium text-grey-900">{row.day}</td>
                <td className="p-3 text-grey-600">{row.platform}</td>
                <td className="p-3 text-grey-600">{row.type}</td>
                <td className="p-3 text-grey-600">{row.theme}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* 30-Day Launch Campaign */}
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h3 className="text-2xl font-serif text-grey-900 mb-2">30-Day Launch Campaign</h3>
      <p className="text-grey-500 mb-6">Daily social content to build excitement for Hudson My Home's launch. Copy captions and download graphics.</p>
      
      <div className="space-y-4">
        {[
          { day: 1, platform: 'Instagram', theme: 'Teaser', caption: "Something is coming to Des Moines. For homeowners who believe their time is worth protecting. Stay tuned. 👀", hashtags: ['#ComingSoon', '#DesMoines', '#HomeManagement'] },
          { day: 2, platform: 'LinkedIn', theme: 'Thought Leadership', caption: "What if your home ran like a well-oiled machine—without you lifting a finger? We've spent years perfecting the answer. Announcement coming soon.", hashtags: ['#Innovation', '#HomeOwnership', '#ComingSoon'] },
          { day: 3, platform: 'Instagram', theme: 'Problem Awareness', caption: "The average homeowner spends 10+ hours a month on home maintenance tasks. Scheduling. Coordinating. Worrying. What if there was another way?", hashtags: ['#HomeownerLife', '#TimeManagement', '#BetterWay'] },
          { day: 4, platform: 'Facebook', theme: 'Lifestyle', caption: "Your home should be your sanctuary—not your second job. We're building something that will change what it means to own a home in Iowa.", hashtags: ['#HomeLife', '#Iowa', '#ComingSoon'] },
          { day: 5, platform: 'Instagram', theme: 'Teaser', caption: "White-glove service. Proactive care. Complete peace of mind. Three days until we reveal everything.", hashtags: ['#WhiteGlove', '#LuxuryLiving', '#Countdown'] },
          { day: 6, platform: 'LinkedIn', theme: 'Founder Story', caption: "After years in property management, we saw the same thing over and over: homeowners overwhelmed by the endless demands of their homes. We knew there had to be a better way. Tomorrow, we share our solution.", hashtags: ['#FounderStory', '#Entrepreneurship', '#HomeCare'] },
          { day: 7, platform: 'All', theme: 'LAUNCH DAY', caption: "🏠 Introducing Hudson My Home™ — We maintain your home so you don't have to. White-glove home management for discerning homeowners who value their time. Your dedicated Hudson professional has it handled—so you can be informed, not involved. Now accepting founding members. Link in bio.", hashtags: ['#HudsonMyHome', '#LaunchDay', '#WhiteGloveService', '#DesMoines', '#LuxuryLiving'] },
          { day: 8, platform: 'Instagram', theme: 'What We Do', caption: "Monthly visits. Proactive maintenance. A personalized 5-year plan for your home. Your dedicated Hudson professional knows your home better than you do—and handles everything before it becomes a problem.", hashtags: ['#HudsonMyHome', '#ProactiveCare', '#HomeMaintenance'] },
          { day: 9, platform: 'Facebook', theme: 'Time Back', caption: "What would you do with 10 extra hours every month? Golf more? Focus on family? Actually relax on weekends? Hudson My Home gives you that time back.", hashtags: ['#TimeBack', '#WorkLifeBalance', '#HudsonMyHome'] },
          { day: 10, platform: 'LinkedIn', theme: 'Business Case', caption: "Smart business owners invest in systems that save time. Why should your home be any different? Hudson My Home brings the same systematic approach to home management that successful people use in business.", hashtags: ['#Efficiency', '#SmartInvesting', '#HudsonMyHome'] },
          { day: 11, platform: 'Instagram', theme: 'Behind the Scenes', caption: "Meet the team behind your peace of mind. Our Hudson professionals are trained, vetted, and dedicated to one thing: making sure you never have to think about home maintenance again.", hashtags: ['#MeetTheTeam', '#BehindTheScenes', '#HudsonMyHome'] },
          { day: 12, platform: 'Facebook', theme: 'Pain Point', caption: "Ever had that sinking feeling when you realize you forgot to change the furnace filter... again? With Hudson My Home, you'll never have that feeling. We handle it all.", hashtags: ['#HomeMaintenance', '#PeaceOfMind', '#HudsonMyHome'] },
          { day: 13, platform: 'Instagram', theme: 'Lifestyle', caption: "Imagine walking into your home knowing every detail has been handled. The filters changed. The gutters cleaned. The systems inspected. That's the Hudson experience.", hashtags: ['#LuxuryLiving', '#HomeLife', '#HudsonMyHome'] },
          { day: 14, platform: 'LinkedIn', theme: 'Value Prop', caption: "Our members don't just save time. They protect their largest investment. Proactive maintenance catches small issues before they become expensive problems.", hashtags: ['#HomeValue', '#SmartOwnership', '#HudsonMyHome'] },
          { day: 15, platform: 'All', theme: 'Testimonial', caption: "\"I used to spend every Saturday dealing with house stuff. Now I spend it with my kids. Hudson My Home changed everything for us.\" — Sarah M., West Des Moines", hashtags: ['#Testimonial', '#RealResults', '#HudsonMyHome'] },
          { day: 16, platform: 'Instagram', theme: 'Service Spotlight', caption: "Your 5-Year Home Plan: We don't just react to problems—we prevent them. Your Hudson professional creates a comprehensive maintenance roadmap tailored to your home.", hashtags: ['#HomePlan', '#ProactiveCare', '#HudsonMyHome'] },
          { day: 17, platform: 'Facebook', theme: 'Community', caption: "Proud to be Iowa-born and Iowa-focused. Hudson My Home was built right here in Des Moines for homeowners who expect more.", hashtags: ['#IowaProud', '#LocalBusiness', '#DesMoines', '#HudsonMyHome'] },
          { day: 18, platform: 'Instagram', theme: 'Comparison', caption: "Traditional home ownership: Endless to-do lists, missed maintenance, weekend projects. Hudson membership: Everything handled, nothing missed, weekends reclaimed.", hashtags: ['#Comparison', '#BetterWay', '#HudsonMyHome'] },
          { day: 19, platform: 'LinkedIn', theme: 'ROI', caption: "The true cost of deferred maintenance? Studies show homeowners who skip regular upkeep pay 3x more in emergency repairs. Hudson My Home keeps you ahead of the curve.", hashtags: ['#SmartInvesting', '#HomeValue', '#HudsonMyHome'] },
          { day: 20, platform: 'Instagram', theme: 'FAQ', caption: "\"What exactly does Hudson handle?\" Everything from furnace filters to full remodels. Seasonal prep, vendor coordination, emergency response. If it's about your home, we've got it.", hashtags: ['#FAQ', '#FullService', '#HudsonMyHome'] },
          { day: 21, platform: 'Facebook', theme: 'Lifestyle', caption: "Sunday morning should be for coffee and the paper—not for making lists of house repairs. Experience what stress-free homeownership actually feels like.", hashtags: ['#SundayMorning', '#StressFree', '#HudsonMyHome'] },
          { day: 22, platform: 'Instagram', theme: 'Trust', caption: "Every Hudson professional is background-checked, trained, and committed to excellence. Your home is in expert hands.", hashtags: ['#Trust', '#Excellence', '#HudsonMyHome'] },
          { day: 23, platform: 'LinkedIn', theme: 'Partner Announcement', caption: "Attention real estate agents: Want to stand out from every other agent in your market? Our partner program lets you gift clients something they'll actually use—and remember you for.", hashtags: ['#RealEstateAgents', '#PartnerProgram', '#HudsonMyHome'] },
          { day: 24, platform: 'Instagram', theme: 'Seasonal', caption: "Winter prep checklist? Already done. Spring maintenance? Scheduled. Fall cleanup? Handled. With Hudson, every season is stress-free.", hashtags: ['#SeasonalCare', '#YearRound', '#HudsonMyHome'] },
          { day: 25, platform: 'Facebook', theme: 'Testimonial', caption: "\"We found a small leak during a routine visit that would have caused thousands in damage if left unchecked. Hudson My Home literally paid for itself.\" — Mark T., Urbandale", hashtags: ['#Testimonial', '#ValueProof', '#HudsonMyHome'] },
          { day: 26, platform: 'Instagram', theme: 'MyHome Refresh', caption: "Not ready for full membership? Start with the MyHome Refresh™—a one-time visit that gives you complete clarity about your home's condition. Starting at $299.", hashtags: ['#MyHomeRefresh', '#StartHere', '#HudsonMyHome'] },
          { day: 27, platform: 'LinkedIn', theme: 'Builder Partnership', caption: "Luxury home builders: What if your buyer experience didn't end at closing? Hudson My Home handles first-year warranty coordination so you can focus on what you do best—building.", hashtags: ['#Builders', '#Partnership', '#HudsonMyHome'] },
          { day: 28, platform: 'Instagram', theme: 'Urgency', caption: "Founding member spots are limited. Lock in your rate before it's gone. Your home—and your weekends—will thank you.", hashtags: ['#FoundingMember', '#LimitedSpots', '#HudsonMyHome'] },
          { day: 29, platform: 'Facebook', theme: 'Vision', caption: "We believe everyone deserves to truly live in their home—not just maintain it. That's the Hudson My Home promise.", hashtags: ['#OurVision', '#LiveYourLife', '#HudsonMyHome'] },
          { day: 30, platform: 'All', theme: 'Milestone', caption: "30 days in, and we're just getting started. Thank you to everyone who has joined the Hudson family. The future of homeownership is here—and it looks like freedom.", hashtags: ['#ThankYou', '#Milestone', '#HudsonMyHome', '#FreedomFromMaintenance'] },
        ].map((post, idx) => (
          <div key={idx} className="border border-grey-200 rounded-lg p-4 hover:border-grey-300 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white" style={{ backgroundColor: colors.teal }}>
                  {post.day}
                </span>
                <div>
                  <span className="font-medium text-grey-900">Day {post.day}</span>
                  <div className="flex items-center gap-2 text-sm text-grey-500">
                    <span>{post.platform}</span>
                    <span>•</span>
                    <span>{post.theme}</span>
                  </div>
                </div>
              </div>
              <CopyButton text={`${post.caption}\n\n${post.hashtags.join(' ')}`} />
            </div>
            <p className="text-grey-700 text-sm leading-relaxed mb-2">{post.caption}</p>
            <p className="text-xs text-grey-400">{post.hashtags.join(' ')}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
)

// ============================================
// GOOGLE ADS TAB
// ============================================
const GoogleAdsTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-2">Google Ads Campaign Assets</h2>
      <p className="text-grey-500 mb-6">Search, Display, and YouTube ad copy ready for deployment.</p>

      <ExpandableSection title="Search Ads - Membership" icon={<Search className="w-5 h-5" style={{ color: colors.teal }} />} defaultOpen>
        <div className="grid md:grid-cols-2 gap-4">
          <AdCard
            type="Responsive Search Ad"
            headline="White-Glove Home Management"
            description="Your dedicated professional handles everything. Monthly visits, proactive care, complete peace of mind. $399/mo."
            cta="Discover Home Membership"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="Stop Managing Your Home"
            description="Busy homeowners: Reclaim 10+ hours/month. Hudson My Home handles your home's 5-year plan. Apply today."
            cta="Get Started"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="Home Management for Executives"
            description="Too busy for home maintenance? Your dedicated Hudson visits monthly, handles vendors, and prevents costly repairs."
            cta="Learn More"
          />
          <AdCard
            type="Responsive Search Ad"
            headline="Live Like a Guest in Your Own Home"
            description="Luxury home care with white-glove service. Monthly visits, 24/7 support, complete documentation. $399/mo."
            cta="Apply Now"
          />
        </div>
      </ExpandableSection>

      <ExpandableSection title="Display Ads - Membership" icon={<Image className="w-5 h-5" style={{ color: colors.teal }} />}>
        <div className="grid md:grid-cols-3 gap-4">
          <DesignedDisplayAd 
            size="300x250" 
            name="Medium Rectangle" 
            design={{
              headline: 'White-Glove Home Care',
              subhead: 'Your dedicated Hudson professional',
              cta: 'Learn More',
              bgColor: colors.teal,
              accentColor: colors.teal,
              image: brandAssets.photos.stock1,
            }}
          />
          <DesignedDisplayAd 
            size="728x90" 
            name="Leaderboard" 
            design={{
              headline: 'We maintain your home so you don\'t have to.',
              subhead: '$399/mo',
              cta: 'Apply',
              bgColor: colors.tealDark,
              accentColor: colors.teal,
            }}
          />
          <DesignedDisplayAd 
            size="160x600" 
            name="Wide Skyscraper" 
            design={{
              headline: 'Membership',
              subhead: 'Monthly visits. Complete peace of mind.',
              cta: 'Apply Now',
              bgColor: colors.teal,
              accentColor: colors.teal,
              image: brandAssets.photos.stock4,
            }}
          />
          <DesignedDisplayAd 
            size="320x50" 
            name="Mobile Banner" 
            design={{
              headline: 'Hudson Membership',
              subhead: '$399/mo',
              cta: 'Learn More',
              bgColor: colors.tealDark,
              accentColor: colors.teal,
            }}
          />
          <DesignedDisplayAd 
            size="300x600" 
            name="Half Page" 
            design={{
              headline: 'Your Home Deserves a Director',
              subhead: 'White-glove home management for those who value their time.',
              cta: 'Discover Home Membership',
              bgColor: colors.teal,
              accentColor: colors.teal,
              image: brandAssets.photos.stock8,
            }}
          />
          <DesignedDisplayAd 
            size="970x250" 
            name="Billboard" 
            design={{
              headline: 'We maintain your home so you don\'t have to.',
              subhead: 'Monthly visits. Proactive care. Complete peace of mind.',
              cta: 'Apply Now',
              bgColor: colors.tealDark,
              accentColor: colors.teal,
            }}
          />
        </div>
      </ExpandableSection>

      <ExpandableSection title="Display Ads - MyHome Refresh" icon={<Image className="w-5 h-5" style={{ color: colors.teal }} />}>
        <div className="grid md:grid-cols-3 gap-4">
          <DesignedDisplayAd 
            size="300x250" 
            name="Medium Rectangle" 
            design={{
              headline: 'The MyHome Refresh™',
              subhead: 'One visit. Complete clarity.',
              cta: 'Book Now',
              bgColor: colors.tealDark,
              accentColor: colors.teal,
              image: brandAssets.photos.stock2,
            }}
          />
          <DesignedDisplayAd 
            size="728x90" 
            name="Leaderboard" 
            design={{
              headline: 'MyHome Refresh™ — Starting at $299',
              subhead: 'No membership required',
              cta: 'Book',
              bgColor: '#7a5d47',
              accentColor: colors.teal,
            }}
          />
          <DesignedDisplayAd 
            size="320x50" 
            name="Mobile Banner" 
            design={{
              headline: 'MyHome Refresh™',
              subhead: '$299',
              cta: 'Book Now',
              bgColor: colors.tealDark,
              accentColor: colors.teal,
            }}
          />
        </div>
      </ExpandableSection>

      <ExpandableSection title="YouTube Ads" icon={<Video className="w-5 h-5" style={{ color: colors.teal }} />}>
        <div className="space-y-4">
          {[
            {
              type: '6-Second Bumper',
              script: '"Your home deserves a director. Hudson My Home. Apply today."',
              visual: 'Quick cuts of luxury home, Hudson professional, happy homeowner',
            },
            {
              type: '15-Second Non-Skippable',
              script: '"Tired of spending weekends on home maintenance? Hudson My Home handles your home\'s 5-year plan. Monthly visits. Proactive care. Complete peace of mind. Apply for membership today."',
              visual: 'Before/after comparison, Hudson visit montage, CTA screen',
            },
            {
              type: '30-Second Skippable',
              script: 'Full narrative: Problem → Solution → Benefits → Social proof → CTA',
              visual: 'Cinematic home footage, testimonial clips, service demonstration',
            },
          ].map((ad, idx) => (
            <div key={idx} className="p-4 bg-grey-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-grey-900">{ad.type}</h4>
                <DownloadButton label="Download Script" />
              </div>
              <div className="space-y-2">
                <div>
                  <span className="text-xs text-grey-500 uppercase">Script</span>
                  <p className="text-sm text-grey-700">{ad.script}</p>
                </div>
                <div>
                  <span className="text-xs text-grey-500 uppercase">Visual Direction</span>
                  <p className="text-sm text-grey-600">{ad.visual}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </ExpandableSection>

      <ExpandableSection title="Keywords & Targeting" icon={<Target className="w-5 h-5" style={{ color: colors.teal }} />}>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-grey-900 mb-4">High-Intent Keywords</h4>
            <div className="flex flex-wrap gap-2">
              {[
                'home management service',
                'luxury home maintenance',
                'home concierge service',
                'property management for homeowners',
                'home care subscription',
                'white glove home service',
                'home maintenance plan',
                'personal home manager',
              ].map((keyword, idx) => (
                <span key={idx} className="px-3 py-1 bg-grey-100 text-grey-700 text-sm rounded-full">{keyword}</span>
              ))}
            </div>
          </div>
          <div>
            <h4 className="font-medium text-grey-900 mb-4">Audience Targeting</h4>
            <ul className="space-y-2 text-sm text-grey-600">
              <li>• HHI $250K+ (Google affinity)</li>
              <li>• Homeowners (property data)</li>
              <li>• Luxury goods shoppers</li>
              <li>• Business owners / executives</li>
              <li>• Strategic Coach members (custom audience)</li>
              <li>• YPO / EO members (custom audience)</li>
            </ul>
          </div>
        </div>
      </ExpandableSection>
    </div>
  </div>
)

// ============================================
// EMAIL SIGNATURES TAB
// ============================================
const EmailSignaturesTab: React.FC = () => {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const copySignature = (html: string, id: string) => {
    const blob = new Blob([html], { type: 'text/html' })
    const item = new ClipboardItem({ 'text/html': blob })
    navigator.clipboard.write([item])
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const signatures = [
    {
      id: 'joe',
      role: 'Founder / CEO',
      name: 'Joe Schafbuch',
      title: 'Founder & CEO',
      phone: '(515) 216-4433',
      email: 'mitch@hudsonmyhome.com',
      helpItems: [
        'Schedule a discovery call',
        'Learn about our membership',
        'Discuss partnership opportunities',
        'Get a MyHome Refresh quote',
      ],
    },
    {
      id: 'cassi',
      role: 'COO',
      name: 'Cassi Luree',
      title: 'Director of Operations',
      phone: '(515) 216-4447',
      email: 'cassi@hudsonmyhome.com',
      helpItems: [
        'Coordinate service scheduling',
        'Discuss operational partnerships',
        'Address member concerns',
        'Explore franchise opportunities',
      ],
    },
    {
      id: 'bonnie',
      role: 'Marketing',
      name: 'Bonnie Le Page',
      title: 'Fractional CMO',
      phone: '(515) 216-4433',
      email: 'mitch@hudsonmyhome.com',
      helpItems: [
        'Discuss marketing partnerships',
        'Co-marketing opportunities',
        'Press and media inquiries',
        'Brand collaboration',
      ],
    },
    {
      id: 'sales',
      role: 'Sales',
      name: '[Sales Rep Name]',
      title: 'Home Care Advisor',
      phone: '(515) 216-4433',
      email: 'info@hudsonmyhome.com',
      helpItems: [
        'Schedule your MyHome Refresh',
        'Learn about membership benefits',
        'Get a custom home care plan',
        'Answer any questions',
      ],
    },
    {
      id: 'partners',
      role: 'Partner Relations',
      name: '[Partner Rep Name]',
      title: 'Partner Success Manager',
      phone: '(515) 216-4433',
      email: 'mitch@hudsonmyhome.com',
      helpItems: [
        'Join our agent partner program',
        'Builder partnership inquiries',
        'Closing gift program details',
        'Co-marketing materials',
      ],
    },
  ]

  const generateSignatureHTML = (sig: typeof signatures[0]) => `
<table cellpadding="0" cellspacing="0" style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #333; max-width: 500px;">
  <tr>
    <td style="padding-right: 20px; vertical-align: top;">
      <img src="${brandAssets.logos.primary}" alt="Hudson My Home" width="100" style="display: block;" />
    </td>
    <td style="padding-left: 20px; border-left: 3px solid ${colors.teal};">
      <p style="margin: 0 0 2px 0; font-size: 16px; font-weight: 600; color: ${colors.teal};">${sig.name}</p>
      <p style="margin: 0 0 12px 0; font-size: 13px; color: #666;">${sig.title}</p>
      <p style="margin: 0 0 2px 0; font-size: 13px;"><a href="tel:${sig.phone.replace(/[^0-9]/g, '')}" style="color: #333; text-decoration: none;">${sig.phone}</a></p>
      <p style="margin: 0 0 2px 0; font-size: 13px;"><a href="mailto:${sig.email}" style="color: ${colors.teal}; text-decoration: none;">${sig.email}</a></p>
      <p style="margin: 0; font-size: 13px;"><a href="https://hudsonmyhome.app" style="color: ${colors.teal}; text-decoration: none;">hudsonmyhome.app</a></p>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding-top: 20px;">
      <table cellpadding="0" cellspacing="0" style="background: ${colors.cream}; border-radius: 8px; width: 100%;">
        <tr>
          <td style="padding: 16px;">
            <p style="margin: 0 0 10px 0; font-size: 13px; font-weight: 600; color: ${colors.teal};">Here's how I can help:</p>
            <table cellpadding="0" cellspacing="0" style="font-size: 12px; color: #555;">
              ${sig.helpItems.map(item => `
              <tr>
                <td style="padding: 2px 8px 2px 0; vertical-align: top; color: ${colors.teal};">→</td>
                <td style="padding: 2px 0;">${item}</td>
              </tr>
              `).join('')}
            </table>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td colspan="2" style="padding-top: 16px;">
      <p style="margin: 0; font-size: 11px; color: #999; font-style: italic;">We maintain your home so you don't have to.</p>
    </td>
  </tr>
</table>`

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h2 className="text-2xl font-serif text-grey-900 mb-2">Email Signatures</h2>
        <p className="text-grey-500 mb-6">
          Professional email signatures with "Super Signature" format including "Here&apos;s how I can help" section. 
          Copy the HTML and paste directly into your email client settings.
        </p>

        <div className="space-y-8">
          {signatures.map((sig) => (
            <div key={sig.id} className="border border-grey-200 rounded-lg overflow-hidden">
              <div className="p-4 bg-grey-50 border-b border-grey-200 flex items-center justify-between">
                <span className="font-medium text-grey-900">{sig.role} Signature</span>
                <button
                  onClick={() => copySignature(generateSignatureHTML(sig), sig.id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded transition-all ${
                    copiedId === sig.id
                      ? 'bg-green-100 text-green-700'
                      : 'bg-grey-900 hover:bg-grey-800 text-white'
                  }`}
                >
                  {copiedId === sig.id ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy HTML
                    </>
                  )}
                </button>
              </div>
              
              {/* Live Preview */}
              <div className="p-6 bg-white">
                <div className="max-w-lg">
                  <div className="flex gap-5">
                    <div className="flex-shrink-0">
                      <img src={brandAssets.logos.primary} alt="Hudson" className="w-24 h-auto" />
                    </div>
                    <div className="pl-5 border-l-[3px]" style={{ borderColor: colors.teal }}>
                      <p className="font-semibold text-base" style={{ color: colors.teal }}>{sig.name}</p>
                      <p className="text-grey-500 text-sm mb-3">{sig.title}</p>
                      <p className="text-grey-700 text-sm">{sig.phone}</p>
                      <p className="text-sm" style={{ color: colors.teal }}>{sig.email}</p>
                      <p className="text-sm" style={{ color: colors.teal }}>hudsonmyhome.app</p>
                    </div>
                  </div>
                  
                  <div className="mt-5 p-4 rounded-lg" style={{ backgroundColor: colors.cream }}>
                    <p className="font-semibold text-sm mb-3" style={{ color: colors.teal }}>Here&apos;s how I can help:</p>
                    <ul className="text-sm text-grey-600 space-y-1.5">
                      {sig.helpItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span style={{ color: colors.teal }}>→</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <p className="mt-4 text-xs text-grey-400 italic">We maintain your home so you don't have to.</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Signature Guidelines */}
      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Signature Guidelines</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-grey-900 mb-3">Do</h4>
            <ul className="space-y-2">
              {[
                'Use consistent formatting across all team members',
                'Include the "Here\'s how I can help" section',
                'Keep help items relevant to your role',
                'Use the brand teal color (#145F6B) for links',
                'Include the tagline at the bottom',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-grey-600">
                  <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-grey-900 mb-3">Don&apos;t</h4>
            <ul className="space-y-2">
              {[
                'Add personal quotes or inspirational messages',
                'Include social media icons (keep it clean)',
                'Use different fonts or colors',
                'Add excessive promotional banners',
                'Include more than 4 help items',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-grey-600">
                  <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center text-red-600 text-xs mt-0.5 flex-shrink-0">✕</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Promotional Signature Blocks */}
      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Promotional Signature Blocks</h3>
        <p className="text-grey-500 mb-6">Add these below your signature for special campaigns.</p>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="border border-grey-200 rounded-lg overflow-hidden">
            <div className="p-4 bg-grey-50 border-b border-grey-200">
              <span className="font-medium text-grey-900">MyHome Refresh Promo</span>
            </div>
            <div className="p-4">
              <div className="p-4 rounded-lg text-white" style={{ backgroundColor: colors.teal }}>
                <p className="font-bold text-sm mb-1">Get Your MyHome Refresh™</p>
                <p className="text-xs text-white/80 mb-2">One visit. Complete clarity. Starting at $299.</p>
                <p className="text-xs font-medium">→ hudsonmyhome.app/refresh</p>
              </div>
            </div>
          </div>

          <div className="border border-grey-200 rounded-lg overflow-hidden">
            <div className="p-4 bg-grey-50 border-b border-grey-200">
              <span className="font-medium text-grey-900">Partner Program Promo</span>
            </div>
            <div className="p-4">
              <div className="p-4 rounded-lg text-white" style={{ backgroundColor: colors.teal }}>
                <p className="font-bold text-sm mb-1">Real Estate Partners</p>
                <p className="text-xs text-white/80 mb-2">The closing gift they&apos;ll actually use. Join our partner program.</p>
                <p className="text-xs font-medium">→ hudsonmyhome.app/partners</p>
              </div>
            </div>
          </div>

          <div className="border border-grey-200 rounded-lg overflow-hidden">
            <div className="p-4 bg-grey-50 border-b border-grey-200">
              <span className="font-medium text-grey-900">Membership Promo</span>
            </div>
            <div className="p-4">
              <div className="p-4 rounded-lg text-white" style={{ backgroundColor: colors.tealDark }}>
                <p className="font-bold text-sm mb-1">White-Glove Membership</p>
                <p className="text-xs text-white/80 mb-2">Your dedicated Hudson professional. $399/month.</p>
                <p className="text-xs font-medium">→ hudsonmyhome.app/membership</p>
              </div>
            </div>
          </div>

          <div className="border border-grey-200 rounded-lg overflow-hidden">
            <div className="p-4 bg-grey-50 border-b border-grey-200">
              <span className="font-medium text-grey-900">Builder Program Promo</span>
            </div>
            <div className="p-4">
              <div className="p-4 rounded-lg text-white" style={{ backgroundColor: colors.nearBlack }}>
                <p className="font-bold text-sm mb-1">Luxury Home Builders</p>
                <p className="text-xs text-white/80 mb-2">Build exceptional homes. We handle everything after.</p>
                <p className="text-xs font-medium">→ hudsonmyhome.app/builders</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Go-to-Market Email Templates */}
      <div className="bg-white rounded-xl p-6 border border-grey-200">
        <h3 className="text-2xl font-serif text-grey-900 mb-2">Go-to-Market Email Templates</h3>
        <p className="text-grey-500 mb-6">
          Launch campaign emails with full inline styles. Copy HTML to send via your email platform. Each email is fully formatted and ready to deploy.
        </p>
        
        <div className="space-y-6">
          {/* Email 1: Launch Announcement */}
          <LaunchEmailTemplate
            id="launch-announce"
            title="Email 1: Launch Announcement"
            subject="Introducing Hudson My Home — The Future of Homeownership Is Here"
            preheader="White-glove home management for those who value their time"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">What if you never had to think about home maintenance again?</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">We're thrilled to introduce <strong style="color: #145F6B;">Hudson My Home</strong> — white-glove home management for discerning homeowners who value their time above all else.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Your dedicated Hudson professional handles everything:</p>
              <ul style="margin: 0 0 16px 0; padding-left: 24px; color: #333; font-size: 16px; line-height: 1.8;">
                <li>Monthly home visits and proactive maintenance</li>
                <li>Your personalized 5-year home plan</li>
                <li>24/7 emergency support and vendor coordination</li>
                <li>Complete documentation and home health tracking</li>
              </ul>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">No more weekend repair projects. No more forgotten maintenance. No more stress.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong style="color: #145F6B;">Founding member spots are limited.</strong> Lock in your rate and join the future of homeownership.</p>
            `}
            cta="Discover Home Membership"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 2: Problem Awareness */}
          <LaunchEmailTemplate
            id="problem-awareness"
            title="Email 2: Problem Awareness"
            subject="The hidden cost of 'I'll get to it later'"
            preheader="What deferred maintenance is really costing you"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Here's what most homeowners don't realize:</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">That furnace filter you've been meaning to change? It's making your system work 15% harder.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Those gutters you'll "clean next weekend"? They're slowly damaging your foundation.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">The dryer vent you keep forgetting about? It's a fire hazard.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>Studies show that deferred maintenance costs homeowners 3x more in emergency repairs than proactive care.</strong></p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">That's why we created Hudson My Home. We handle all the things you keep meaning to get to — before they become expensive problems.</p>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">Your home is your largest investment. Isn't it time you protected it?</p>
            `}
            cta="Learn How It Works"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 3: Time Value */}
          <LaunchEmailTemplate
            id="time-value"
            title="Email 3: Time Value"
            subject="What would you do with 10 extra hours this month?"
            preheader="Reclaim your weekends with Hudson My Home"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">The average homeowner spends <strong>10+ hours per month</strong> on home maintenance tasks.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Scheduling contractors. Waiting for repair people. Making trips to the hardware store. Researching what needs to be done next.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>That's 120 hours a year.</strong></p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Imagine what you could do with that time back:</p>
              <ul style="margin: 0 0 16px 0; padding-left: 24px; color: #333; font-size: 16px; line-height: 1.8;">
                <li>More weekends with family</li>
                <li>Actually relaxing in the home you worked so hard for</li>
                <li>Focusing on what you do best — your career, your passions, your life</li>
              </ul>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">Hudson My Home gives you that time back. Your dedicated professional handles everything, so you can finally <em>live</em> in your home.</p>
            `}
            cta="Reclaim Your Time"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 4: Social Proof */}
          <LaunchEmailTemplate
            id="social-proof"
            title="Email 4: Social Proof"
            subject="'Hudson My Home literally paid for itself'"
            preheader="What our founding members are saying"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Don't just take our word for it. Here's what our founding members are experiencing:</p>
              <div style="margin: 24px 0; padding: 20px; background-color: #F6F6F1; border-left: 4px solid #145F6B;">
                <p style="margin: 0 0 8px 0; color: #333; font-size: 16px; line-height: 1.6; font-style: italic;">"During a routine visit, they found a small leak that would have caused thousands in damage if left unchecked. Hudson My Home literally paid for itself."</p>
                <p style="margin: 0; color: #666; font-size: 14px;">— Mark T., Urbandale</p>
              </div>
              <div style="margin: 24px 0; padding: 20px; background-color: #F6F6F1; border-left: 4px solid #145F6B;">
                <p style="margin: 0 0 8px 0; color: #333; font-size: 16px; line-height: 1.6; font-style: italic;">"I used to spend every Saturday dealing with house stuff. Now I spend it with my kids. This changed everything for our family."</p>
                <p style="margin: 0; color: #666; font-size: 14px;">— Sarah M., West Des Moines</p>
              </div>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">Join the homeowners who are finally experiencing what stress-free home ownership feels like.</p>
            `}
            cta="Join the Hudson Family"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 5: Urgency */}
          <LaunchEmailTemplate
            id="urgency"
            title="Email 5: Urgency"
            subject="Only 10 founding member spots remaining"
            preheader="Lock in your rate before they're gone"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">I wanted to give you a quick heads up: <strong style="color: #145F6B;">we only have 10 founding member spots left.</strong></p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">As a founding member, you'll lock in our introductory rate and receive priority scheduling with your dedicated Hudson professional.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Once these spots are filled, we'll be opening a waitlist for our next membership cohort — at a higher rate.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>Here's what founding members get:</strong></p>
              <ul style="margin: 0 0 16px 0; padding-left: 24px; color: #333; font-size: 16px; line-height: 1.8;">
                <li>Locked-in founding member pricing</li>
                <li>Priority scheduling for all services</li>
                <li>Direct access to your dedicated Hudson professional</li>
                <li>Input on future service offerings</li>
              </ul>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">Don't wait until you're on the outside looking in.</p>
            `}
            cta="Claim Your Spot"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 6: Final Call */}
          <LaunchEmailTemplate
            id="final-call"
            title="Email 6: Final Call"
            subject="[Final] Your founding member invitation expires tomorrow"
            preheader="Last chance to join at the founding rate"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">This is your final reminder: <strong style="color: #145F6B;">founding member enrollment closes tomorrow at midnight.</strong></p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">After that, you'll need to join our waitlist and pay standard pricing when spots open again.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">I want to make this decision easy for you. Here's our promise:</p>
              <div style="margin: 24px 0; padding: 20px; background-color: #F6F6F1; border-radius: 8px;">
                <p style="margin: 0 0 8px 0; color: #145F6B; font-size: 18px; font-weight: 600;">The Hudson Standard Guarantee</p>
                <p style="margin: 0; color: #333; font-size: 16px; line-height: 1.6;">If you're not completely satisfied with your first 30 days as a Hudson member, we'll refund your membership fee. No questions asked.</p>
              </div>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">You have nothing to lose — and 120 hours a year to gain.</p>
            `}
            cta="Become a Founding Member"
            ctaUrl="https://hudsonmyhome.app/membership"
          />

          {/* Email 7: Partner Announcement */}
          <LaunchEmailTemplate
            id="partner-announce"
            title="Email 7: Real Estate Partner Announcement"
            subject="The closing gift that actually gets used"
            preheader="A new partnership opportunity for top agents"
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Let me ask you something: how many of your closing gifts end up in a drawer? Or worse — the trash?</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">We've created something different. Something your buyers will actually use — and remember you for.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong style="color: #145F6B;">Introducing the Hudson My Home Partner Program for Real Estate Agents.</strong></p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Here's how it works:</p>
              <ul style="margin: 0 0 16px 0; padding-left: 24px; color: #333; font-size: 16px; line-height: 1.8;">
                <li>Gift your buyers a complimentary MyHome Refresh (a $299 value)</li>
                <li>They receive a professional home assessment and seasonal maintenance</li>
                <li>You stay top-of-mind as the agent who truly cared</li>
                <li>When they need home services, they think of you — not Google</li>
              </ul>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Stop the post-closing calls. Start the referrals.</p>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">Partner program: <strong>$599/year</strong> — includes 3 complimentary MyHome Refresh gifts for your buyers.</p>
            `}
            cta="Learn About the Partner Program"
            ctaUrl="https://hudsonmyhome.app/partners"
          />

          {/* Email 8: MyHome Refresh Introduction */}
          <LaunchEmailTemplate
            id="refresh-intro"
            title="Email 8: MyHome Refresh Introduction"
            subject="Not ready for membership? Start here."
            preheader="One visit. Complete clarity. Starting at $299."
            body={`
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">Hi [First Name],</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">We get it. Full membership might feel like a big commitment.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;">That's why we created the <strong style="color: #145F6B;">MyHome Refresh</strong> — a single visit that gives you complete clarity about your home's condition.</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>What's included:</strong></p>
              <ul style="margin: 0 0 16px 0; padding-left: 24px; color: #333; font-size: 16px; line-height: 1.8;">
                <li>Comprehensive home health assessment</li>
                <li>Seasonal maintenance (filters, smoke detectors, etc.)</li>
                <li>Your MyHome Snapshot report</li>
                <li>$100 credit toward future services</li>
              </ul>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>MyHome Refresh: $299</strong> — Perfect proactive tune-up (2 hours)</p>
              <p style="margin: 0 0 16px 0; color: #333; font-size: 16px; line-height: 1.6;"><strong>Refresh Deluxe: $399</strong> — Complete assessment & maintenance (3.5 hours)</p>
              <p style="margin: 0 0 24px 0; color: #333; font-size: 16px; line-height: 1.6;">No commitment required. Just peace of mind.</p>
            `}
            cta="Book Your MyHome Refresh"
            ctaUrl="https://hudsonmyhome.app/refresh"
          />
        </div>
      </div>
    </div>
  )
}

// Email template component with full inline styles
const LaunchEmailTemplate: React.FC<{
  id: string
  title: string
  subject: string
  preheader: string
  body: string
  cta: string
  ctaUrl: string
}> = ({ id, title, subject, preheader, body, cta, ctaUrl }) => {
  const [copied, setCopied] = useState(false)
  const emailRef = useRef<HTMLDivElement>(null)

  const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
  <!--[if mso]><style>table,td,div,p{font-family:Arial,sans-serif!important}</style><![endif]-->
</head>
<body style="margin: 0; padding: 0; background-color: #f5f5f5; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
  <div style="display: none; max-height: 0; overflow: hidden;">${preheader}</div>
  <table role="presentation" cellpadding="0" cellspacing="0" style="width: 100%; background-color: #f5f5f5;">
    <tr>
      <td style="padding: 40px 20px;">
        <table role="presentation" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.05);">
          <!-- Header -->
          <tr>
            <td style="padding: 32px 40px; background-color: #145F6B;">
              <img src="https://nuykijziltpoqaszbrwi.supabase.co/storage/v1/object/public/site-images/HMH_Primary_Logo_White.png" alt="Hudson My Home" width="160" style="display: block;">
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding: 40px;">
              ${body}
              <!-- CTA Button -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin: 32px 0;">
                <tr>
                  <td style="background-color: #145F6B; border-radius: 6px;">
                    <a href="${ctaUrl}" style="display: inline-block; padding: 16px 32px; color: #ffffff; text-decoration: none; font-weight: 600; font-size: 16px;">${cta}</a>
                  </td>
                </tr>
              </table>
              <p style="margin: 0; color: #333; font-size: 16px; line-height: 1.6;">Warmly,</p>
              <p style="margin: 8px 0 0 0; color: #145F6B; font-size: 16px; font-weight: 600;">The Hudson My Home Team</p>
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="padding: 24px 40px; background-color: #F6F6F1; border-top: 1px solid #e5e5e5;">
              <p style="margin: 0 0 8px 0; color: #666; font-size: 14px; font-style: italic;">We maintain your home so you don't have to.</p>
              <p style="margin: 0; color: #999; font-size: 12px;">
                Hudson My Home · Urbandale, Iowa · <a href="https://hudsonmyhome.app" style="color: #145F6B;">hudsonmyhome.app</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`

  const copyHtml = () => {
    navigator.clipboard.writeText(fullHtml)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const downloadHtml = () => {
    const blob = new Blob([fullHtml], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `hudson-email-${id}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      <div className="p-4 bg-grey-50 border-b border-grey-200 flex items-center justify-between">
        <div>
          <span className="font-medium text-grey-900">{title}</span>
          <p className="text-sm text-grey-500 mt-1">Subject: {subject}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyHtml}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded transition-colors"
            style={{ backgroundColor: copied ? '#10b981' : colors.teal, color: 'white' }}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy HTML'}
          </button>
          <button
            onClick={downloadHtml}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-grey-200 hover:bg-grey-300 text-grey-700 text-sm font-medium rounded transition-colors"
          >
            <Download className="w-4 h-4" />
            Download
          </button>
        </div>
      </div>
      
      {/* Email Preview */}
      <div ref={emailRef} className="p-6 bg-grey-100">
        <div className="max-w-lg mx-auto bg-white rounded-lg overflow-hidden shadow-sm">
          <div className="p-6" style={{ backgroundColor: colors.teal }}>
            <img src={brandAssets.logos.primaryWhite} alt="Hudson" className="h-8" />
          </div>
          <div className="p-6" dangerouslySetInnerHTML={{ __html: body }} />
          <div className="px-6 pb-6">
            <a 
              href={ctaUrl}
              className="inline-block px-6 py-3 text-white font-medium rounded"
              style={{ backgroundColor: colors.teal }}
            >
              {cta}
            </a>
          </div>
          <div className="p-4" style={{ backgroundColor: colors.cream }}>
            <p className="text-sm text-grey-600 italic mb-1">We maintain your home so you don't have to.</p>
            <p className="text-xs text-grey-400">Hudson My Home · Urbandale, Iowa · hudsonmyhome.app</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================
// DESIGNED FLYER COMPONENT
// ============================================
const DesignedFlyer: React.FC<{
  title: string
  design: {
    type: 'membership' | 'refresh' | 'agents' | 'builders'
    headline: string
    subhead: string
    features: string[]
    cta: string
    price?: string
    image: string
  }
}> = ({ title, design }) => {
  const [isDownloading, setIsDownloading] = useState(false)
  
  // Fixed dimensions for 8.5x11 flyer at print quality (96 DPI = 816x1056, using 2x = 1632x2112)
  const targetWidth = 816
  const targetHeight = 1056
  const previewScale = 0.35 // Show preview at ~285px wide

  const bgColor = design.type === 'membership' ? colors.teal 
    : design.type === 'refresh' ? colors.tealDark 
    : design.type === 'agents' ? colors.cream 
    : colors.nearBlack

  const downloadFlyer = async () => {
    const container = document.getElementById(`flyer-${design.type}`)
    if (!container) {
      console.error('Flyer container not found')
      return
    }

    setIsDownloading(true)
    try {
      // Clone and render at full size
      const clone = container.cloneNode(true) as HTMLElement
      clone.style.width = `${targetWidth}px`
      clone.style.height = `${targetHeight}px`
      clone.style.transform = 'none'
      clone.style.position = 'absolute'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      document.body.appendChild(clone)
      
      await preloadImagesForDownload(clone)
      
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(clone, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: bgColor || null,
        width: targetWidth,
        height: targetHeight,
      })
      
      document.body.removeChild(clone)
      
      const link = document.createElement('a')
      link.download = `hudson-${design.type}-flyer.png`
      link.href = canvas.toDataURL('image/png', 1.0)
      link.click()
    } catch (error) {
      console.error('Error downloading flyer:', error)
      alert('Error generating flyer. Please try again.')
    } finally {
      setIsDownloading(false)
    }
  }

  const textColor = design.type === 'agents' ? colors.nearBlack : 'white'
  const accentColor = colors.teal

  const FlyerContent = () => (
    <div
      style={{
        width: `${targetWidth}px`,
        height: `${targetHeight}px`,
        backgroundColor: bgColor,
        position: 'relative',
        overflow: 'hidden',
        fontFamily: 'DM Sans, sans-serif',
      }}
    >
      {/* Header with logo */}
      <div style={{ padding: '48px' }}>
        <img 
          src={design.type === 'agents' ? brandAssets.logos.primary : brandAssets.logos.primaryWhite} 
          alt="Hudson" 
          style={{ height: '56px', width: 'auto', objectFit: 'contain' }}
        />
      </div>

      {/* Hero image */}
      <div 
        style={{ 
          margin: '0 48px', 
          height: '220px', 
          borderRadius: '12px', 
          backgroundImage: `url(${design.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Content */}
      <div style={{ padding: '32px 48px 48px' }}>
        <p style={{ fontFamily: 'Instrument Serif, serif', fontSize: '42px', color: textColor, lineHeight: 1.2, margin: 0 }}>
          {design.headline}
        </p>
        <p style={{ fontSize: '18px', color: textColor, opacity: 0.8, marginTop: '12px' }}>
          {design.subhead}
        </p>

        {design.price && (
          <p style={{ fontSize: '48px', color: accentColor, fontWeight: 300, marginTop: '24px', marginBottom: '8px' }}>
            {design.price}
          </p>
        )}

        {/* Features */}
        <div style={{ marginTop: '28px', marginBottom: '32px' }}>
          {design.features.map((feature, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
              <span style={{ color: accentColor, fontSize: '20px', lineHeight: 1.2 }}>✓</span>
              <span style={{ fontSize: '18px', color: textColor }}>{feature}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div 
          style={{ 
            textAlign: 'center', 
            padding: '20px', 
            borderRadius: '12px', 
            backgroundColor: accentColor, 
            color: 'white',
            fontSize: '18px',
            fontWeight: 500,
          }}
        >
          {design.cta}
        </div>
      </div>

      {/* Footer */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px', textAlign: 'center', color: textColor }}>
        <p style={{ fontSize: '14px', opacity: 0.6, margin: 0 }}>hudsonmyhome.app | (515) 216-4433</p>
        <p style={{ fontSize: '12px', opacity: 0.4, marginTop: '8px', fontStyle: 'italic' }}>We maintain your home so you don't have to.</p>
      </div>
    </div>
  )

  return (
    <div className="border border-grey-200 rounded-lg overflow-hidden">
      <div className="p-4 bg-grey-50 border-b border-grey-200">
        <span className="font-medium text-grey-900">{title}</span>
      </div>
      
      {/* Flyer Preview with scaling */}
      <div className="p-4">
        <div 
          className="overflow-hidden rounded-lg shadow-lg mx-auto"
          style={{ width: targetWidth * previewScale, height: targetHeight * previewScale }}
        >
          <div 
            id={`flyer-${design.type}`}
            style={{ transform: `scale(${previewScale})`, transformOrigin: 'top left' }}
          >
            <FlyerContent />
          </div>
        </div>
      </div>

      {/* Download button */}
      <div className="p-4 pt-0">
        <button
          onClick={downloadFlyer}
          disabled={isDownloading}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-grey-900 hover:bg-grey-800 disabled:bg-grey-600 text-white text-sm font-medium rounded transition-colors"
        >
          {isDownloading ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Download className="w-4 h-4" />
              Download Flyer (PNG)
            </>
          )}
        </button>
      </div>
    </div>
  )
}

// ============================================
// BROCHURES TAB
// ============================================
const BrochuresTab: React.FC = () => (
  <div className="space-y-8">
    {/* Printable Flyers Section */}
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-2">Printable Flyers</h2>
      <p className="text-grey-500 mb-6">Ready-to-print flyers for each service and audience. Download as PNG for print or digital use.</p>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <DesignedFlyer
          title="Membership Flyer"
          design={{
            type: 'membership',
            headline: 'White-Glove Home Management',
            subhead: 'Your dedicated Hudson professional handles your home\'s 5-year plan.',
            features: [
              'Dedicated home professional',
              'Monthly in-home visits',
              '24/7 priority support',
              'Complete vendor oversight',
              'Full documentation',
            ],
            cta: 'Discover Home Membership',
            price: '$399/mo',
            image: brandAssets.photos.stock1,
          }}
        />
        <DesignedFlyer
          title="MyHome Refresh Flyer"
          design={{
            type: 'refresh',
            headline: 'The MyHome Refresh™',
            subhead: 'A thoughtful reset for homeowners who want clarity and confidence.',
            features: [
              'Professional home health check',
              'Seasonal maintenance included',
              'Clear prioritized report',
              'No membership required',
              'Credit toward future services',
            ],
            cta: 'Book Your Refresh',
            price: 'Starting at $299',
            image: brandAssets.photos.stock2,
          }}
        />
        <DesignedFlyer
          title="Agent Partner Flyer"
          design={{
            type: 'agents',
            headline: 'Partner With Hudson',
            subhead: 'The closing gift they\'ll actually use—and remember you for.',
            features: [
              'Discounted pre-listing inspections',
              'Closing gift memberships',
              'In-app client marketing',
              'Priority scheduling',
              'Referral generation',
            ],
            cta: 'Join Our Partner Program',
            price: '$599/year',
            image: brandAssets.photos.stock5,
          }}
        />
        <DesignedFlyer
          title="Builder Partner Flyer"
          design={{
            type: 'builders',
            headline: 'Build Exceptional Homes',
            subhead: 'We\'ll take care of everything after. No more post-closing calls.',
            features: [
              'First-year warranty coordination',
              'Monthly homeowner visits',
              'Single point of contact',
              'Complete documentation',
              'Brand reputation protection',
            ],
            cta: 'Partner With Us',
            image: brandAssets.photos.stock4,
          }}
        />
      </div>
    </div>

    {/* Downloadable PDF Brochures */}
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-2">Downloadable PDF Brochures</h2>
      <p className="text-grey-500 mb-6">Professional 4-page brochures for each audience. Click Preview to see full content, then Download PDF.</p>

      <div className="space-y-6">
        <DesignedBrochure
          type="membership"
          title="Homeowner Membership Brochure"
          audience="High-net-worth homeowners, busy professionals, Strategic Coach members"
        />

        <DesignedBrochure
          type="refresh"
          title="MyHome Refresh™ Brochure"
          audience="Homeowners seeking one-time preventive care, no commitment"
        />

        <DesignedBrochure
          type="agents"
          title="Real Estate Agent Partner Brochure"
          audience="Top-producing agents, team leaders, brokerages"
        />

        <DesignedBrochure
          type="builders"
          title="Luxury Builder Partner Brochure"
          audience="Custom home builders, luxury spec builders, remodelers"
        />
      </div>
    </div>
  </div>
)

// ============================================
// BRAND GUIDELINES TAB
// ============================================
const BrandGuidelinesTab: React.FC = () => (
  <div className="space-y-8">
    <div className="bg-white rounded-xl p-6 border border-grey-200">
      <h2 className="text-2xl font-serif text-grey-900 mb-6">Brand Guidelines</h2>

      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-lg font-medium text-grey-900 mb-4">Color Palette</h3>
          <div className="space-y-3">
            {[
              { name: 'Primary Teal', hex: '#145F6B', usage: 'Primary buttons, headers, accents' },
              { name: 'Dark Teal', hex: '#083B3A', usage: 'Dark backgrounds, gradients' },
              { name: 'Warm Copper', hex: '#9B775D', usage: 'Secondary accents, CTAs' },
              { name: 'Cream', hex: '#F6F6F1', usage: 'Light backgrounds' },
              { name: 'Gold', hex: '#C9A962', usage: 'Premium accents, highlights' },
              { name: 'Near Black', hex: '#0F0F0F', usage: 'Text, dark sections' },
            ].map((color, idx) => (
              <div key={idx} className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg border border-grey-200" style={{ backgroundColor: color.hex }} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-grey-900">{color.name}</span>
                    <code className="text-xs bg-grey-100 px-2 py-0.5 rounded text-grey-600">{color.hex}</code>
                    <CopyButton text={color.hex} />
                  </div>
                  <span className="text-sm text-grey-500">{color.usage}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-medium text-grey-900 mb-4">Typography</h3>
          <div className="space-y-4">
            <div className="p-4 bg-grey-50 rounded-lg">
              <span className="text-xs text-grey-500 uppercase tracking-wider">Headlines</span>
              <p className="text-2xl font-serif text-grey-900 mt-2">Instrument Serif</p>
              <p className="text-sm text-grey-600 mt-1">Used for all headlines and display text</p>
            </div>
            <div className="p-4 bg-grey-50 rounded-lg">
              <span className="text-xs text-grey-500 uppercase tracking-wider">Body Text</span>
              <p className="text-lg text-grey-900 mt-2">DM Sans</p>
              <p className="text-sm text-grey-600 mt-1">Used for body copy, buttons, and UI elements</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Logo Assets</h3>
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { name: 'Primary (Dark)', bg: 'bg-white', desc: 'Use on light backgrounds', src: brandAssets.logos.primary },
            { name: 'Primary (White)', bg: 'bg-grey-900', desc: 'Use on dark backgrounds', src: brandAssets.logos.primaryWhite },
            { name: 'Secondary (Teal)', bg: 'bg-white', desc: 'Alternate version', src: brandAssets.logos.secondary },
            { name: 'Secondary (White)', bg: 'bg-grey-900', desc: 'Use on dark backgrounds', src: brandAssets.logos.secondaryWhite },
          ].map((logo, idx) => (
            <div key={idx} className="border border-grey-200 rounded-lg overflow-hidden">
              <div className={`aspect-video ${logo.bg} flex items-center justify-center p-6`}>
                <img src={logo.src} alt={logo.name} className="max-h-12 w-auto object-contain" />
              </div>
              <div className="p-3">
                <p className="font-medium text-grey-900 text-sm">{logo.name}</p>
                <p className="text-xs text-grey-500">{logo.desc}</p>
                <div className="mt-2">
                  <a 
                    href={logo.src} 
                    download={`hudson-${logo.name.toLowerCase().replace(/[^a-z]/g, '-')}.png`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-grey-100 hover:bg-grey-200 text-grey-700 text-xs font-medium rounded transition-colors"
                  >
                    <Download className="w-3 h-3" />
                    Download PNG
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Brand Pattern</h3>
        <div className="border border-grey-200 rounded-lg overflow-hidden">
          <div 
            className="h-32 bg-cover bg-center"
            style={{ backgroundImage: `url(${brandAssets.pattern})` }}
          />
          <div className="p-4 flex items-center justify-between">
            <div>
              <p className="font-medium text-grey-900 text-sm">Hudson Pattern</p>
              <p className="text-xs text-grey-500">Use for backgrounds and accents</p>
            </div>
            <a 
              href={brandAssets.pattern} 
              download="hudson-pattern.png"
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-grey-100 hover:bg-grey-200 text-grey-700 text-xs font-medium rounded transition-colors"
            >
              <Download className="w-3 h-3" />
              Download
            </a>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Photo Library</h3>
        <p className="text-grey-500 text-sm mb-4">Approved brand photography for marketing materials.</p>
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
          {Object.entries(brandAssets.photos).slice(0, 16).map(([key, url]) => (
            <a 
              key={key}
              href={url} 
              download={`hudson-${key}.png`}
              className="aspect-square bg-cover bg-center rounded-lg hover:opacity-80 transition-opacity cursor-pointer"
              style={{ backgroundImage: `url(${url})` }}
              title={`Download ${key}`}
            />
          ))}
        </div>
        <p className="text-xs text-grey-400 mt-3">Click any image to download</p>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Voice & Tone</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {[
            { trait: 'Confident', desc: 'We speak with authority about home management. We\'re the experts.' },
            { trait: 'Approachable', desc: 'Luxury doesn\'t mean stuffy. We\'re warm, helpful, and human.' },
            { trait: 'Aspirational', desc: 'We paint a picture of what life could be—with more time and less stress.' },
          ].map((item, idx) => (
            <div key={idx} className="p-4 bg-grey-50 rounded-lg">
              <h4 className="font-medium text-grey-900 mb-2">{item.trait}</h4>
              <p className="text-sm text-grey-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="text-lg font-medium text-grey-900 mb-4">Key Messages</h3>
        <div className="space-y-3">
          {[
            '"We maintain your home so you don\'t have to."',
            '"Your home deserves a director, not a to-do list."',
            '"From furnace filters to full remodels."',
            '"White-glove home care for those who value their time."',
            '"Monthly visits. Proactive care. Complete peace of mind."',
          ].map((message, idx) => (
            <div key={idx} className="flex items-center justify-between p-4 bg-grey-50 rounded-lg">
              <span className="text-grey-900 font-serif italic">{message}</span>
              <CopyButton text={message} />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
)

// ============================================
// MAIN PAGE COMPONENT
// ============================================
const BrandAssetsPage: React.FC = () => {
  const [authenticated, setAuthenticated] = useState(false)

  if (!authenticated) {
    return <PasswordGate onSuccess={() => setAuthenticated(true)} />
  }

  return (
    <>
      <BrandAssetsContent />
      <Footer />
    </>
  )
}

export default BrandAssetsPage

